import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Tooltip,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search,
  Image as ImageIcon,
  Refresh,
  Store,
  InfoOutlined,
  CalendarToday,
  ExpandMore,
  ExpandLess,
  WarningAmber,
  CheckCircle,
  KeyboardArrowDown,
  SettingsOutlined,
  ArrowUpward,
  ArrowDownward,
  DeleteOutline,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, COLOR_PALETTE } from './constants';
import { resolveProductImageUrlFromFields, mergeJtiProductRowOverrides, pickLatestRowOnOrBeforeDate, filterRowsForProductPopup, productRowDateKey } from './productUtils';
import {
  buildMatchingGroupsMapFromTables,
  groupProductsByMatching,
  pricesDifferInGroup,
  getFlatVariantsFromGroup,
  forEachMatchingInfo,
  matchingInfosForSku,
} from './matchingUtils';
import { ProductImageHoverOverlay } from './ProductImageHoverOverlay';
import ProductDetailPopup from './ProductDetailPopup';
import { useAuth } from './AuthContext';
import dayjs from 'dayjs';
import { getDefaultManufacturerRetailers, THIRD_PARTY_RETAILERS } from './utils/brandBoardUtils';
import {
  useResolvedMarketAnnouncements,
} from './utils/marketAnnouncementsUtils';
import { MarketAnnouncementBannerList } from './MarketAnnouncementBanner';
import MarketRegulationsPanel from './MarketRegulationsPanel';
import {
  BASE_PRODUCT_AUTO,
  AUTO_BASE_QUEUE_SKU_PREFIX,
  DEFAULT_BASE_PRODUCT_AUTO_QUEUES,
  readBaseProductAutoQueuesFromStorage,
  persistBaseProductAutoQueues,
  buildAutoBaseProductQueueChoices,
  migrateLegacyQueueEntry,
  pickBaseProductFromAutoQueue,
  formatBaseProductSelectLabel,
} from './utils/baseProductAutoQueueUtils';

/** Same hover overlay as Price & Promotions chart product images (cursor-following panel). */
function BrandBoardProductHoverWrap({ productImage, productName, groupProductNames, children }) {
  const [cursorPos, setCursorPos] = useState(null);
  return (
    <>
      <Box
        onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={() => setCursorPos(null)}
      >
        {children}
      </Box>
      <ProductImageHoverOverlay
        cursorPos={cursorPos}
        productImage={productImage}
        productName={productName}
        groupProductNames={groupProductNames}
      />
    </>
  );
}

/** Numeric pack size used to order products within a board cell (min → max). Bare/unknown = 1 unit. */
function boardPackSortValue(product, getItemPackSize) {
  const raw = product?._boardSlotPackPart;
  const fromSlot = raw != null && raw !== '' && raw !== 'u' ? Number(raw) : NaN;
  if (Number.isFinite(fromSlot) && fromSlot > 0) return fromSlot;
  const ps = getItemPackSize ? getItemPackSize(product) : null;
  return Number.isFinite(ps) && ps > 0 ? ps : 1;
}

/** Grey bracket label e.g. "(10 units)" from `_boardSlotPackPart` on brand-board rows. */
function boardPackUnitsBracketLabel(product) {
  const raw = product?._boardSlotPackPart;
  if (raw == null || raw === '' || raw === 'u') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return null;
  if (n === 1) return null;
  return `(${n} units)`;
}

/** Select value for “Number of units”: smallest pack for each product (per country+title), omit larger packs. */
const BRAND_BOARD_PACK_SELECTION_MINIMAL = 'minimal';

/**
 * `Number of units` filter: either explicit pack sizes, or Minimal = smallest pack for each
 * product (per country+title; larger packs omitted for that product in that market).
 */
function brandBoardFilterRowsByPackSelection(rows, selectedPackSizes, getItemPackSize) {
  if (!selectedPackSizes?.length) return rows;
  if (selectedPackSizes.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL)) {
    const minPackByCountryTitle = new Map();
    rows.forEach((row) => {
      const t = row.title;
      if (!t) return;
      const ps = getItemPackSize(row);
      if (ps == null) return;
      const key = `${row.country || ''}\x1f${t}`;
      const cur = minPackByCountryTitle.get(key);
      if (cur == null || ps < cur) minPackByCountryTitle.set(key, ps);
    });
    return rows.filter((row) => {
      const ps = getItemPackSize(row);
      if (ps == null) return true;
      const key = `${row.country || ''}\x1f${row.title || ''}`;
      const minP = minPackByCountryTitle.get(key);
      if (minP == null) return true;
      return ps === minP;
    });
  }
  return rows.filter((row) => selectedPackSizes.includes(getItemPackSize(row)));
}

/** Sticks base-product select: `GroupName\\x1e10` value → label `GroupName (10 units)`. */
const STICK_BASE_PRODUCT_PACK_SEP = '\x1e';

function encodeStickBaseProductOption(displayName, pack) {
  const name = (displayName || '').trim();
  const p =
    pack === 'u' || pack == null || pack === '' || Number.isNaN(Number(pack))
      ? 'u'
      : String(Math.round(Number(pack)));
  return `${name}${STICK_BASE_PRODUCT_PACK_SEP}${p}`;
}

function decodeStickBaseProductOption(v) {
  if (typeof v !== 'string') return { name: v || '', pack: null };
  const i = v.indexOf(STICK_BASE_PRODUCT_PACK_SEP);
  if (i === -1) return { name: v, pack: null };
  const name = v.slice(0, i).trim();
  const raw = v.slice(i + 1);
  if (raw === 'u') return { name, pack: null };
  const n = Number(raw);
  return { name, pack: Number.isFinite(n) && n >= 1 ? n : null };
}

function formatStickBaseProductOptionLabel(v) {
  const { name, pack } = decodeStickBaseProductOption(v);
  if (!name) return v || '';
  if (pack == null) return name;
  const unitWord = pack === 1 ? 'unit' : 'units';
  return `${name} (${pack} ${unitWord})`;
}

function resolveBrandBoardBaseProduct(isStickRoute, baseProduct, baseProductSkuOverride) {
  if (baseProductSkuOverride) {
    return { lookupKey: baseProductSkuOverride, packFilter: null };
  }
  if (!baseProduct) {
    return { lookupKey: null, packFilter: null };
  }
  if (isStickRoute && baseProduct.includes(STICK_BASE_PRODUCT_PACK_SEP)) {
    const d = decodeStickBaseProductOption(baseProduct);
    return { lookupKey: d.name, packFilter: d.pack };
  }
  return { lookupKey: baseProduct, packFilter: null };
}

function isLimitedEditionSkuTitle(title, productFlags) {
  const t = (title || '').trim();
  if (!t) return false;
  if (productFlags?.[t]?.portfolioType === 'Limited edition') return true;
  if (/\blimited edition\b|\bedycja limitowana\b|\blimitowana\b/i.test(t)) return true;
  return false;
}

const BRAND_HEADER_CONFIG = {
  IQOS: { label: 'iqos / iluma', color: COLOR_PALETTE.brands.IQOS },
  GLO: { label: 'glo', color: COLOR_PALETTE.brands.GLO },
  PLOOM: { label: 'ploom', color: COLOR_PALETTE.brands.PLOOM },
  PULZE: { label: 'pulze', color: COLOR_PALETTE.brands.PULZE },
  LIL: { label: 'lil', color: COLOR_PALETTE.brands.LIL }
};

const BRAND_KEY_ALIASES = {
  IQOS: 'IQOS',
  GLO: 'GLO',
  PLOOM: 'PLOOM',
  PULZE: 'PULZE',
  LIL: 'LIL'
};

const PRICE_TYPE_OPTIONS = [
  { key: 'regular', label: 'Regular Price', info: 'The regular shelf price listed by the retailer, before any promotions.' },
  { key: 'promo', label: 'Promo Price', info: 'The discounted price applied during a promotional period.' },
  { key: 'actual', label: 'Actual Price', info: 'The minimal price for which it is possible to buy the product, including promo prices.', italic: true },
];

const PRODUCT_TRI_STATE_INCLUDE = 'Include';
const PRODUCT_TRI_STATE_EXCLUDE = 'Exclude';
const PRODUCT_TRI_STATE_ONLY = 'Only';
const PRODUCT_TRI_STATE_FILTER_OPTIONS = [
  PRODUCT_TRI_STATE_INCLUDE,
  PRODUCT_TRI_STATE_EXCLUDE,
  PRODUCT_TRI_STATE_ONLY,
];

const STARTER_KIT_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const STARTER_KIT_FILTER_INCLUDE = PRODUCT_TRI_STATE_INCLUDE;
const STARTER_KIT_FILTER_EXCLUDE = PRODUCT_TRI_STATE_EXCLUDE;
const STARTER_KIT_FILTER_ONLY = PRODUCT_TRI_STATE_ONLY;
const STARTER_KIT_FILTER_DEFAULT = STARTER_KIT_FILTER_EXCLUDE;

const LIMITED_EDITION_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const LIMITED_EDITION_FILTER_DEFAULT = PRODUCT_TRI_STATE_INCLUDE;

const MANUFACTURER_TO_BRANDS = {
  JTI: ['Ploom'],
  BAT: ['glo'],
  PMI: ['IQOS', 'Lil', 'LIL', 'Bonds', 'BONDS'],
  'Imperial Brands': ['Imperial Brands', 'Pulze', 'blu'],
};
const MANUFACTURER_OPTIONS = ['JTI', 'BAT', 'PMI', 'Imperial Brands'];
const getManufacturerBrandKeywords = (m) => (MANUFACTURER_TO_BRANDS[m] || []).map((b) => normalizeValue(b));

function coerceProductTriStateLabel(raw) {
  if (PRODUCT_TRI_STATE_FILTER_OPTIONS.includes(raw)) return raw;
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'include' || s.startsWith('include ')) return PRODUCT_TRI_STATE_INCLUDE;
  if (s === 'exclude' || s.startsWith('exclude ')) return PRODUCT_TRI_STATE_EXCLUDE;
  if (s === 'only' || s.startsWith('only ')) return PRODUCT_TRI_STATE_ONLY;
  return null;
}

function normalizeStarterKitFilter(raw) {
  const coerced = coerceProductTriStateLabel(raw);
  if (coerced) return coerced;
  if (Array.isArray(raw)) {
    return raw.includes('starter_kit') || raw.includes(STARTER_KIT_FILTER_ONLY)
      ? STARTER_KIT_FILTER_ONLY
      : STARTER_KIT_FILTER_INCLUDE;
  }
  if (raw === 'starter_kit') return STARTER_KIT_FILTER_ONLY;
  return STARTER_KIT_FILTER_DEFAULT;
}

function normalizeLimitedEditionFilter(raw) {
  const coerced = coerceProductTriStateLabel(raw);
  if (coerced) return coerced;
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'all editions' || s === 'all') return LIMITED_EDITION_FILTER_DEFAULT;
  if (s === 'regular editions only' || s.includes('regular')) return PRODUCT_TRI_STATE_EXCLUDE;
  if (s === 'limited editions only' || s.includes('limited')) return PRODUCT_TRI_STATE_ONLY;
  return LIMITED_EDITION_FILTER_DEFAULT;
}

function rowMatchesLimitedEditionFilter(item, editionFilter, productFlags) {
  const mode = normalizeLimitedEditionFilter(editionFilter);
  if (mode === PRODUCT_TRI_STATE_INCLUDE) return true;
  const title = item?.title || item?._originalTitle;
  if (!title) return mode !== PRODUCT_TRI_STATE_ONLY;
  const isLimited = isLimitedEditionSkuTitle(title, productFlags);
  if (mode === PRODUCT_TRI_STATE_ONLY) return isLimited;
  return !isLimited;
}

function isStarterKitTaggedSku(title, productFlags) {
  if (!title) return false;
  return !!productFlags?.[title]?.isStarterKitOrRegistration;
}

function rowMatchesStarterKitProductFilter(item, starterKitFilter, productFlags) {
  const mode = normalizeStarterKitFilter(starterKitFilter);
  if (mode === STARTER_KIT_FILTER_INCLUDE) return true;
  const title = item?.title || item?._originalTitle;
  if (!title) return mode !== STARTER_KIT_FILTER_ONLY;
  const isKit = isStarterKitTaggedSku(title, productFlags);
  if (mode === STARTER_KIT_FILTER_ONLY) return isKit;
  return !isKit;
}

function getManufacturerForBrand(brand) {
  const itemBrand = normalizeValue(brand || '');
  if (!itemBrand) return null;
  for (const manufacturer of MANUFACTURER_OPTIONS) {
    const keywords = getManufacturerBrandKeywords(manufacturer);
    if (keywords.some((kw) => itemBrand === kw || itemBrand.includes(kw))) {
      return manufacturer;
    }
  }
  return null;
}

const getManufacturerPaletteColor = (manufacturer) =>
  COLOR_PALETTE.manufacturers?.[manufacturer] || COLOR_PALETTE.manufacturers?.OTHERS || '#A6A6A6';

function sanitizeActivePriceTypes(priceTypes) {
  if (!(priceTypes instanceof Set) || !priceTypes.has('starter_kit')) return priceTypes;
  const next = new Set(priceTypes);
  next.delete('starter_kit');
  return next;
}

/** Non-empty Select value for “first product = 100%” so the outlined label shrinks (empty string + displayEmpty overlaps the label). */
const BASE_PRODUCT_SELECT_FIRST = '__brand_board_first_product__';
const DATE_MODE_DAILY = 'daily';
const DATE_MODE_WEEKLY = 'weekly';
const DATE_MODE_MONTHLY = 'monthly';
const DATE_AGGREGATION_DAY = 'day';
const DATE_AGGREGATION_AVERAGE = 'average';
const BASE_COMPARE_TYPE_AUTO = 'auto';
const BASE_COMPARE_TYPE_LISTED = 'listed';
const BASE_COMPARE_TYPE_PROMO = 'promo';
const BASE_COMPARE_TYPE_STARTER_KIT = 'starter_kit';
const BRAND_BOARD_ACCENT = '#2e7d32';
const BRAND_BOARD_ACCENT_SOFT = 'rgba(46, 125, 50, 0.12)';
const INTERNAL_REPORTING_STORAGE_KEY = 'brand_board_internal_reporting_v1';
const INTERNAL_REPORTING_COMPARISONS_KEY = 'brand_board_internal_reporting_comparisons_v1';
/** When false, hides “Internal reporting comparison testing” and skips its localStorage / paste listeners. */
const SHOW_INTERNAL_REPORTING_COMPARISON_TESTING = false;
/** Brand board main area: classic column grid vs canvas-style bands with larger pack shots. */
const BRAND_BOARD_VISUAL_GRID = 'grid';
const BRAND_BOARD_VISUAL_CANVAS = 'canvas';
/** Canvas layout: fixed brand columns (same family as table board). */
const CANVAS_BRAND_COLUMN_KEYS = ['IQOS', 'GLO', 'PLOOM'];
/** 1px rules only — match strength of horizontal grid lines inside bands. */
const CANVAS_GRID_LINE = '1px solid rgba(0,0,0,0.09)';
/** Same template for brand header row and product rows so columns line up. */
const CANVAS_COL_BG_LIGHT = '#f9f9fa';
const CANVAS_COL_BG_SHADE = '#f0f0f3';
const CANVAS_BAND_GRID_BG =
  'repeating-linear-gradient(180deg, transparent 0, transparent 40px, rgba(0,0,0,0.06) 40px, rgba(0,0,0,0.06) 41px)';
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

/**
 * Inline remote <img> sources as data URLs before html2canvas, then restore originals.
 * Tries CORS fetch first; falls back to `GET {API_URL}/api/image-proxy` when the CDN blocks the browser.
 */
async function withInlinedImagesForExport(container, captureFn) {
  if (!container || typeof container.querySelectorAll !== 'function') {
    return captureFn();
  }
  const imgs = [...container.querySelectorAll('img')];
  const originals = imgs.map((img) => img.getAttribute('src') || '');

  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            if (typeof img.decode === 'function') {
              img.decode().then(resolve).catch(resolve);
            } else {
              resolve();
            }
          } else {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }
        })
    )
  );

  const apiBase = (API_URL || '').replace(/\/$/, '');

  await Promise.all(
    imgs.map(async (img, idx) => {
      const raw = originals[idx];
      if (!raw || raw.startsWith('data:')) return;
      let absolute;
      try {
        absolute = new URL(raw, window.location.href).href;
      } catch {
        return;
      }
      let blob = null;
      try {
        const r = await fetch(absolute, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
        if (r.ok) {
          const b = await r.blob();
          if (b && (!b.type || b.type.startsWith('image/') || b.type === 'application/octet-stream')) blob = b;
        }
      } catch {
        /* try proxy */
      }
      if (!blob && apiBase) {
        try {
          const proxyUrl = `${apiBase}/api/image-proxy?url=${encodeURIComponent(absolute)}`;
          const r2 = await fetch(proxyUrl, { credentials: 'omit' });
          if (r2.ok) {
            const b2 = await r2.blob();
            if (b2 && (!b2.type || b2.type.startsWith('image/') || b2.type === 'application/octet-stream')) blob = b2;
          }
        } catch {
          /* keep original */
        }
      }
      if (!blob) return;
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onloadend = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
      await new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        img.src = dataUrl;
        if (img.complete && img.naturalWidth > 0) done();
      });
    })
  );

  try {
    return await captureFn();
  } finally {
    imgs.forEach((img, idx) => {
      const o = originals[idx];
      if (o != null && o !== '') img.setAttribute('src', o);
    });
  }
}

const normalizeKey = (value) => (value || '').toString().trim().toUpperCase();
const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();

// Brands to exclude entirely from the brand board (hidden from the Brand filter and the board).
const EXCLUDED_BRANDS = new Set(['heccig', 'dropp']);
const isExcludedBrand = (brand) => EXCLUDED_BRANDS.has(normalizeValue(brand));
const normalizeTitle = (value) => normalizeKey(value).replace(/[^A-Z0-9]+/g, ' ');

const normalizeCurrencyCode = (value) => {
  const c = (value || '').toString().trim().toUpperCase();
  return c || 'EUR';
};

/** Same static rates as Price & Promotions (value in EUR per 1 unit of foreign currency). */
const EXCHANGE_RATES_TO_EUR = {
  EUR: 1,
  GBP: 1.16,
  USD: 0.93,
  CHF: 1.04,
  RON: 0.2,
  PLN: 0.23,
  SEK: 0.089,
  NOK: 0.088,
  DKK: 0.134,
  CZK: 0.04,
  HUF: 0.0026,
};

const convertToEur = (value, currency) => {
  const rate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(currency)];
  const numericValue = parseFloat(value);
  if (!rate || Number.isNaN(numericValue)) {
    return Number.isNaN(numericValue) ? 0 : numericValue;
  }
  return Number((numericValue * rate).toFixed(2));
};

const convertToCurrency = (value, fromCurrency, toCurrency) => {
  if (!toCurrency || normalizeCurrencyCode(fromCurrency) === normalizeCurrencyCode(toCurrency)) {
    return parseFloat(value);
  }
  const eurValue = convertToEur(value, fromCurrency);
  const toRate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(toCurrency)];
  if (!toRate) return eurValue;
  const numericValue = parseFloat(eurValue);
  if (Number.isNaN(numericValue)) return eurValue;
  return Number((numericValue / toRate).toFixed(2));
};

/** Show price in the dataset's local currency (row `currency`); no FX conversion. */
const formatLocalPriceDisplay = (amountStr, currencyCode) => {
  const c = normalizeCurrencyCode(currencyCode);
  const n = parseFloat(amountStr);
  if (!Number.isFinite(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  const s = parseFloat(rounded.toFixed(2)).toString();
  if (c === 'EUR') return `€${s}`;
  if (c === 'GBP') return `£${s}`;
  if (c === 'USD') return `$${s}`;
  if (c === 'CHF') return `CHF ${s}`;
  return `${s} ${c}`;
};

const boardPricesDiffer = (a, b, eps = 0.02) => {
  const na = parseFloat(a);
  const nb = parseFloat(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return true;
  return Math.abs(na - nb) > eps;
};

const formatBoardPriceWithIndex = (amountStr, currencyCode, indexPct, showQuestionIndex) => {
  const base = formatLocalPriceDisplay(amountStr, currencyCode);
  if (indexPct != null) return `${base} / ${indexPct}%`;
  if (showQuestionIndex) return `${base} / ?%`;
  return base;
};

const getListedPromoInlineParts = (product) => {
  const promoPart = formatBoardPriceWithIndex(
    product.promoDisplayPrice,
    product.promoDisplayCurrency,
    product.priceIndexPromo,
    product.priceIndexPromo == null && product.promoCanvasListedFallbackPct != null
  );
  const listedPart = formatBoardPriceWithIndex(
    product.listedDisplayPrice,
    product.listedDisplayCurrency,
    product.priceIndexListed ?? product.priceIndex,
    false
  );
  const promoLabel = `${promoPart} - Promo Price`;
  const listedLabel = `${listedPart} - Regular Price`;
  return {
    promoLabel,
    listedLabel,
    fullLabel: `${promoLabel} | ${listedLabel}`,
  };
};

const buildListedPromoInlineLabel = (product) => getListedPromoInlineParts(product).fullLabel;

const isBundleRow = (product) =>
  normalizeValue(product?.category_group || product?.clean_category).includes('bundle');

// For bundles the hover overlay shows only the promo price (never the Listed Price),
// even when the promo price equals the listed price.
const getBundleHoverTitle = (product, displayTitle) => {
  const promoStr =
    product.promoDisplayPrice != null && product.promoDisplayCurrency != null
      ? formatLocalPriceDisplay(product.promoDisplayPrice, product.promoDisplayCurrency)
      : formatLocalPriceDisplay(product.displayPrice, product.displayCurrency);
  const base = (displayTitle || '').replace(/\s*-\s*Promo Price$/i, '');
  return `${base} - Promo Price: ${promoStr}`;
};

const PRODUCT_TYPE_ALIASES = {
  devices: ['devices', 'device'],
  sticks: ['sticks', 'stick'],
  accessories: ['accessories', 'accessory'],
};

// Bundle Selection filter — unified with the same filter in the Price & Promotions view.
const BUNDLE_SELECTION_OPTIONS_BY_ROUTE = {
  devices: [
    { label: 'Devices', canonical: 'Devices' },
    { label: 'Device Bundle', canonical: 'Device Bundles' },
  ],
  sticks: [
    { label: 'Sticks', canonical: 'Sticks' },
    { label: 'Sticks Bundles', canonical: 'Sticks Bundles' },
  ],
  accessories: [{ label: 'Accessories', canonical: 'Accessories' }],
};

const resolveCategoryGroupOptionValue = (canonical, categoryGroupOptions) => {
  if (!canonical) return null;
  const exact = (categoryGroupOptions || []).find((option) => option === canonical);
  if (exact) return exact;
  const target = normalizeValue(canonical);
  return (categoryGroupOptions || []).find((option) => normalizeValue(option) === target) || canonical;
};

const resolveCategoryGroupForType = (productType, options) => {
  if (!productType || !Array.isArray(options) || options.length === 0) {
    return null;
  }
  const normalizedType = normalizeValue(productType);
  if (!normalizedType) return null;
  const isDevices = PRODUCT_TYPE_ALIASES.devices.includes(normalizedType);
  const isSticks = PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType);
  const isAccessories = PRODUCT_TYPE_ALIASES.accessories.includes(normalizedType);
  if (!isDevices && !isSticks && !isAccessories) return null;
  if (isDevices) {
    const exactMatch = options.find((option) => option === 'Devices');
    if (exactMatch) return exactMatch;
    const match = options.find((option) => normalizeValue(option).includes('device'));
    return match || null;
  }
  if (isSticks) {
    const exactMatch = options.find((option) => option === 'Sticks');
    if (exactMatch) return exactMatch;
    const match = options.find((option) => normalizeValue(option).includes('stick'));
    return match || null;
  }
  const exactAccessories = options.find((option) => option === 'Accessories');
  if (exactAccessories) return exactAccessories;
  return options.find((option) => normalizeValue(option).includes('accessories')) || null;
};

const getBrandKey = (brand) => BRAND_KEY_ALIASES[normalizeKey(brand)] || normalizeKey(brand);
const getBrandPaletteColor = (brand) => COLOR_PALETTE.brands?.[getBrandKey(brand)];

const getProductImageUrl = (product) =>
  resolveProductImageUrlFromFields(
    product?.image,
    product?.box_image_urls,
    product?.image_urls,
    product?.media_gallery_urls
  );

const getContrastTextColor = (hexColor) => {
  if (!hexColor || !hexColor.startsWith('#')) return '#0a0a0a';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#0a0a0a' : '#ffffff';
};

const getProductPaletteInfo = (brand, title) => {
  const brandKey = getBrandKey(brand);
  const palette = COLOR_PALETTE.brandProducts?.[brandKey];
  if (!palette) return null;
  const normalizedTitle = normalizeTitle(title);
  const groups = ['core', 'secondary'];
  for (const group of groups) {
    const entries = palette[group] || {};
    for (const [lineName, color] of Object.entries(entries)) {
      if (normalizedTitle.includes(normalizeTitle(lineName))) {
        return { color, lineName, group };
      }
    }
  }
  return null;
};

const MARKET_COUNTRY_ALIASES = {
  uk: ['uk', 'united kingdom', 'gb', 'gbr', 'great britain', 'england'],
  italy: ['italy', 'it', 'ita', 'italia'],
  romania: ['romania', 'ro', 'rou'],
  germany: ['germany', 'de', 'deu', 'deutschland'],
  france: ['france', 'fr', 'fra', 'francia'],
  spain: ['spain', 'es', 'esp', 'espana'],
  poland: ['poland', 'pl', 'pol', 'polska'],
  czech: ['czech', 'cz', 'czech republic', 'ceska', 'cesko'],
  cz: ['czech', 'cz', 'czech republic', 'ceska', 'cesko']
};

const resolveMarketCountry = (marketId, availableCountries) => {
  if (!marketId || !Array.isArray(availableCountries) || availableCountries.length === 0) {
    return null;
  }
  const aliases = MARKET_COUNTRY_ALIASES[marketId];
  if (!aliases) return null;
  return availableCountries.find((country) => aliases.includes(normalizeValue(country))) || null;
};

/** Check if item country matches selected country (handles uk/UK/gb etc.) */
const countryMatches = (itemCountry, selectedCountry) => {
  if (!selectedCountry) return true;
  if (!itemCountry) return false;
  const itemNorm = normalizeValue(itemCountry);
  const selNorm = normalizeValue(selectedCountry);
  if (itemNorm === selNorm) return true;
  const aliases = MARKET_COUNTRY_ALIASES[selNorm] || Object.entries(MARKET_COUNTRY_ALIASES).find(([, a]) => a.includes(selNorm))?.[1];
  return aliases ? aliases.includes(itemNorm) : false;
};

const PROJECT_TO_COUNTRY_MATCH = {
  cz: ['cz', 'czech', 'czech republic', 'ceska', 'cesko'],
  ro: ['ro', 'romania', 'rou'],
  it: ['it', 'italy', 'ita', 'italia'],
  uk: ['uk', 'united kingdom', 'gb', 'gbr'],
  pl: ['pl', 'poland', 'pol', 'polska'],
  de: ['de', 'germany', 'deu', 'deutschland']
};

const getDefaultCountryForProject = (projectKey, availableCountries) => {
  if (!projectKey || !Array.isArray(availableCountries) || availableCountries.length === 0) {
    return null;
  }
  const key = (projectKey || '').toString().toLowerCase();
  const matchList = PROJECT_TO_COUNTRY_MATCH[key] ||
    Object.entries(PROJECT_TO_COUNTRY_MATCH).find(([code]) => key.includes(code))?.[1];
  if (!matchList) return null;
  return availableCountries.find((c) => matchList.some((m) => normalizeValue(c) === m || normalizeValue(c).includes(m))) || null;
};

const getReportingProductKey = (product) =>
  `${product.country || ''}__${product.retailer || ''}__${product._originalTitle || product.title || ''}`;

const isReportedPriceMatch = (reportedPrice, boardPrice) => {
  const reported = parseFloat(reportedPrice);
  const board = parseFloat(boardPrice);
  if (!Number.isFinite(reported) || !Number.isFinite(board)) return false;
  return Math.abs(reported - board) < 0.005;
};

const DEFAULT_REPORTING_ENTRY = { reportedPrice: '', acceptedDiscrepancy: false, note: '' };

const getReportingEntryDefaults = (raw) => {
  const e = raw && typeof raw === 'object' ? raw : {};
  return {
    reportedPrice: (e.reportedPrice ?? '').toString(),
    acceptedDiscrepancy: !!e.acceptedDiscrepancy,
    note: (e.note ?? '').toString(),
    missing: !!e.missing,
  };
};

/** Board row: legacy entries use "missing"; new entries use accept + optional note. */
const isBoardRowTick = (entryRaw, boardPrice) => {
  const d = getReportingEntryDefaults(entryRaw);
  const match = isReportedPriceMatch(d.reportedPrice, boardPrice);
  const legacy = !entryRaw || !Object.prototype.hasOwnProperty.call(entryRaw, 'acceptedDiscrepancy');
  if (legacy) return !d.missing && match;
  return match || d.acceptedDiscrepancy;
};

const isManualReportingRowTick = (row) => {
  const legacy = !row || !Object.prototype.hasOwnProperty.call(row, 'acceptedDiscrepancy');
  if (legacy) return false;
  return !!row.acceptedDiscrepancy;
};

/** Accordion summary counts from persisted snapshot (same rules as live table). */
const statusCountsFromRowSnapshot = (rowSnapshot) => {
  if (!Array.isArray(rowSnapshot) || rowSnapshot.length === 0) {
    return { totalCount: 0, tickCount: 0, alertCount: 0 };
  }
  let tickCount = 0;
  rowSnapshot.forEach((row) => {
    const legacy = row?.acceptedDiscrepancy === undefined;
    let isTick = false;
    if (row?.isManualRow) {
      if (legacy) isTick = false;
      else isTick = !!row?.acceptedDiscrepancy;
    } else {
      const match = isReportedPriceMatch(row?.reportedPrice, row?.boardPrice);
      if (legacy) isTick = !row?.missing && match;
      else isTick = match || !!row?.acceptedDiscrepancy;
    }
    if (isTick) tickCount += 1;
  });
  const totalCount = rowSnapshot.length;
  const alertCount = totalCount - tickCount;
  return { totalCount, tickCount, alertCount };
};

/** Internal reporting: one bucket per project + product type (HQ and Market share the same list). */
const buildReportingStorageScopeBase = (projectKey, productType) =>
  `${projectKey || 'unknown'}__${productType || 'all'}`;

const mergeSavedComparisonsFromStorage = (parsed, scopeBase) => {
  const candidates = [
    parsed[scopeBase],
    parsed[`${scopeBase}__hq`],
    parsed[`${scopeBase}__market`],
  ].filter(Array.isArray);
  if (candidates.length === 0) return [];
  const byId = new Map();
  candidates.forEach((list) => {
    list.forEach((item) => {
      const id = item.uniqueContextKey || item.id;
      if (!id) return;
      const existing = byId.get(id);
      if (!existing) {
        byId.set(id, item);
        return;
      }
      const tNew = new Date(item.createdAt || 0).getTime();
      const tOld = new Date(existing.createdAt || 0).getTime();
      if (tNew >= tOld) byId.set(id, item);
    });
  });
  return Array.from(byId.values()).sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
};

const getIsoWeekMeta = (dateValue) => {
  const d = dayjs(dateValue).startOf('day');
  if (!d.isValid()) return null;
  const isoDay = ((d.day() + 6) % 7) + 1; // Monday=1 ... Sunday=7
  const thursday = d.add(4 - isoDay, 'day');
  const isoYear = thursday.year();
  const firstThursday = dayjs(`${isoYear}-01-04`).startOf('day');
  const firstIsoDay = ((firstThursday.day() + 6) % 7) + 1;
  const firstIsoThursday = firstThursday.add(4 - firstIsoDay, 'day');
  const week = Math.floor(thursday.diff(firstIsoThursday, 'day') / 7) + 1;
  const weekStart = d.subtract(isoDay - 1, 'day').startOf('day');
  return {
    isoYear,
    week,
    isoDay,
    weekKey: `${isoYear}-W${String(week).padStart(2, '0')}`,
    weekLabel: `Week ${week} (${weekStart.format('YYYY-MM-DD')})`,
    weekStart,
  };
};

function BrandBoardSummaryPaletteView({ scopeMode, selectedMarket }) {
  const { projectKey, productType } = useParams();
  const { user } = useAuth();
  const isMarketScope = scopeMode === 'market';
  const isHqScope = scopeMode === 'hq';
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filteredDataRaw, setFilteredDataRaw] = useState([]); // pre-matching-group, used by brand board
  /** Same filters as filteredDataRaw but without brand filter — baseline SKU may be another brand (e.g. IQOS vs Ploom-only board). */
  const [filteredDataRawBeforeBrand, setFilteredDataRawBeforeBrand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCountry, setSelectedCountry] = useState('ro');
  const [countryTouched, setCountryTouched] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [isInitialBrandSelectionResolved, setIsInitialBrandSelectionResolved] = useState(false);
  const [selectedCategoryGroups, setSelectedCategoryGroups] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [dateSelectionMode, setDateSelectionMode] = useState(DATE_MODE_DAILY);
  const [dateAggregationMode, setDateAggregationMode] = useState(DATE_AGGREGATION_DAY);
  const [selectedDailyDateKey, setSelectedDailyDateKey] = useState('');
  const [selectedWeekKey, setSelectedWeekKey] = useState('');
  const [selectedWeekday, setSelectedWeekday] = useState(1);
  const [selectedMonthKey, setSelectedMonthKey] = useState('');
  const [selectedMonthDay, setSelectedMonthDay] = useState(1);
  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null);
  const [reportingScreenshotFileName, setReportingScreenshotFileName] = useState('');
  const [reportingScreenshotPreview, setReportingScreenshotPreview] = useState('');
  const [reportingEntries, setReportingEntries] = useState({});
  const [reportingManualMissingProducts, setReportingManualMissingProducts] = useState([]);
  const [reportingSectionExpanded, setReportingSectionExpanded] = useState(false);
  const [reportingUploadDialogOpen, setReportingUploadDialogOpen] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const dateRange = selectedDate ? [selectedDate, selectedDate] : [null, null]; // single-date as [d,d] for compatibility
  const [searchTerm, setSearchTerm] = useState('');

  const [productFlagsFromStorage, setProductFlagsFromStorage] = useState({});
  const [activePriceTypes, setActivePriceTypes] = useState(() => new Set(['regular']));
  const [starterKitProductFilter, setStarterKitProductFilter] = useState(STARTER_KIT_FILTER_DEFAULT);
  const [limitedEditionFilter, setLimitedEditionFilter] = useState(LIMITED_EDITION_FILTER_DEFAULT);

  const [filterOptions, setFilterOptions] = useState({ retailers: [], brands: [] });
  const [availableCategoryGroups, setAvailableCategoryGroups] = useState([]);
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [productPopupData, setProductPopupData] = useState({
    productName: null,
    productImage: null,
    productData: null,
    productDataRows: [],
    groupProductNames: undefined,
  });

  // HQ-only: group columns by brands or countries
  const [groupBy, setGroupBy] = useState('brands');

  // Filter search terms
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');
  const [retailerGroupsExpanded, setRetailerGroupsExpanded] = useState({
    manufacturer: true,
    thirdParty: true,
  });
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  // Pack size + price display filters
  const [selectedPackSizes, setSelectedPackSizes] = useState([]);
  const [packSizeSearchTerm, setPackSizeSearchTerm] = useState('');
  const isSticksFromRoute = productType && PRODUCT_TYPE_ALIASES.sticks.includes(normalizeValue(productType));
  const isDevicesFromRoute = productType && PRODUCT_TYPE_ALIASES.devices.includes(normalizeValue(productType));
  const isAccessoriesFromRoute =
    productType && PRODUCT_TYPE_ALIASES.accessories.includes(normalizeValue(productType));
  const [priceDisplayMode, setPriceDisplayMode] = useState(isSticksFromRoute ? 'per_unit' : 'absolute');
  const [baseProduct, setBaseProduct] = useState(null);
  const [baseProductSkuOverride, setBaseProductSkuOverride] = useState('');
  const [baseProductAutoQueues, setBaseProductAutoQueues] = useState(() =>
    readBaseProductAutoQueuesFromStorage()
  );
  const [baseProductAutoQueueDialogOpen, setBaseProductAutoQueueDialogOpen] = useState(false);
  const [baseProductAutoQueueDraft, setBaseProductAutoQueueDraft] = useState([]);
  const [compareToPriceType, setCompareToPriceType] = useState(BASE_COMPARE_TYPE_AUTO);
  /** 'local' = each row’s own currency (HQ only in the menu); market uses dominant row currency via effectiveDisplayCurrency. Index % still uses EUR. */
  const [displayCurrencySetting, setDisplayCurrencySetting] = useState('local');
  const [brandBoardVisualStyle, setBrandBoardVisualStyle] = useState(BRAND_BOARD_VISUAL_GRID);
  const [canvasExportBusy, setCanvasExportBusy] = useState(false);
  const canvasBoardExportRef = useRef(null);
  const prevScopeModeRef = React.useRef(null);
  const defaultPloomBrandAppliedRef = useRef(false);
  const hqBrandBoardDefaultsAppliedRef = useRef(false);
  const hqDevicesBaseDefaultAppliedRef = useRef(false);
  const initialBaseProductDefaultAppliedRef = useRef(false);
  const defaultManufacturerRetailersAppliedRef = useRef(false);

  // Matching groups (loaded from API, synced on dataset load)
  const [matchingGroups, setMatchingGroups] = useState(new Map());

  const [packSizeOverridesFromStorage, setPackSizeOverridesFromStorage] = useState({});

  // Same logic as Price and Promotions: pack size from overrides or Units (#), used for per-unit price division
  const getItemPackSize = useCallback((item) => {
    if (!item) return null;
    const titleForLookup = item._originalTitle || item.title;
    const saved = packSizeOverridesFromStorage[titleForLookup];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        return !isNaN(n) && n > 0 ? n : null;
      }
      const n = Number(saved.packSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    const raw = item['Units (#)'] ?? item.pack_size ?? item.pack_size_number;
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !isNaN(n) && n > 0 ? n : null;
  }, [packSizeOverridesFromStorage]);

  useEffect(() => {
    loadData();
  }, []);

  const groupNameToProducts = useMemo(() => {
    const map = new Map();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      const gName = info?.groupName;
      if (!gName) return;
      if (!map.has(gName)) map.set(gName, []);
      map.get(gName).push(title);
    });
    return map;
  }, [matchingGroups]);

  const routeCategoryGroup = useMemo(
    () => resolveCategoryGroupForType(productType, availableCategoryGroups),
    [productType, availableCategoryGroups]
  );

  const categoryGroupOptions = useMemo(() => {
    if (!productType) return availableCategoryGroups;
    const normalizedType = normalizeValue(productType);
    const isDevices = PRODUCT_TYPE_ALIASES.devices.includes(normalizedType);
    const isSticks = PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType);
    const isAccessories = PRODUCT_TYPE_ALIASES.accessories.includes(normalizedType);
    if (isDevices) {
      return availableCategoryGroups.filter((opt) => opt === 'Devices' || opt === 'Device Bundles');
    }
    if (isSticks) {
      return availableCategoryGroups.filter((opt) => opt === 'Sticks' || opt === 'Sticks Bundles');
    }
    if (isAccessories) {
      return availableCategoryGroups.filter((opt) => opt === 'Accessories');
    }
    return availableCategoryGroups;
  }, [productType, availableCategoryGroups]);

  // Bundle Selection options/value — mirrors the Price & Promotions "Bundle Selection" filter.
  const bundleSelectionFilterOptions = useMemo(() => {
    const defs = isDevicesFromRoute
      ? BUNDLE_SELECTION_OPTIONS_BY_ROUTE.devices
      : isSticksFromRoute
      ? BUNDLE_SELECTION_OPTIONS_BY_ROUTE.sticks
      : isAccessoriesFromRoute
      ? BUNDLE_SELECTION_OPTIONS_BY_ROUTE.accessories
      : null;
    if (defs) {
      return defs
        .map(({ label, canonical }) => ({
          label,
          value: resolveCategoryGroupOptionValue(canonical, categoryGroupOptions),
        }))
        .filter((option) => option.value);
    }
    return (categoryGroupOptions || []).map((g) => ({ label: g, value: g }));
  }, [isDevicesFromRoute, isSticksFromRoute, categoryGroupOptions]);

  const bundleSelectionValue = useMemo(() => {
    const allowed = new Set(bundleSelectionFilterOptions.map((option) => option.value));
    return (selectedCategoryGroups || []).filter((group) => allowed.has(group));
  }, [bundleSelectionFilterOptions, selectedCategoryGroups]);

  const isStickRoute = useMemo(() => {
    const cats = selectedCategoryGroups.length > 0 ? selectedCategoryGroups : (routeCategoryGroup ? [routeCategoryGroup] : []);
    return cats.some((c) => normalizeValue(c).includes('stick'));
  }, [selectedCategoryGroups, routeCategoryGroup]);
  const baseProductAutoQueueRouteKey =
    isStickRoute || isSticksFromRoute ? 'sticks' : 'devices';
  const marketAnnouncementContext = useMemo(
    () => ({
      isStickRoute,
      isMarketScope,
      selectedMarket,
      selectedCountry,
      selectedCountries: selectedCountry ? [selectedCountry] : [],
      isHqScope,
      groupBy,
      filteredDataRaw,
    }),
    [
      isStickRoute,
      isMarketScope,
      selectedMarket,
      selectedCountry,
      isHqScope,
      groupBy,
      filteredDataRaw,
    ]
  );
  const {
    messages: marketAnnouncementMessages,
    hasNotice: showMarketAnnouncementsNotice,
    showAboveBoard: showMarketAnnouncementsAboveBoard,
  } = useResolvedMarketAnnouncements(marketAnnouncementContext);
  const showItalySticksNoticeAboveBoard = showMarketAnnouncementsAboveBoard;

  const packSizeOptions = useMemo(() => {
    const sizes = new Set();
    filteredDataRaw.forEach((item) => {
      const ps = getItemPackSize(item);
      if (ps !== null) sizes.add(ps);
    });
    return [...sizes].sort((a, b) => a - b);
  }, [filteredDataRaw, getItemPackSize]);

  const dominantCurrencyFromFilteredData = useMemo(() => {
    const counts = new Map();
    const rows = filteredDataRaw.length > 0 ? filteredDataRaw : data;
    rows.forEach((row) => {
      const c = (row.currency || '').toString().trim().toUpperCase();
      if (c) counts.set(c, (counts.get(c) || 0) + 1);
    });
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    return sorted[0]?.[0] || 'EUR';
  }, [filteredDataRaw, data]);

  const currencyFilterOptions = useMemo(() => {
    const fromData = new Set();
    data.forEach((row) => {
      const c = (row.currency || '').toString().trim().toUpperCase();
      if (c) fromData.add(c);
    });
    const rest = [...fromData].filter((c) => c !== 'EUR').sort();
    const base = ['EUR', ...rest];
    if (isHqScope) return ['local', ...base];
    return base;
  }, [data, isHqScope]);

  const effectiveDisplayCurrency = useMemo(() => {
    if (displayCurrencySetting === 'local') {
      if (isHqScope) return 'local';
      return dominantCurrencyFromFilteredData || 'EUR';
    }
    return displayCurrencySetting;
  }, [isHqScope, displayCurrencySetting, dominantCurrencyFromFilteredData]);

  const retailerOptions = useMemo(() => {
    const countryFilter =
      selectedCountry && groupBy !== 'countries'
        ? new Set([normalizeValue(selectedCountry)])
        : (isMarketScope
            ? (() => {
                const inferred = resolveMarketCountry(selectedMarket, availableCountries);
                return inferred ? new Set([normalizeValue(inferred)]) : null;
              })()
            : null);

    const retailers = new Set();
    data.forEach((item) => {
      const retailer = (item?.retailer || '').toString().trim();
      if (!retailer) return;
      if (countryFilter && !countryFilter.has(normalizeValue(item?.country))) return;
      retailers.add(retailer);
    });
    return Array.from(retailers).sort();
  }, [data, selectedCountry, groupBy, isMarketScope, selectedMarket, availableCountries]);

  const selectedRetailersSet = useMemo(
    () => new Set(selectedRetailers || []),
    [selectedRetailers]
  );
  const filteredRetailers = useMemo(
    () => (retailerOptions || []).filter((retailer) =>
      retailer.toLowerCase().includes((retailerSearchTerm || '').toLowerCase())
    ),
    [retailerOptions, retailerSearchTerm]
  );
  const manufacturerRetailers = useMemo(
    () => filteredRetailers.filter((retailer) => !THIRD_PARTY_RETAILERS.has(normalizeValue(retailer))),
    [filteredRetailers]
  );
  const thirdPartyRetailers = useMemo(
    () => filteredRetailers.filter((retailer) => THIRD_PARTY_RETAILERS.has(normalizeValue(retailer))),
    [filteredRetailers]
  );
  const toggleRetailerGroupSelection = useCallback((groupRetailers) => {
    if (!groupRetailers.length) return;
    const allSelected = groupRetailers.every((retailer) => selectedRetailersSet.has(retailer));
    if (allSelected) {
      const removalSet = new Set(groupRetailers);
      setSelectedRetailers((selectedRetailers || []).filter((retailer) => !removalSet.has(retailer)));
      return;
    }
    setSelectedRetailers(Array.from(new Set([...(selectedRetailers || []), ...groupRetailers])));
  }, [selectedRetailers, selectedRetailersSet]);

  useEffect(() => {
    if (!selectedRetailers.length) return;
    const allowed = new Set(retailerOptions);
    const next = selectedRetailers.filter((r) => allowed.has(r));
    if (next.length !== selectedRetailers.length) {
      setSelectedRetailers(next);
    }
  }, [retailerOptions, selectedRetailers]);

  useEffect(() => {
    if (defaultManufacturerRetailersAppliedRef.current || selectedRetailers.length > 0) return;
    if (!retailerOptions?.length) return;
    const mfr = getDefaultManufacturerRetailers(retailerOptions);
    if (mfr.length > 0) setSelectedRetailers(mfr);
    defaultManufacturerRetailersAppliedRef.current = true;
  }, [retailerOptions, selectedRetailers.length]);

  useEffect(() => {
    if (scopeMode === 'hq' && prevScopeModeRef.current != null && prevScopeModeRef.current !== 'hq') {
      setDisplayCurrencySetting('local');
    }
    prevScopeModeRef.current = scopeMode;
  }, [scopeMode]);

  // Same as Price & Promotions: product titles from current filters but not the snapshot date, then
  // groupNames + ungrouped-only (no duplicate SKUs that belong to a matching group).
  const filteredProductTitles = useMemo(() => {
    if (!data.length) return [];
    let items = [...data];
    const effectiveCategoryGroups = selectedCategoryGroups.length > 0
      ? selectedCategoryGroups
      : (routeCategoryGroup ? [routeCategoryGroup] : []);
    if (effectiveCategoryGroups.length > 0) {
      const normalizedGroups = new Set(effectiveCategoryGroups.map((value) => normalizeValue(value)));
      items = items.filter((row) => {
        const itemGroup = normalizeValue(row.category_group || row.clean_category);
        return normalizedGroups.has(itemGroup);
      });
    }
    if (selectedCountry && groupBy !== 'countries') {
      items = items.filter((row) => countryMatches(row.country, selectedCountry));
    }
    if (selectedRetailers.length > 0) {
      items = items.filter((row) => selectedRetailers.includes(row.retailer));
    }
    if (selectedBrands.length > 0) {
      items = items.filter((row) => selectedBrands.includes(row.brand));
    }
    if (selectedPackSizes.length > 0) {
      items = brandBoardFilterRowsByPackSelection(items, selectedPackSizes, getItemPackSize);
    }
    return [...new Set(items.map((row) => row.title).filter(Boolean))]
      .filter((title, index, self) => title && self.indexOf(title) === index)
      .sort();
  }, [
    data,
    selectedCountry,
    selectedRetailers,
    selectedBrands,
    selectedCategoryGroups,
    selectedPackSizes,
    routeCategoryGroup,
    groupBy,
    getItemPackSize,
  ]);

  /** Same as filteredProductTitles but ignores brand filter — HQ/devices can index vs IQOS while showing Ploom-only. */
  const filteredProductTitlesNoBrand = useMemo(() => {
    if (!data.length) return [];
    let items = [...data];
    const effectiveCategoryGroups = selectedCategoryGroups.length > 0
      ? selectedCategoryGroups
      : (routeCategoryGroup ? [routeCategoryGroup] : []);
    if (effectiveCategoryGroups.length > 0) {
      const normalizedGroups = new Set(effectiveCategoryGroups.map((value) => normalizeValue(value)));
      items = items.filter((row) => {
        const itemGroup = normalizeValue(row.category_group || row.clean_category);
        return normalizedGroups.has(itemGroup);
      });
    }
    if (selectedCountry && groupBy !== 'countries') {
      items = items.filter((row) => countryMatches(row.country, selectedCountry));
    }
    if (selectedRetailers.length > 0) {
      items = items.filter((row) => selectedRetailers.includes(row.retailer));
    }
    if (selectedPackSizes.length > 0) {
      items = brandBoardFilterRowsByPackSelection(items, selectedPackSizes, getItemPackSize);
    }
    return [...new Set(items.map((row) => row.title).filter(Boolean))]
      .filter((title, index, self) => title && self.indexOf(title) === index)
      .sort();
  }, [
    data,
    selectedCountry,
    selectedRetailers,
    selectedCategoryGroups,
    selectedPackSizes,
    routeCategoryGroup,
    groupBy,
    getItemPackSize,
  ]);

  /** Raw rows for stick base-product pack discovery (same slice as filteredProductTitlesNoBrand). */
  const stickRowsForBasePackDiscovery = useMemo(() => {
    if (!isStickRoute || !data.length) return [];
    let items = [...data];
    const effectiveCategoryGroups = selectedCategoryGroups.length > 0
      ? selectedCategoryGroups
      : (routeCategoryGroup ? [routeCategoryGroup] : []);
    if (effectiveCategoryGroups.length > 0) {
      const normalizedGroups = new Set(effectiveCategoryGroups.map((value) => normalizeValue(value)));
      items = items.filter((row) => {
        const itemGroup = normalizeValue(row.category_group || row.clean_category);
        return normalizedGroups.has(itemGroup);
      });
    }
    if (selectedCountry && groupBy !== 'countries') {
      items = items.filter((row) => countryMatches(row.country, selectedCountry));
    }
    if (selectedRetailers.length > 0) {
      items = items.filter((row) => selectedRetailers.includes(row.retailer));
    }
    if (selectedPackSizes.length > 0) {
      items = brandBoardFilterRowsByPackSelection(items, selectedPackSizes, getItemPackSize);
    }
    return items;
  }, [
    isStickRoute,
    data,
    selectedCountry,
    selectedRetailers,
    selectedCategoryGroups,
    selectedPackSizes,
    routeCategoryGroup,
    groupBy,
    getItemPackSize,
  ]);

  const groupNamesForBase = useMemo(() => {
    const filteredSet = new Set(filteredProductTitles);
    const names = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (filteredSet.has(title) && info?.groupName) names.add(info.groupName);
    });
    return names;
  }, [filteredProductTitles, matchingGroups]);

  const groupNamesForBaseNoBrand = useMemo(() => {
    const filteredSet = new Set(filteredProductTitlesNoBrand);
    const names = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (filteredSet.has(title) && info?.groupName) names.add(info.groupName);
    });
    return names;
  }, [filteredProductTitlesNoBrand, matchingGroups]);

  const baseProductOptions = useMemo(() => {
    const productsInGroups = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (info?.groupName) productsInGroups.add(title);
    });
    const ungroupedOnly = filteredProductTitles.filter((title) => !productsInGroups.has(title));
    return [...Array.from(groupNamesForBase).sort(), ...ungroupedOnly];
  }, [filteredProductTitles, groupNamesForBase, matchingGroups]);

  const baseProductOptionsNoBrand = useMemo(() => {
    const productsInGroups = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (info?.groupName) productsInGroups.add(title);
    });
    const ungroupedOnly = filteredProductTitlesNoBrand.filter((title) => !productsInGroups.has(title));
    return [...Array.from(groupNamesForBaseNoBrand).sort(), ...ungroupedOnly];
  }, [filteredProductTitlesNoBrand, groupNamesForBaseNoBrand, matchingGroups]);

  const baseProductOptionsForSelect = useMemo(() => {
    if (!isStickRoute) {
      const merged = new Set([...baseProductOptions, ...baseProductOptionsNoBrand]);
      return [...merged].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    const mergedKeys = new Set([...baseProductOptions, ...baseProductOptionsNoBrand]);
    const titlesInData = new Set(
      stickRowsForBasePackDiscovery.map((r) => (r.title || '').trim()).filter(Boolean)
    );
    const packsForTitle = (title) => {
      const pk = new Set();
      stickRowsForBasePackDiscovery.forEach((row) => {
        if ((row.title || '').trim() !== title) return;
        const ps = getItemPackSize(row);
        if (Number.isFinite(ps) && ps >= 1) pk.add(ps);
      });
      return Array.from(pk).sort((a, b) => a - b);
    };
    const out = [];
    [...mergedKeys]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .forEach((key) => {
        const groupSkus = groupNameToProducts.get(key);
        if (groupSkus && groupSkus.length > 0) {
          const packSet = new Set();
          groupSkus.forEach((sku) => {
            if (!titlesInData.has(sku)) return;
            packsForTitle(sku).forEach((p) => packSet.add(p));
          });
          if (packSet.size === 0) {
            out.push(encodeStickBaseProductOption(key, 'u'));
          } else {
            [...packSet].sort((x, y) => x - y).forEach((p) => out.push(encodeStickBaseProductOption(key, p)));
          }
        } else {
          if (!titlesInData.has(key)) return;
          const packs = packsForTitle(key);
          if (packs.length === 0) {
            out.push(encodeStickBaseProductOption(key, 'u'));
          } else {
            packs.forEach((p) => out.push(encodeStickBaseProductOption(key, p)));
          }
        }
      });
    return out;
  }, [
    isStickRoute,
    baseProductOptions,
    baseProductOptionsNoBrand,
    stickRowsForBasePackDiscovery,
    groupNameToProducts,
    getItemPackSize,
  ]);

  const autoBaseProductQueueChoices = useMemo(
    () =>
      buildAutoBaseProductQueueChoices(
        baseProductOptionsForSelect,
        filteredProductTitlesNoBrand,
        isStickRoute,
        groupNameToProducts
      ),
    [baseProductOptionsForSelect, filteredProductTitlesNoBrand, isStickRoute, groupNameToProducts]
  );

  const openBaseProductAutoQueueDialog = useCallback(() => {
    const key = baseProductAutoQueueRouteKey;
    const source =
      baseProductAutoQueues[key]?.length
        ? baseProductAutoQueues[key]
        : DEFAULT_BASE_PRODUCT_AUTO_QUEUES[key] || [];
    setBaseProductAutoQueueDraft(
      source.map((entry) =>
        migrateLegacyQueueEntry(
          entry,
          autoBaseProductQueueChoices,
          baseProductOptionsForSelect,
          isStickRoute
        )
      )
    );
    setBaseProductAutoQueueDialogOpen(true);
  }, [
    baseProductAutoQueueRouteKey,
    baseProductAutoQueues,
    autoBaseProductQueueChoices,
    baseProductOptionsForSelect,
    isStickRoute,
    groupNameToProducts,
  ]);

  const effectiveBaseProduct = useMemo(() => {
    if (baseProduct === BASE_PRODUCT_AUTO) {
      const queue = baseProductAutoQueues[baseProductAutoQueueRouteKey] || [];
      return pickBaseProductFromAutoQueue(
        queue,
        baseProductOptionsForSelect,
        isStickRoute,
        groupNameToProducts
      );
    }
    return baseProduct;
  }, [
    baseProduct,
    baseProductAutoQueues,
    baseProductAutoQueueRouteKey,
    baseProductOptionsForSelect,
    isStickRoute,
    groupNameToProducts,
  ]);

  const specificBaseSkuOptions = useMemo(() => {
    if (!effectiveBaseProduct) return [];
    const groupKey =
      isStickRoute && effectiveBaseProduct.includes(STICK_BASE_PRODUCT_PACK_SEP)
        ? decodeStickBaseProductOption(effectiveBaseProduct).name
        : effectiveBaseProduct;
    const groupSkus = groupNameToProducts.get(groupKey);
    const allowed = new Set(filteredProductTitlesNoBrand);
    let list;
    if (groupSkus && groupSkus.length > 0) {
      list = Array.from(new Set(groupSkus)).filter((sku) => allowed.has(sku));
    } else {
      list = allowed.has(groupKey) ? [groupKey] : [];
    }
    return list.sort((a, b) => {
      const aLe = isLimitedEditionSkuTitle(a, productFlagsFromStorage);
      const bLe = isLimitedEditionSkuTitle(b, productFlagsFromStorage);
      if (aLe !== bLe) return aLe ? 1 : -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
  }, [effectiveBaseProduct, groupNameToProducts, filteredProductTitlesNoBrand, isStickRoute, productFlagsFromStorage]);

  const preferredAutoBaseSku = useMemo(() => {
    if (!specificBaseSkuOptions.length) return '';
    const nonLe = specificBaseSkuOptions.find(
      (sku) => !isLimitedEditionSkuTitle(sku, productFlagsFromStorage)
    );
    return nonLe || specificBaseSkuOptions[0];
  }, [specificBaseSkuOptions, productFlagsFromStorage]);

  useEffect(() => {
    setBaseProductSkuOverride('');
  }, [effectiveBaseProduct]);

  useEffect(() => {
    if (
      baseProduct &&
      baseProduct !== BASE_PRODUCT_AUTO &&
      baseProductOptionsForSelect.length > 0 &&
      !baseProductOptionsForSelect.includes(baseProduct)
    ) {
      setBaseProduct(null);
    }
  }, [baseProduct, baseProductOptionsForSelect]);

  useEffect(() => {
    if (!baseProductSkuOverride) return;
    if (!specificBaseSkuOptions.includes(baseProductSkuOverride)) {
      setBaseProductSkuOverride('');
    }
  }, [baseProductSkuOverride, specificBaseSkuOptions]);

  useEffect(() => {
    if (defaultPloomBrandAppliedRef.current) return;
    if (isMarketScope) {
      defaultPloomBrandAppliedRef.current = true;
      setIsInitialBrandSelectionResolved(true);
      return;
    }
    const brands = filterOptions?.brands || [];
    if (!brands.length) {
      if (data.length > 0) {
        defaultPloomBrandAppliedRef.current = true;
        setIsInitialBrandSelectionResolved(true);
      }
      return;
    }
    if (selectedBrands.length > 0) {
      defaultPloomBrandAppliedRef.current = true;
      setIsInitialBrandSelectionResolved(true);
      return;
    }
    defaultPloomBrandAppliedRef.current = true;
    setIsInitialBrandSelectionResolved(true);
  }, [isMarketScope, filterOptions.brands, selectedBrands.length, data.length]);

  useEffect(() => {
    if (!isHqScope || !isDevicesFromRoute) {
      hqBrandBoardDefaultsAppliedRef.current = false;
      hqDevicesBaseDefaultAppliedRef.current = false;
      return;
    }
    if (hqBrandBoardDefaultsAppliedRef.current) return;
    const brands = filterOptions?.brands || [];
    if (brands.length === 0 || !data.length) return;
    hqBrandBoardDefaultsAppliedRef.current = true;
  }, [isHqScope, isDevicesFromRoute, filterOptions.brands, data.length]);

  useEffect(() => {
    if (!isHqScope || !isDevicesFromRoute) return;
    if (!hqBrandBoardDefaultsAppliedRef.current || hqDevicesBaseDefaultAppliedRef.current) return;
    if (baseProduct != null) {
      hqDevicesBaseDefaultAppliedRef.current = true;
      initialBaseProductDefaultAppliedRef.current = true;
      return;
    }
    if (!baseProductOptionsForSelect.length) return;
    setBaseProduct(BASE_PRODUCT_AUTO);
    hqDevicesBaseDefaultAppliedRef.current = true;
    initialBaseProductDefaultAppliedRef.current = true;
  }, [isHqScope, isDevicesFromRoute, baseProductOptionsForSelect, baseProduct]);

  useEffect(() => {
    if (initialBaseProductDefaultAppliedRef.current) return;
    if (baseProduct != null) {
      initialBaseProductDefaultAppliedRef.current = true;
      return;
    }
    if (!baseProductOptionsForSelect.length) return;
    if (isStickRoute || isSticksFromRoute || isDevicesFromRoute) {
      setBaseProduct(BASE_PRODUCT_AUTO);
    }
    initialBaseProductDefaultAppliedRef.current = true;
  }, [isStickRoute, isSticksFromRoute, isDevicesFromRoute, baseProduct, baseProductOptionsForSelect]);

  useEffect(() => {
    if (!isStickRoute) return;
    setStarterKitProductFilter(STARTER_KIT_FILTER_DEFAULT);
  }, [isStickRoute]);

  useEffect(() => {
    setActivePriceTypes((prev) => {
      if (!prev.has('starter_kit')) return prev;
      const next = sanitizeActivePriceTypes(prev);
      setStarterKitProductFilter((skPrev) =>
        skPrev === STARTER_KIT_FILTER_DEFAULT ? STARTER_KIT_FILTER_ONLY : skPrev
      );
      return next;
    });
  }, []);

  // Bundles are shown at their promo/offer price, not a listed shelf price. When only bundle
  // categories are selected (e.g. jumping to Device Bundles only), automatically switch Listed
  // Price → Promo Price.
  const isBundlesOnlySelection = useMemo(() => {
    if (!selectedCategoryGroups || selectedCategoryGroups.length === 0) return false;
    return selectedCategoryGroups.every((g) => normalizeValue(g).includes('bundle'));
  }, [selectedCategoryGroups]);

  useEffect(() => {
    if (!isBundlesOnlySelection) return;
    setActivePriceTypes((prev) => {
      if (!prev.has('regular')) return prev;
      const next = new Set(prev);
      next.delete('regular');
      if (!next.has('promo')) next.add('promo');
      return next;
    });
  }, [isBundlesOnlySelection]);

  const prevSticksRouteRef = React.useRef(false);
  useEffect(() => {
    if (isStickRoute && !prevSticksRouteRef.current) {
      setPriceDisplayMode('per_unit');
    }
    prevSticksRouteRef.current = isStickRoute;
  }, [isStickRoute]);

  // Starter kit only when the user set it in Product Detail (product_flags API). No category or group heuristics.
  const getIsKit = useCallback((title) => {
    if (!title) return false;
    return !!productFlagsFromStorage[title]?.isStarterKitOrRegistration;
  }, [productFlagsFromStorage]);

  /** Same promo value as Price & Promotions: promo-offer flag → actual; else discounted actual when actual≠listed; else promo_price. */
  const getPromoPriceRawValue = useCallback((item, title) => {
    if (!item || !title) return null;
    if (item.__avgPromoRaw != null && Number.isFinite(Number(item.__avgPromoRaw))) {
      return Number(item.__avgPromoRaw);
    }
    if (getIsKit(title)) return null;
    const parseNum = (v) => {
      if (v == null || v === '') return NaN;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : NaN;
    };
    if (productFlagsFromStorage[title]?.isPromoOffer) {
      const p = item.actual_price ?? item.price ?? item.current_price;
      const n = parseNum(p);
      return Number.isFinite(n) ? n : null;
    }
    const actual = parseNum(item.actual_price ?? item.price ?? item.current_price);
    const regular = parseNum(item.regular_price ?? item.original_price);
    if (Number.isFinite(actual) && Number.isFinite(regular) && actual > 0 && regular > 0 && actual !== regular) {
      return actual;
    }
    const pp = parseNum(item.promo_price);
    return Number.isFinite(pp) ? pp : null;
  }, [productFlagsFromStorage, getIsKit]);

  const weekOptions = useMemo(() => {
    const byKey = new Map();
    availableDates.forEach((d) => {
      const meta = getIsoWeekMeta(d);
      if (!meta) return;
      if (!byKey.has(meta.weekKey)) byKey.set(meta.weekKey, meta);
    });
    return Array.from(byKey.values()).sort((a, b) => a.weekStart.valueOf() - b.weekStart.valueOf());
  }, [availableDates]);

  const monthOptions = useMemo(() => {
    const byKey = new Map();
    availableDates.forEach((d) => {
      if (!d?.isValid?.()) return;
      const key = d.format('YYYY-MM');
      if (!byKey.has(key)) {
        byKey.set(key, { key, label: d.format('MMMM YYYY') });
      }
    });
    return Array.from(byKey.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [availableDates]);

  const availableDateKeys = useMemo(
    () => new Set(availableDates.map((d) => d.format('YYYY-MM-DD'))),
    [availableDates]
  );

  const isSelectedDateUnavailable = useMemo(() => {
    if (!selectedDate?.isValid?.()) return false;
    // This warning is for explicit picked dates (daily mode).
    if (dateSelectionMode !== DATE_MODE_DAILY) return false;
    return !availableDateKeys.has(selectedDate.format('YYYY-MM-DD'));
  }, [selectedDate, dateSelectionMode, availableDateKeys]);

  useEffect(() => {
    if (!isInitialBrandSelectionResolved) return;
    applyFilters();
  }, [isInitialBrandSelectionResolved, data, selectedCountry, selectedRetailers, selectedBrands, selectedCategoryGroups, selectedPackSizes, selectedDate, searchTerm, routeCategoryGroup, groupBy, activePriceTypes, starterKitProductFilter, limitedEditionFilter, productFlagsFromStorage, getItemPackSize, matchingGroups, getIsKit, getPromoPriceRawValue, dateSelectionMode, dateAggregationMode, selectedWeekKey, weekOptions]);

  // If route sets a product type (devices/sticks), default category filter to it until user changes it.
  const lastProductTypeRef = React.useRef(productType);
  useEffect(() => {
    if (!routeCategoryGroup) return;
    const productTypeChanged = lastProductTypeRef.current !== productType;
    if (productTypeChanged || selectedCategoryGroups.length === 0) {
      setSelectedCategoryGroups([routeCategoryGroup]);
    }
    lastProductTypeRef.current = productType;
  }, [routeCategoryGroup, productType, selectedCategoryGroups.length]);

  useEffect(() => {
    if (!data.length) return;
    const effectiveCategoryGroups =
      selectedCategoryGroups.length > 0
        ? selectedCategoryGroups
        : routeCategoryGroup
          ? [routeCategoryGroup]
          : [];
    let rows = data;
    if (effectiveCategoryGroups.length > 0) {
      const normalizedGroups = new Set(effectiveCategoryGroups.map((value) => normalizeValue(value)));
      rows = data.filter((item) => {
        const itemGroup = normalizeValue(item.category_group || item.clean_category);
        return normalizedGroups.has(itemGroup);
      });
    }
    const brands = [...new Set(rows.map((item) => item.brand).filter(Boolean))]
      .filter((b) => !isExcludedBrand(b))
      .sort();
    setFilterOptions((prev) => ({ ...prev, brands }));
  }, [data, selectedCategoryGroups, routeCategoryGroup]);

  useEffect(() => {
    const allowed = new Set(filterOptions?.brands || []);
    if (!allowed.size) return;
    setSelectedBrands((prev) => {
      const next = prev.filter((b) => allowed.has(b));
      return next.length === prev.length ? prev : next;
    });
  }, [filterOptions.brands]);

  useEffect(() => {
    // Prefer projectKey from route when it maps to a country (e.g. /cz/... -> Czech)
    const fromProject = getDefaultCountryForProject(projectKey, availableCountries);
    const selectedCountryIsValid =
      !selectedCountry || availableCountries.length === 0
        ? true
        : availableCountries.includes(selectedCountry);

    // If user has already chosen a country and it's still valid, don't override it with route defaults.
    if (countryTouched && selectedCountryIsValid) {
      return;
    }

    if (fromProject && fromProject !== selectedCountry) {
      setSelectedCountry(fromProject);
      return;
    }
    if (isMarketScope) {
      const nextCountry = resolveMarketCountry(selectedMarket, availableCountries);
      if (nextCountry && nextCountry !== selectedCountry) {
        setSelectedCountry(nextCountry);
      }
    } else {
      // Project scope: selectedMarket = projectKey (e.g. 'cz')
      const nextCountry = getDefaultCountryForProject(selectedMarket, availableCountries);
      if (nextCountry && nextCountry !== selectedCountry) {
        setSelectedCountry(nextCountry);
      }
    }
  }, [projectKey, isMarketScope, selectedMarket, availableCountries, selectedCountry, countryTouched]);

  // When the route context changes, allow default country derivation again.
  useEffect(() => {
    setCountryTouched(false);
  }, [projectKey, selectedMarket, scopeMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dataResponse, overridesResponse, brandOverridesResponse, packSizeResponse, flagsResponse, matchingResponse] = await Promise.all([
        axios.get(`${API_URL}/jti/global-merged-data`),
        axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/product-flags`).catch(() => ({ data: { flags: {} } })),
        axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
      ]);
      if (dataResponse.data && dataResponse.data.data) {
        const { data: rawData, columns } = dataResponse.data;
        const formattedData = rawData.map((row) => {
          const obj = {};
          columns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });
        const overrides = overridesResponse?.data?.overrides || {};
        const brandOverrides = brandOverridesResponse?.data?.overrides || {};
        setPackSizeOverridesFromStorage(packSizeResponse?.data?.overrides || {});
        setProductFlagsFromStorage(flagsResponse?.data?.flags || {});
        setMatchingGroups(buildMatchingGroupsMapFromTables(matchingResponse?.data?.tables || {}));
        const formattedWithOverrides = formattedData
          .map((item) => mergeJtiProductRowOverrides(item, overrides, brandOverrides))
          .filter((item) => !isExcludedBrand(item?.brand));
        setData(formattedWithOverrides);
        extractFilterOptions(formattedWithOverrides);

        const dates = formattedWithOverrides
          .map((item) => item.updated_at || item.date)
          .filter(Boolean)
          .map((value) => dayjs(value))
          .filter((value) => value.isValid());
        if (dates.length > 0) {
          const uniqueDates = [...new Set(dates.map((d) => d.format('YYYY-MM-DD')))].sort().map((s) => dayjs(s));
          setAvailableDates(uniqueDates);
          const maxDate = uniqueDates[uniqueDates.length - 1];
          setSelectedDate(maxDate);
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load brand board summary data');
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = (rows) => {
    const countries = [...new Set(rows.map((item) => item.country).filter(Boolean))];
    const brands = [...new Set(rows.map((item) => item.brand).filter(Boolean))];
    const categoryGroups = [...new Set(rows.map((item) => item.category_group || item.clean_category).filter(Boolean))];

    setAvailableCountries(countries);
    setFilterOptions({ retailers: [], brands });
    setAvailableCategoryGroups(categoryGroups);
  };

  const applyFilters = () => {
    let filtered = [...data];
    const effectiveCategoryGroups = selectedCategoryGroups.length > 0
      ? selectedCategoryGroups
      : (routeCategoryGroup ? [routeCategoryGroup] : []);
    if (effectiveCategoryGroups.length > 0) {
      const normalizedGroups = new Set(effectiveCategoryGroups.map((value) => normalizeValue(value)));
      filtered = filtered.filter((item) => {
        const itemGroup = normalizeValue(item.category_group || item.clean_category);
        return normalizedGroups.has(itemGroup);
      });
    }
    // When HQ view groups by countries all countries are shown as columns — don't pre-filter
    if (selectedCountry && groupBy !== 'countries') {
      filtered = filtered.filter((item) => countryMatches(item.country, selectedCountry));
    }
    if (selectedRetailers.length > 0) {
      filtered = filtered.filter((item) => selectedRetailers.includes(item.retailer));
    }
    const afterCore = filtered;

    const runFilteredPipeline = (input, withBrandFilter) => {
      let f = input;
      if (withBrandFilter && selectedBrands.length > 0) {
        f = f.filter((item) => selectedBrands.includes(item.brand));
      }
      if (selectedPackSizes.length > 0) {
        f = brandBoardFilterRowsByPackSelection(f, selectedPackSizes, getItemPackSize);
      }
      // Date handling:
      // - daily or specific day in weekly/monthly: as-of selected day (fill-forward)
      // - average in weekly/monthly: average prices over all rows inside selected period
      if (selectedDate && selectedDate.isValid?.()) {
        const toValidDate = (item) => {
          const rawDate = item.updated_at || item.date;
          if (!rawDate) return null;
          const itemDate = dayjs(rawDate);
          return itemDate.isValid() ? itemDate : null;
        };
        const useAverage =
          dateAggregationMode === DATE_AGGREGATION_AVERAGE &&
          (dateSelectionMode === DATE_MODE_WEEKLY || dateSelectionMode === DATE_MODE_MONTHLY);

        if (useAverage) {
          let periodStart = null;
          let periodEnd = null;
          if (dateSelectionMode === DATE_MODE_WEEKLY) {
            const selectedWeek = weekOptions.find((w) => w.weekKey === selectedWeekKey);
            if (selectedWeek?.weekStart?.isValid?.()) {
              periodStart = selectedWeek.weekStart.startOf('day');
              periodEnd = selectedWeek.weekStart.add(6, 'day').endOf('day');
            }
          } else {
            periodStart = selectedDate.startOf('month');
            periodEnd = selectedDate.endOf('month');
          }

          if (periodStart && periodEnd) {
            const AVG_FIELDS = ['actual_price', 'regular_price', 'price', 'current_price', 'original_price', 'promo_price'];
            // Keep product universe stable (as-of period end), only average prices where period rows exist.
            const universeRows = f.filter((item) => {
              const itemDate = toValidDate(item);
              return itemDate && !itemDate.isAfter(periodEnd, 'day');
            });
            const groupedForAverage = new Map();
            universeRows.forEach((item) => {
              const itemDate = toValidDate(item);
              if (!itemDate) return;
              const key = `${item.country || ''}__${item.retailer || ''}__${item.title || ''}`;
              if (!groupedForAverage.has(key)) {
                groupedForAverage.set(key, { latest: item, latestDate: itemDate, periodRows: [] });
                return;
              }
              const entry = groupedForAverage.get(key);
              if (itemDate.isAfter(entry.latestDate)) {
                entry.latest = item;
                entry.latestDate = itemDate;
              }
            });
            universeRows.forEach((item) => {
              const itemDate = toValidDate(item);
              if (!itemDate) return;
              if (itemDate.isBefore(periodStart, 'day') || itemDate.isAfter(periodEnd, 'day')) return;
              const key = `${item.country || ''}__${item.retailer || ''}__${item.title || ''}`;
              const entry = groupedForAverage.get(key);
              if (entry) entry.periodRows.push(item);
            });

            f = Array.from(groupedForAverage.values()).map(({ latest, periodRows }) => {
              const rowsForAverage = periodRows.length > 0 ? periodRows : [latest];
              const averaged = { ...latest };
              const title = latest._originalTitle || latest.title;
              const promoValues = rowsForAverage
                .map((r) => getPromoPriceRawValue(r, r._originalTitle || r.title || title))
                .filter((v) => Number.isFinite(v) && v > 0);
              if (promoValues.length > 0) {
                averaged.__avgPromoRaw = promoValues.reduce((sum, v) => sum + v, 0) / promoValues.length;
              }
              averaged.__avgHasActual = rowsForAverage.some((r) => {
                const n = parseFloat(r.actual_price);
                return Number.isFinite(n);
              });
              averaged.__avgHasRegular = rowsForAverage.some((r) => {
                const n = parseFloat(r.regular_price);
                return Number.isFinite(n);
              });
              AVG_FIELDS.forEach((field) => {
                const values = rowsForAverage
                  .map((r) => parseFloat(r[field]))
                  .filter((v) => Number.isFinite(v));
                if (values.length > 0) {
                  averaged[field] = (values.reduce((sum, v) => sum + v, 0) / values.length).toFixed(4);
                }
              });
              return averaged;
            });
          } else {
            f = [];
          }
        } else {
          f = f.filter((item) => {
            const itemDate = toValidDate(item);
            if (!itemDate) return false;
            // Brand board should reflect SKUs available on the specific selected day,
            // not an as-of snapshot that can keep outdated variants.
            return itemDate.isSame(selectedDate, 'day');
          });
        }
      }
      // Price types: a row must match at least one *selected* series (not "any price data").
      // Otherwise with only "Promo Price" checked, every SKU with listed/actual would still appear.
      // Promo-offer SKUs (product_flags) only appear when "Promo Price" is selected.
      if (!isStickRoute) {
        f = f.filter((item) =>
          rowMatchesStarterKitProductFilter(item, starterKitProductFilter, productFlagsFromStorage)
        );
      }
      f = f.filter((item) =>
        rowMatchesLimitedEditionFilter(item, limitedEditionFilter, productFlagsFromStorage)
      );
      if (activePriceTypes?.size > 0) {
        f = f.filter((item) => {
          const title = item._originalTitle || item.title;
          const isKit = getIsKit(title);
          const isPromoOfferFlag = !!(title && productFlagsFromStorage[title]?.isPromoOffer && !isKit);
          if (isPromoOfferFlag && !activePriceTypes.has('promo')) return false;

          const hasActual = item.__avgHasActual || (item.actual_price != null && item.actual_price !== '' && !isNaN(parseFloat(item.actual_price)));
          const hasRegular = item.__avgHasRegular || (item.regular_price != null && item.regular_price !== '' && !isNaN(parseFloat(item.regular_price)));
          const promoVal = getPromoPriceRawValue(item, title);
          const hasPromo = promoVal != null && promoVal > 0;

          if (isKit) {
            const p = item.actual_price ?? item.regular_price ?? item.price ?? item.current_price;
            return p != null && p !== '' && !isNaN(parseFloat(p));
          }

          let matches = false;
          if (activePriceTypes.has('actual')) matches = matches || hasActual;
          if (activePriceTypes.has('regular')) matches = matches || hasRegular;
          if (activePriceTypes.has('promo')) matches = matches || hasPromo;
          return matches;
        });
      }
      if (searchTerm) {
        f = f.filter((item) =>
          (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.brand && item.brand.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      return f;
    };

    const filteredForBoard = runFilteredPipeline(afterCore, true);
    const filteredBeforeBrand = runFilteredPipeline(afterCore, false);

    // Apply matching group grouping: show one representative per group if prices match
    const groupedFiltered = groupProductsByMatching(filteredForBoard, matchingGroups);

    setFilteredDataRaw(filteredForBoard); // store raw (pre-grouping) for brand board layout
    setFilteredDataRawBeforeBrand(filteredBeforeBrand);
    setFilteredData(groupedFiltered);
  };

  const getDisplayPriceForItem = useCallback((item) => {
    const title = item._originalTitle || item.title;
    const isKit = getIsKit(title);
    if (!activePriceTypes?.size) {
      return parseFloat(item.actual_price || item.regular_price || item.price || 0);
    }
    if (isKit) {
      const p = item.actual_price ?? item.regular_price ?? item.price;
      return p != null && p !== '' ? parseFloat(p) : parseFloat(item.actual_price || item.regular_price || item.price || 0);
    }
    if (activePriceTypes.has('actual')) {
      const p = item.actual_price;
      if (p != null && p !== '' && !isNaN(parseFloat(p))) return parseFloat(p);
    }
    if (activePriceTypes.has('regular')) {
      const p = item.regular_price;
      if (p != null && p !== '' && !isNaN(parseFloat(p))) return parseFloat(p);
    }
    if (activePriceTypes.has('promo')) {
      const raw = getPromoPriceRawValue(item, title);
      if (raw != null && Number.isFinite(raw)) return raw;
    }
    return parseFloat(item.actual_price || item.regular_price || item.price || 0);
  }, [activePriceTypes, productFlagsFromStorage, getIsKit, getPromoPriceRawValue]);

  /**
   * Build brand board products — aligned with Price and Promotions country chart on a date:
   * same matching groups, promo / user-flagged starter kit / normal split, price buckets per country,
   * and `_groupedVariantTitles` matching the chart line’s SKU list (tooltip / image hover).
   */
  const buildBrandBoardProducts = (products, options = {}) => {
    const skipMinimalCollapse = options.skipMinimalCollapse === true;
    const PRICE_BUCKET_DECIMALS = 2;
    const fmtBucketKey = (v) => {
      if (v == null || !Number.isFinite(v)) return '';
      const maxD = Math.max(0, PRICE_BUCKET_DECIMALS);
      const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
      return parseFloat(rounded.toFixed(maxD)).toString();
    };

    const isStarterKitProduct = (title) =>
      !!title && !!productFlagsFromStorage[title]?.isStarterKitOrRegistration;

    const isPromoOfferTitle = (title) =>
      !!(title && productFlagsFromStorage[title]?.isPromoOffer && !isStarterKitProduct(title));

    const getComparablePrice = (item) => {
      const rawPrice = getDisplayPriceForItem(item);
      let amount = parseFloat(rawPrice != null ? rawPrice : (item.actual_price || item.regular_price));
      if (Number.isNaN(amount)) amount = 0;
      if (priceDisplayMode === 'per_unit') {
        const ps = getItemPackSize(item);
        if (ps && ps > 1) amount = amount / ps;
      }
      return convertToEur(amount, item.currency);
    };

    const rowTime = (p) => {
      const raw = p.updated_at || p.date;
      if (!raw) return 0;
      const t = new Date(raw).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    /** Pack part for dedup keys — same title + different pack must stay distinct rows. */
    const packDedupPart = (p) => {
      const ps = getItemPackSize(p);
      return ps != null && ps > 0 ? String(Math.round(ps)) : 'u';
    };
    const slotKeyPackPart = (sk) => (String(sk).split('\x1f')[2] || 'u').trim();
    const latestByKey = new Map();
    products.forEach((product) => {
      const key = `${product.country || ''}-${product.retailer || ''}-${product.title || ''}-${packDedupPart(product)}`;
      const existing = latestByKey.get(key);
      if (!existing) { latestByKey.set(key, product); return; }
      if (rowTime(product) > rowTime(existing)) latestByKey.set(key, product);
    });
    const latestProducts = Array.from(latestByKey.values());

    /** Distinct pack / comparable-price lines so e.g. 1-pack vs 10-pack are not averaged together. */
    const makeSlotKey = (p) => {
      const c = p.country || '';
      const t = p.title || '';
      const ps = getItemPackSize(p);
      const psPart = ps != null && ps > 0 ? String(Math.round(ps)) : 'u';
      const price = getComparablePrice(p);
      const pb = fmtBucketKey(price) || 'na';
      return `${c}\x1f${t}\x1f${psPart}\x1f${pb}`;
    };

    const pickRepresentative = (list) => {
      if (!list.length) return null;
      const sorted = [...list].sort((a, b) => {
        const aImg = !!getProductImageUrl(a);
        const bImg = !!getProductImageUrl(b);
        if (aImg && !bImg) return -1;
        if (!aImg && bImg) return 1;
        return (a.title || '').localeCompare(b.title || '');
      });
      return sorted[0];
    };

    const keysByLogical = new Map();
    const bySlotKey = new Map();
    latestProducts.forEach((p) => {
      const c = p.country || '';
      const t = p.title || '';
      if (!t) return;
      const sk = makeSlotKey(p);
      const logical = `${c}\x1f${t}`;
      if (!keysByLogical.has(logical)) keysByLogical.set(logical, []);
      const slotList = keysByLogical.get(logical);
      if (!slotList.includes(sk)) slotList.push(sk);

      if (!bySlotKey.has(sk)) bySlotKey.set(sk, { rows: [] });
      const agg = bySlotKey.get(sk);
      agg.rows.push(p);
    });
    const slotDataByKey = new Map();
    bySlotKey.forEach((agg, sk) => {
      const rep = pickRepresentative(agg.rows);
      const repPrice = rep ? getComparablePrice(rep) : NaN;
      slotDataByKey.set(sk, {
        rows: agg.rows,
        repPrice: Number.isFinite(repPrice) && repPrice > 0 ? repPrice : null,
      });
    });

    const result = [];

    /** Drop rows with no resolvable SKU (bad data / generic "Product" label with no variants). */
    const filterIdentifiableRows = (rows) =>
      rows.filter((row) => {
        const variants = (row._groupedVariantTitles || []).filter((t) => t && String(t).trim());
        if (variants.length) return true;
        if ((row._originalTitle || '').trim()) return true;
        const t = (row.title || '').trim();
        if (t && t !== 'Product') return true;
        return false;
      });

    const emitCluster = (country, groupName, clusterSlotKeys, primaryType) => {
      if (!clusterSlotKeys.length) return;
      const rowsForTitles = [];
      clusterSlotKeys.forEach((sk) => {
        const slot = slotDataByKey.get(sk);
        if (slot?.rows) rowsForTitles.push(...slot.rows);
      });
      const seen = new Set();
      const uniqueRows = [];
      rowsForTitles.forEach((r) => {
        const rk = `${r.country}-${r.retailer}-${r.title}-${packDedupPart(r)}`;
        if (seen.has(rk)) return;
        seen.add(rk);
        uniqueRows.push(r);
      });
      if (!uniqueRows.length) {
        latestProducts.forEach((p) => {
          if ((p.country || '') !== country || !clusterSlotKeys.includes(makeSlotKey(p))) return;
          const rk = `${p.country}-${p.retailer}-${p.title}-${packDedupPart(p)}`;
          if (seen.has(rk)) return;
          seen.add(rk);
          uniqueRows.push(p);
        });
      }
      if (!uniqueRows.length) return;
      const rep = pickRepresentative(uniqueRows);
      const sortedVariantTitles = [
        ...new Set(uniqueRows.map((r) => (r.title || '').trim()).filter(Boolean)),
      ].sort((a, b) => a.localeCompare(b));
      const psPart = (clusterSlotKeys[0].split('\x1f')[2] || 'u').trim();
      const repPrice = rep ? getComparablePrice(rep) : null;
      const priceBucketKey = repPrice != null ? fmtBucketKey(repPrice) : '';
      result.push({
        ...rep,
        title: groupName,
        _originalTitle: rep.title,
        _isGroupRepresentative: true,
        _groupSkus: sortedVariantTitles,
        _groupedVariantTitles: sortedVariantTitles,
        _groupPrimaryType: primaryType,
        _boardSlotPackPart: psPart,
        _boardPriceBucketKey: priceBucketKey,
      });
    };

    if (!matchingGroups || matchingGroups.size === 0) {
      return filterIdentifiableRows(
        latestProducts.map((p) => {
          const sk = makeSlotKey(p);
          const psPart = (sk.split('\x1f')[2] || 'u').trim();
          return {
            ...p,
            _originalTitle: p.title,
            _groupedVariantTitles: p.title ? [p.title] : [],
            _boardSlotPackPart: psPart,
          };
        })
      );
    }

    const groupMap = new Map();
    const ungrouped = [];
    latestProducts.forEach((product) => {
      const infos = matchingInfosForSku(matchingGroups, product.title);
      if (!infos.length) {
        ungrouped.push(product);
        return;
      }
      infos.forEach((groupInfo) => {
        const groupKey = groupInfo.groupName;
        if (!groupKey) return;
        if (!groupMap.has(groupKey)) groupMap.set(groupKey, new Set());
        groupMap.get(groupKey).add(product.title);
      });
    });

    groupMap.forEach((titlesSet, groupName) => {
      const countries = new Set();
      latestProducts.forEach((p) => {
        if (titlesSet.has(p.title)) countries.add(p.country || '');
      });

      const clusterByPrice = (country, titleList, primaryType) => {
        if (!titleList.length) return;
        const clusterSlotKeys = [];
        titleList.forEach((t) => {
          const logical = `${country}\x1f${t}`;
          (keysByLogical.get(logical) || []).forEach((sk) => {
            if (!clusterSlotKeys.includes(sk)) clusterSlotKeys.push(sk);
          });
        });
        if (!clusterSlotKeys.length) return;
        const buckets = new Map();
        clusterSlotKeys.forEach((sk) => {
          const slot = slotDataByKey.get(sk);
          if (!slot || !slot.rows?.length) return;
          const v = slot.repPrice;
          if (v == null || !Number.isFinite(v) || v <= 0) return;
          const bk = fmtBucketKey(v);
          if (!bk) return;
          if (!buckets.has(bk)) buckets.set(bk, []);
          buckets.get(bk).push(sk);
        });
        if (buckets.size === 0 && clusterSlotKeys.length) {
          const byPackFallback = new Map();
          clusterSlotKeys.forEach((sk) => {
            const ps = slotKeyPackPart(sk);
            if (!byPackFallback.has(ps)) byPackFallback.set(ps, []);
            byPackFallback.get(ps).push(sk);
          });
          byPackFallback.forEach((skList) => emitCluster(country, groupName, skList, primaryType));
          return;
        }
        buckets.forEach((sks) => {
          const byPack = new Map();
          sks.forEach((sk) => {
            const ps = slotKeyPackPart(sk);
            if (!byPack.has(ps)) byPack.set(ps, []);
            byPack.get(ps).push(sk);
          });
          byPack.forEach((skList) => emitCluster(country, groupName, skList, primaryType));
        });
      };

      countries.forEach((country) => {
        const titlesHere = [...titlesSet].filter((t) =>
          latestProducts.some((p) => (p.country || '') === country && p.title === t)
        );
        const promoKeys = titlesHere.filter((t) => isPromoOfferTitle(t));
        const starterKitKeys = titlesHere.filter((t) => isStarterKitProduct(t));
        const normalKeys = titlesHere.filter((t) => !isPromoOfferTitle(t) && !isStarterKitProduct(t));
        clusterByPrice(country, promoKeys, 'promo');
        clusterByPrice(country, starterKitKeys, 'starter_kit');
        clusterByPrice(country, normalKeys, 'actual');
      });
    });

    ungrouped.forEach((product) => {
      const sk = makeSlotKey(product);
      const psPart = (sk.split('\x1f')[2] || 'u').trim();
      result.push({
        ...product,
        _originalTitle: product.title,
        _isGroupRepresentative: false,
        _groupedVariantTitles: product.title ? [product.title] : [],
        _boardSlotPackPart: psPart,
      });
    });

    if (
      !skipMinimalCollapse &&
      selectedPackSizes.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL) &&
      result.length
    ) {
      const packRank = (p) => {
        const raw = p._boardSlotPackPart;
        if (raw == null || raw === '' || raw === 'u') return Infinity;
        const n = Number(raw);
        return Number.isFinite(n) && n >= 1 ? n : Infinity;
      };
      const minimalTileKey = (p) => {
        const pricePart = p._boardPriceBucketKey || fmtBucketKey(getComparablePrice(p)) || 'na';
        return `${p.country || ''}\x1f${(p.title || '').trim()}\x1f${p._groupPrimaryType || 'actual'}\x1f${(p.brand || '').trim()}\x1f${pricePart}`;
      };
      const byKey = new Map();
      result.forEach((p) => {
        const k = minimalTileKey(p);
        if (!byKey.has(k)) byKey.set(k, []);
        byKey.get(k).push(p);
      });
      const collapsed = [];
      byKey.forEach((list) => {
        let minP = Infinity;
        list.forEach((p) => {
          const r = packRank(p);
          if (r < minP) minP = r;
        });
        if (!Number.isFinite(minP) || minP === Infinity) {
          collapsed.push(list[0]);
          return;
        }
        const atMin = list.filter((p) => packRank(p) === minP);
        const sorted = [...atMin].sort((a, b) => {
          const pa = getComparablePrice(a);
          const pb = getComparablePrice(b);
          if (Number.isFinite(pa) && Number.isFinite(pb) && pa !== pb) return pa - pb;
          return (a._originalTitle || '').localeCompare(b._originalTitle || '');
        });
        collapsed.push(sorted[0]);
      });
      return filterIdentifiableRows(collapsed);
    }

    return filterIdentifiableRows(result);
  };

  const createBrandBoardLayout = (products) => {
    const { lookupKey: baseLookupKey, packFilter: basePackFilter } = resolveBrandBoardBaseProduct(
      isStickRoute,
      effectiveBaseProduct,
      baseProductSkuOverride
    );
    const rowMatchesBasePack = (row) => {
      if (basePackFilter == null) return true;
      return getItemPackSize(row) === basePackFilter;
    };
    const groupedProducts = buildBrandBoardProducts(products);
    
    const productsWithConvertedPrices = groupedProducts.map((product) => {
      const rawPrice = getDisplayPriceForItem(product);
      const src = normalizeCurrencyCode(product.currency);
      let amountLocal = parseFloat(rawPrice != null ? rawPrice : (product.actual_price || product.regular_price));
      if (Number.isNaN(amountLocal)) amountLocal = 0;
      if (priceDisplayMode === 'per_unit') {
        const ps = getItemPackSize(product);
        if (ps && ps > 1) amountLocal = amountLocal / ps;
      }
      const priceEur = convertToEur(amountLocal, src);
      let displayAmount = amountLocal;
      let displayCur = src;
      if (effectiveDisplayCurrency === 'EUR') {
        displayAmount = priceEur;
        displayCur = 'EUR';
      } else if (effectiveDisplayCurrency !== 'local') {
        displayAmount = convertToCurrency(amountLocal, src, effectiveDisplayCurrency);
        displayCur = effectiveDisplayCurrency;
      }

      const titleForPromo = product._originalTitle || product.title;
      const promoRaw = getPromoPriceRawValue(product, titleForPromo);
      let promoDisplayPrice = null;
      let promoDisplayCurrency = null;
      if (promoRaw != null && Number.isFinite(promoRaw) && promoRaw > 0) {
        let promoLocal = promoRaw;
        if (priceDisplayMode === 'per_unit') {
          const ps = getItemPackSize(product);
          if (ps && ps > 1) promoLocal = promoLocal / ps;
        }
        const promoEur = convertToEur(promoLocal, src);
        let pAmt = promoLocal;
        let pCur = src;
        if (effectiveDisplayCurrency === 'EUR') {
          pAmt = promoEur;
          pCur = 'EUR';
        } else if (effectiveDisplayCurrency !== 'local') {
          pAmt = convertToCurrency(promoLocal, src, effectiveDisplayCurrency);
          pCur = effectiveDisplayCurrency;
        }
        promoDisplayPrice = pAmt.toFixed(2);
        promoDisplayCurrency = pCur;
      }

      let listedDisplayPrice = null;
      let listedDisplayCurrency = null;
      const listedRawParse = product.regular_price ?? product.original_price;
      const listedRaw =
        listedRawParse != null && listedRawParse !== ''
          ? parseFloat(listedRawParse)
          : NaN;
      if (
        activePriceTypes.has('regular') &&
        !getIsKit(titleForPromo) &&
        Number.isFinite(listedRaw) &&
        listedRaw > 0
      ) {
        let listedLocal = listedRaw;
        if (priceDisplayMode === 'per_unit') {
          const ps = getItemPackSize(product);
          if (ps && ps > 1) listedLocal = listedLocal / ps;
        }
        const listedEur = convertToEur(listedLocal, src);
        let lAmt = listedLocal;
        let lCur = src;
        if (effectiveDisplayCurrency === 'EUR') {
          lAmt = listedEur;
          lCur = 'EUR';
        } else if (effectiveDisplayCurrency !== 'local') {
          lAmt = convertToCurrency(listedLocal, src, effectiveDisplayCurrency);
          lCur = effectiveDisplayCurrency;
        }
        listedDisplayPrice = lAmt.toFixed(2);
        listedDisplayCurrency = lCur;
      }
      const listedBesidePromoLayout =
        activePriceTypes.has('promo') &&
        activePriceTypes.has('regular') &&
        !getIsKit(titleForPromo) &&
        listedDisplayPrice != null &&
        promoDisplayPrice != null &&
        boardPricesDiffer(listedDisplayPrice, promoDisplayPrice);

      let starterKitDisplayPrice = null;
      let starterKitDisplayCurrency = null;
      const isKitRow =
        getIsKit(titleForPromo) ||
        product._groupPrimaryType === 'starter_kit';
      if (isKitRow && !isStickRoute) {
        const kitParse = product.actual_price ?? product.regular_price ?? product.price ?? '';
        const kitLocalRaw = parseFloat(kitParse);
        if (Number.isFinite(kitLocalRaw) && kitLocalRaw > 0) {
          let kLocal = kitLocalRaw;
          if (priceDisplayMode === 'per_unit') {
            const ps = getItemPackSize(product);
            if (ps && ps > 1) kLocal = kLocal / ps;
          }
          const kitEurConv = convertToEur(kLocal, src);
          let kAmt = kLocal;
          let kCur = src;
          if (effectiveDisplayCurrency === 'EUR') {
            kAmt = kitEurConv;
            kCur = 'EUR';
          } else if (effectiveDisplayCurrency !== 'local') {
            kAmt = convertToCurrency(kLocal, src, effectiveDisplayCurrency);
            kCur = effectiveDisplayCurrency;
          }
          starterKitDisplayPrice = kAmt.toFixed(2);
          starterKitDisplayCurrency = kCur;
        }
      }

      return {
        ...product,
        convertedPrice: priceEur,
        displayPrice: displayAmount.toFixed(2),
        displayCurrency: displayCur,
        promoDisplayPrice,
        promoDisplayCurrency,
        listedDisplayPrice,
        listedDisplayCurrency,
        listedBesidePromoLayout,
        starterKitDisplayPrice,
        starterKitDisplayCurrency,
      };
    });

    if (productsWithConvertedPrices.length === 0) {
      return { priceGroups: [], allBrands: [], allCountries: [] };
    }

    const buildBaseTitleSet = () => {
      if (baseProductSkuOverride?.trim()) {
        return new Set([baseProductSkuOverride.trim()]);
      }
      if (!baseLookupKey) return new Set();
      const s = new Set(groupNameToProducts.get(baseLookupKey) || []);
      s.add(baseLookupKey);
      return s;
    };

    const skuMatchesLookup = (sku, lookup) => {
      const s = (sku || '').trim();
      const l = (lookup || '').trim();
      if (!s || !l) return false;
      return s === l || normalizeValue(s) === normalizeValue(l);
    };

    const pickExplicitBaseBoardTile = (tiles) => {
      if (!tiles?.length) return null;
      if (baseProductSkuOverride?.trim()) {
        const want = baseProductSkuOverride.trim();
        return (
          tiles.find((t) => skuMatchesLookup(t._originalTitle, want)) ||
          tiles.find((t) => skuMatchesLookup(t.title, want)) ||
          null
        );
      }
      const autoSku = preferredAutoBaseSku?.trim();
      if (autoSku) {
        const bySku = tiles.find((t) => skuMatchesLookup(t._originalTitle, autoSku));
        if (bySku) return bySku;
      }
      if (!baseLookupKey) return tiles[0];
      const keyNorm = normalizeValue(baseLookupKey);
      const skuExact = tiles.find((t) => skuMatchesLookup(t._originalTitle, baseLookupKey));
      if (skuExact) return skuExact;
      const groupMatches = tiles.filter((t) => {
        const groupLabel = (t.title || '').trim();
        return groupLabel === baseLookupKey || normalizeValue(groupLabel) === keyNorm;
      });
      if (groupMatches.length) {
        const nonLe = groupMatches.filter(
          (t) => !isLimitedEditionSkuTitle(t._originalTitle || t.title, productFlagsFromStorage)
        );
        const pool = nonLe.length ? nonLe : groupMatches;
        return pool[0];
      }
      return tiles[0];
    };

    const baselineEurFromBoardTile = (tile) => {
      if (!tile) return null;
      const rawPrice = getDisplayPriceForItem(tile);
      const src = normalizeCurrencyCode(tile.currency);
      let amountLocal = parseFloat(
        rawPrice != null ? rawPrice : (tile.actual_price || tile.regular_price)
      );
      if (Number.isNaN(amountLocal)) amountLocal = 0;
      if (priceDisplayMode === 'per_unit') {
        const ps = getItemPackSize(tile);
        if (ps && ps > 1) amountLocal = amountLocal / ps;
      }
      const priceEur = convertToEur(amountLocal, src);
      return Number.isFinite(priceEur) && priceEur > 0 ? priceEur : null;
    };

    const referenceFromRawRows = (rawRows) => {
      if (!baseLookupKey || !rawRows?.length) return null;
      const bt = buildBaseTitleSet();
      const slice = rawRows.filter((p) => bt.has(p.title || '') && rowMatchesBasePack(p));
      if (!slice.length) return null;
      const grouped = buildBrandBoardProducts(slice, { skipMinimalCollapse: true });
      return baselineEurFromBoardTile(pickExplicitBaseBoardTile(grouped));
    };

    let referencePrice = productsWithConvertedPrices[0]?.convertedPrice || 1;
    if (baseLookupKey) {
      const baseTitles = buildBaseTitleSet();
      const baseRows = productsWithConvertedPrices.filter((p) => {
        const sku = (p._originalTitle || p.title || '').trim();
        const lineTitle = (p.title || '').trim();
        if (!baseTitles.has(sku) && !baseTitles.has(lineTitle)) return false;
        if (basePackFilter == null) return true;
        return getItemPackSize(p) === basePackFilter;
      });
      if (baseRows.length > 0) {
        const explicitTile = pickExplicitBaseBoardTile(baseRows);
        referencePrice = explicitTile?.convertedPrice ?? baseRows[0].convertedPrice;
      } else {
        const fallback = productsWithConvertedPrices.find(
          (p) =>
            ((p.title || '') === baseLookupKey || (p._originalTitle || '') === baseLookupKey) &&
            rowMatchesBasePack(p)
        );
        if (fallback?.convertedPrice != null) referencePrice = fallback.convertedPrice;
        else {
          const ext = referenceFromRawRows(filteredDataRawBeforeBrand);
          if (ext != null) referencePrice = ext;
        }
      }
    }

    const safeLegacyRef =
      Number.isFinite(referencePrice) && referencePrice > 0 ? referencePrice : 1;

    const baseTitlesForMatch = baseLookupKey ? buildBaseTitleSet() : null;

    const canonicalBaseSkuByCountry = new Map();
    const canonicalBaseBucketByCountry = new Map();
    if (baseLookupKey && filteredDataRawBeforeBrand?.length) {
      const baseTitlesForRef = buildBaseTitleSet();
      const countriesWithBase = new Set(
        filteredDataRawBeforeBrand
          .filter((p) => baseTitlesForRef.has(p.title || '') && rowMatchesBasePack(p))
          .map((p) => p.country || '')
      );
      countriesWithBase.forEach((country) => {
        const rows = filteredDataRawBeforeBrand.filter(
          (p) =>
            baseTitlesForRef.has(p.title || '') &&
            (p.country || '') === country &&
            rowMatchesBasePack(p)
        );
        const tiles = buildBrandBoardProducts(rows, { skipMinimalCollapse: true });
        const baseTile = pickExplicitBaseBoardTile(tiles);
        if (!baseTile) return;
        const sku = (baseTile._originalTitle || '').trim();
        if (sku) canonicalBaseSkuByCountry.set(country, sku);
        if (baseTile._boardPriceBucketKey) {
          canonicalBaseBucketByCountry.set(country, baseTile._boardPriceBucketKey);
        }
      });
    }

    const rowIsBaseProduct = (product) => {
      if (!baseLookupKey) return false;
      if (!rowMatchesBasePack(product)) return false;
      const sku = (product._originalTitle || '').trim();
      const lineTitle = (product.title || '').trim();

      if (baseProductSkuOverride?.trim()) {
        return (
          skuMatchesLookup(sku, baseProductSkuOverride) ||
          skuMatchesLookup(lineTitle, baseProductSkuOverride)
        );
      }

      if (baseTitlesForMatch && !baseTitlesForMatch.has(sku) && !baseTitlesForMatch.has(lineTitle)) {
        return false;
      }
      const country = product.country || '';
      const canonicalSku = canonicalBaseSkuByCountry.get(country);
      if (canonicalSku && sku !== canonicalSku) return false;
      const canonicalBucket = canonicalBaseBucketByCountry.get(country);
      if (
        canonicalBucket &&
        product._boardPriceBucketKey &&
        product._boardPriceBucketKey !== canonicalBucket
      ) {
        return false;
      }
      return true;
    };

    const applyBaseProductHundredIndex = (product, indexed) => {
      if (!rowIsBaseProduct(product)) return indexed;
      const country = product.country || '';
      const canonicalBucket = canonicalBaseBucketByCountry.get(country);
      if (
        canonicalBucket &&
        product._boardPriceBucketKey &&
        product._boardPriceBucketKey !== canonicalBucket
      ) {
        return indexed;
      }
      return {
        ...indexed,
        priceIndex: 100,
        priceIndexRaw: 100,
        priceIndexListed: indexed.priceIndexListed != null ? 100 : indexed.priceIndexListed,
        priceIndexPromo: indexed.priceIndexPromo != null ? 100 : indexed.priceIndexPromo,
        priceIndexKit: indexed.priceIndexKit != null ? 100 : indexed.priceIndexKit,
        promoCanvasListedFallbackPct:
          indexed.promoCanvasListedFallbackPct != null ? 100 : indexed.promoCanvasListedFallbackPct,
        starterKitCanvasListedFallbackPct:
          indexed.starterKitCanvasListedFallbackPct != null
            ? 100
            : indexed.starterKitCanvasListedFallbackPct,
      };
    };

    const itemLocalToEur = (item, localAmount) => {
      if (!Number.isFinite(localAmount) || localAmount <= 0) return null;
      let amt = localAmount;
      if (priceDisplayMode === 'per_unit') {
        const ps = getItemPackSize(item);
        if (ps && ps > 1) amt /= ps;
      }
      const eur = convertToEur(amt, normalizeCurrencyCode(item.currency));
      return Number.isFinite(eur) && eur > 0 ? eur : null;
    };

    const refBaselinesByCountry = new Map();
    if (baseLookupKey && filteredDataRawBeforeBrand?.length) {
      const baseTitlesForRef = buildBaseTitleSet();
      const countriesWithBase = new Set(
        filteredDataRawBeforeBrand
          .filter((p) => baseTitlesForRef.has(p.title || '') && rowMatchesBasePack(p))
          .map((p) => p.country || '')
      );
      countriesWithBase.forEach((country) => {
        const rows = filteredDataRawBeforeBrand.filter(
          (p) =>
            baseTitlesForRef.has(p.title || '') &&
            (p.country || '') === country &&
            rowMatchesBasePack(p)
        );
        const tiles = buildBrandBoardProducts(rows, { skipMinimalCollapse: true });
        const baseTile = pickExplicitBaseBoardTile(tiles);
        if (!baseTile) return;
        const t = baseTile._originalTitle || baseTile.title || '';
        const kit =
          getIsKit(t) || !!productFlagsFromStorage[t]?.isStarterKitOrRegistration;
        const listedParse = baseTile.regular_price ?? baseTile.original_price;
        const listedLocal = parseFloat(listedParse);
        const listedEur =
          Number.isFinite(listedLocal) && listedLocal > 0
            ? itemLocalToEur(baseTile, listedLocal)
            : null;
        const promoV = getPromoPriceRawValue(baseTile, t);
        const promoEur =
          promoV != null && Number.isFinite(promoV) && promoV > 0
            ? itemLocalToEur(baseTile, promoV)
            : null;
        let kitEur = null;
        if (kit) {
          const kp = parseFloat(baseTile.actual_price ?? baseTile.regular_price ?? baseTile.price ?? '');
          if (Number.isFinite(kp) && kp > 0) kitEur = itemLocalToEur(baseTile, kp);
        }
        refBaselinesByCountry.set(country, {
          listed: listedEur,
          promo: promoEur,
          kit: kitEur,
        });
      });
    }

    const inferRowKind = (product) => {
      if (product._groupPrimaryType === 'starter_kit') return 'starter_kit';
      if (product._groupPrimaryType === 'promo') return 'promo';
      if (product._groupPrimaryType === 'actual') return 'listed';
      const t = product._originalTitle || product.title || '';
      const kit =
        getIsKit(t) || !!productFlagsFromStorage[t]?.isStarterKitOrRegistration;
      if (kit) return 'starter_kit';
      if (productFlagsFromStorage[t]?.isPromoOffer) return 'promo';
      return 'listed';
    };

    const pct = (numerEur, denomEur) => {
      if (
        numerEur == null ||
        denomEur == null ||
        !Number.isFinite(numerEur) ||
        !Number.isFinite(denomEur) ||
        numerEur <= 0 ||
        denomEur <= 0
      ) {
        return null;
      }
      return Math.round((numerEur / denomEur) * 100);
    };

    /** Unrounded counterpart of `pct` — used for grouping so similar-but-different prices stay apart. */
    const pctRaw = (numerEur, denomEur) => {
      if (
        numerEur == null ||
        denomEur == null ||
        !Number.isFinite(numerEur) ||
        !Number.isFinite(denomEur) ||
        numerEur <= 0 ||
        denomEur <= 0
      ) {
        return null;
      }
      return (numerEur / denomEur) * 100;
    };

    const productsWithPriceIndex = productsWithConvertedPrices.map((product) => {
      const legacyIndex = Math.round((product.convertedPrice / safeLegacyRef) * 100);
      const legacyIndexRaw = (product.convertedPrice / safeLegacyRef) * 100;
      if (!baseLookupKey) {
        const kitRawVal = parseFloat(product.actual_price ?? product.regular_price ?? product.price ?? '');
        const kitEurLegacy = itemLocalToEur(product, kitRawVal);
        return applyBaseProductHundredIndex(product, {
          ...product,
          priceIndex: legacyIndex,
          priceIndexRaw: legacyIndexRaw,
          priceIndexKit: null,
          starterKitCanvasListedFallbackPct: null,
          promoCanvasListedFallbackPct: null,
        });
      }

      const country = product.country || '';
      const refSlice = refBaselinesByCountry.get(country);
      const denomListed =
        refSlice?.listed != null ? refSlice.listed : safeLegacyRef;
      let denomPromo = safeLegacyRef;
      if (refSlice) {
        if (refSlice.promo != null) {
          denomPromo = refSlice.promo;
        } else if (refSlice.listed != null) {
          denomPromo = refSlice.listed;
        }
      }
      /** Base-product starter-kit average only — no fallback (canvas shows ?% if null). */
      const denomKitStrict = refSlice?.kit ?? null;
      const denomKit = denomKitStrict != null ? denomKitStrict : safeLegacyRef;

      const titleForPromo = product._originalTitle || product.title;
      const listedRaw = parseFloat(product.regular_price ?? product.original_price ?? '');
      const listedEur = itemLocalToEur(product, listedRaw);
      const promoRawVal = getPromoPriceRawValue(product, titleForPromo);
      const promoEur =
        promoRawVal != null && Number.isFinite(promoRawVal)
          ? itemLocalToEur(product, promoRawVal)
          : null;
      const kitRaw = parseFloat(product.actual_price ?? product.regular_price ?? product.price ?? '');
      const kitEur = itemLocalToEur(product, kitRaw);
      const listedBaselineStrict = refSlice?.listed ?? null;
      /** Base reference has no averaged promo in this country — canvas promo % uses ? with listed footnote. */
      const refPromoStrict = refSlice?.promo ?? null;
      const starterKitCanvasPctVsBaseKit =
        inferRowKind(product) === 'starter_kit' && kitEur != null && denomKitStrict != null
          ? pct(kitEur, denomKitStrict)
          : null;
      const starterKitCanvasListedFallbackPct =
        inferRowKind(product) === 'starter_kit' &&
        kitEur != null &&
        denomKitStrict == null &&
        listedBaselineStrict != null
          ? pct(kitEur, listedBaselineStrict)
          : null;

      const dualListedPromoIdx =
        product.listedBesidePromoLayout &&
        activePriceTypes.has('promo') &&
        activePriceTypes.has('regular') &&
        listedEur != null &&
        promoEur != null;

      const selectedDenom = (() => {
        if (compareToPriceType === BASE_COMPARE_TYPE_LISTED) return denomListed;
        if (compareToPriceType === BASE_COMPARE_TYPE_PROMO) return denomPromo;
        if (compareToPriceType === BASE_COMPARE_TYPE_STARTER_KIT) return denomKit;
        return null;
      })();

      if (dualListedPromoIdx) {
        const listedDenom = compareToPriceType === BASE_COMPARE_TYPE_AUTO ? denomListed : selectedDenom;
        const promoDenom = compareToPriceType === BASE_COMPARE_TYPE_AUTO ? denomPromo : selectedDenom;
        const priceIndexListed = pct(listedEur, listedDenom);
        const priceIndexPromo =
          promoEur != null && refPromoStrict != null ? pct(promoEur, promoDenom) : null;
        const promoCanvasListedFallbackPct =
          promoEur != null && refPromoStrict == null && listedBaselineStrict != null
            ? pct(promoEur, listedBaselineStrict)
            : null;
        const priceIndex =
          priceIndexPromo ?? promoCanvasListedFallbackPct ?? priceIndexListed ?? legacyIndex;
        const priceIndexRaw =
          (promoEur != null && refPromoStrict != null ? pctRaw(promoEur, promoDenom) : null) ??
          (promoEur != null && refPromoStrict == null && listedBaselineStrict != null
            ? pctRaw(promoEur, listedBaselineStrict)
            : null) ??
          pctRaw(listedEur, listedDenom) ??
          legacyIndexRaw;
        return applyBaseProductHundredIndex(product, {
          ...product,
          priceIndex,
          priceIndexRaw,
          priceIndexListed,
          priceIndexPromo,
          promoCanvasListedFallbackPct,
          priceIndexKit: starterKitCanvasPctVsBaseKit,
          starterKitCanvasListedFallbackPct,
        });
      }

      const kind = inferRowKind(product);
      let numerEur = listedEur;
      let denomEur = denomListed;
      if (compareToPriceType === BASE_COMPARE_TYPE_AUTO && kind === 'promo') {
        numerEur = promoEur;
        denomEur = denomPromo;
      } else if (compareToPriceType === BASE_COMPARE_TYPE_AUTO && kind === 'starter_kit') {
        numerEur = kitEur;
        denomEur = denomKit;
      } else if (compareToPriceType === BASE_COMPARE_TYPE_PROMO) {
        denomEur = denomPromo;
        if (kind === 'promo') numerEur = promoEur;
        else if (kind === 'starter_kit') numerEur = kitEur;
      } else if (compareToPriceType === BASE_COMPARE_TYPE_STARTER_KIT) {
        denomEur = denomKit;
        if (kind === 'promo') numerEur = promoEur;
        else if (kind === 'starter_kit') numerEur = kitEur;
      } else if (compareToPriceType === BASE_COMPARE_TYPE_LISTED) {
        denomEur = denomListed;
        if (kind === 'promo') numerEur = promoEur;
        else if (kind === 'starter_kit') numerEur = kitEur;
      }
      const priceIndex = pct(numerEur, denomEur) ?? legacyIndex;
      const priceIndexRaw = pctRaw(numerEur, denomEur) ?? legacyIndexRaw;
      return applyBaseProductHundredIndex(product, {
        ...product,
        priceIndex,
        priceIndexRaw,
        priceIndexKit: starterKitCanvasPctVsBaseKit,
        starterKitCanvasListedFallbackPct,
        promoCanvasListedFallbackPct: null,
      });
    });

    // Group products strictly by their actual (unrounded) price level. Items at the exact same
    // level are shown together in one cell regardless of pack size; products whose prices are
    // merely similar (e.g. 142 vs 145) keep different levels and are NOT merged.
    const priceGroups = [];
    productsWithPriceIndex.sort((a, b) => b.priceIndex - a.priceIndex);
    const LEVEL_EPSILON = 1e-9;
    const levelKeyOf = (product) =>
      product.priceIndexRaw != null && Number.isFinite(product.priceIndexRaw)
        ? product.priceIndexRaw
        : product.priceIndex;
    productsWithPriceIndex.forEach((product) => {
      const levelKey = levelKeyOf(product);
      let added = false;
      for (const group of priceGroups) {
        if (Math.abs(group.levelKey - levelKey) <= LEVEL_EPSILON) {
          group.products.push(product);
          added = true;
          break;
        }
      }
      if (!added) {
        priceGroups.push({ priceIndex: product.priceIndex, levelKey, products: [product] });
      }
    });

    const allBrands = [...new Set(productsWithPriceIndex.map((p) => p.brand))].filter(Boolean);
    const allCountries = [...new Set(productsWithPriceIndex.map((p) => p.country).filter(Boolean))].sort();

    return { priceGroups, allBrands, allCountries };
  };

  /** Same as Price & Promotions chart product click → ProductDetailPopup. */
  const handleProductClick = useCallback(
    (product) => {
      const productImage = getProductImageUrl(product);
      const sourceRows = data?.length ? data : ((filteredDataRaw && filteredDataRaw.length > 0) ? filteredDataRaw : []);
      const names = (() => {
        if (product._groupedVariantTitles?.length) {
          return product._groupedVariantTitles.filter((t) => t && String(t).trim());
        }
        if (product._groupSkus?.length) {
          return product._groupSkus.filter((t) => t && String(t).trim());
        }
        const ot = product._originalTitle?.trim();
        if (ot) return [ot];
        const disp = product.title?.trim();
        if (disp && disp !== 'Product') return [disp];
        return [];
      })();
      const country = product.country || selectedCountry || '';
      const dateLabel = selectedDate?.isValid?.() ? selectedDate.format('YYYY-MM-DD') : undefined;
      const rows = names
        .map((name) => pickLatestRowOnOrBeforeDate(filterRowsForProductPopup(sourceRows, name, country), dateLabel))
        .filter(Boolean);
      let primary = rows[0] || null;
      if (!primary) {
        const sku = names[0] || product._originalTitle?.trim?.() || product._groupedVariantTitles?.[0];
        if (sku) {
          primary = pickLatestRowOnOrBeforeDate(filterRowsForProductPopup(sourceRows, sku, country), dateLabel) || {
            ...product,
            title: sku,
          };
        } else if (product.actual_price != null || product.regular_price != null || product.displayPrice != null) {
          primary = {
            ...product,
            title: product._originalTitle || product.title || '',
          };
        }
      }
      const primaryTitle =
        names[0] ||
        primary?.title ||
        product._originalTitle?.trim() ||
        (product.title?.trim() !== 'Product' ? product.title?.trim() : '') ||
        '';
      const productDataRows = rows.length ? rows : primary ? [primary] : [];
      const pinnedStockRaw = primary?.stock_status || primary?.availability || '';
      const pinnedStock = String(pinnedStockRaw || '').trim() || undefined;
      const pinnedPopupPricing = {
        ...(product.displayCurrency ? { currency: product.displayCurrency } : {}),
        ...(product.displayPrice != null ? { actual_price: String(product.displayPrice) } : {}),
        ...(product.listedDisplayPrice != null ? { regular_price: String(product.listedDisplayPrice) } : {}),
        ...(pinnedStock ? { stock_status: pinnedStock } : {}),
      };
      const mergedPrimary =
        primary
          ? {
              ...primary,
              ...(pinnedPopupPricing.currency ? { currency: pinnedPopupPricing.currency } : {}),
              ...(pinnedPopupPricing.actual_price != null ? { actual_price: pinnedPopupPricing.actual_price } : {}),
              ...(pinnedPopupPricing.regular_price != null ? { regular_price: pinnedPopupPricing.regular_price } : {}),
              ...(pinnedPopupPricing.stock_status ? { stock_status: pinnedPopupPricing.stock_status } : {}),
            }
          : null;
      setProductPopupData({
        productName: primaryTitle,
        productImage,
        productData: mergedPrimary,
        productDataRows,
        groupProductNames: names.length > 1 ? names : undefined,
        packSizeOverrides: packSizeOverridesFromStorage,
        productFlags: productFlagsFromStorage,
        pricingAsOfDate: dateLabel || productRowDateKey(mergedPrimary),
        pinnedChartPrices: !!(
          pinnedPopupPricing.actual_price != null ||
          pinnedPopupPricing.regular_price != null ||
          pinnedPopupPricing.stock_status
        ),
      });
      setProductPopupOpen(true);
    },
    [data, filteredDataRaw, selectedDate, selectedCountry, packSizeOverridesFromStorage, productFlagsFromStorage]
  );

  const getDisplayTitleForProduct = useCallback((product) => {
    const titleForLookup = product._originalTitle || product.title;
    const isKit =
      getIsKit(titleForLookup) || product._groupPrimaryType === 'starter_kit';
    const groupLabel = (product.title || '').trim();
    const skuLabel = (product._originalTitle || '').trim();
    let baseTitle = product.title || product._originalTitle || 'Product';
    if (groupLabel === 'Product' && skuLabel) baseTitle = skuLabel;
    else if (!groupLabel && skuLabel) baseTitle = skuLabel;

    const flagTitles = [
      titleForLookup,
      product._originalTitle,
      product.title,
      ...(product._groupedVariantTitles || []),
    ].filter((t) => t && String(t).trim());
    const isLimitedEdition = flagTitles.some(
      (t) => productFlagsFromStorage[t]?.portfolioType === 'Limited edition'
    );
    const withLimitedEdition = (label) => {
      if (!label || !isLimitedEdition) return label;
      if (/\(Limited edition\)/i.test(label)) return label;
      return `${label} (Limited edition)`;
    };

    /** Omit “(Starter Kit)” in the title — kit rows show a dedicated starter-kit price band below. */
    if (isKit) return withLimitedEdition(baseTitle);
    if (product.listedBesidePromoLayout) return withLimitedEdition(baseTitle);
    const isPromoOffer =
      (!!titleForLookup &&
        productFlagsFromStorage[titleForLookup]?.isPromoOffer &&
        !getIsKit(titleForLookup)) ||
      product._groupPrimaryType === 'promo';
    if (isPromoOffer) return withLimitedEdition(`${baseTitle} - Promo Price`);
    if (
      product._isGroupRepresentative &&
      product._groupedVariantTitles?.length > 1
    ) {
      return withLimitedEdition(`${baseTitle} - ${product._groupedVariantTitles.length} variants`);
    }
    return withLimitedEdition(baseTitle);
  }, [getIsKit, productFlagsFromStorage]);

  const getBoardProductHoverPreview = useCallback(
    (product) => {
      const sourceRows = filteredDataRaw?.length ? filteredDataRaw : data;
      const sku = (product._originalTitle || product.title || '').trim();
      const rowTime = (row) => {
        const raw = row?.updated_at || row?.date;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
      };
      let skuRow = product;
      if (sku && sourceRows?.length) {
        let matches = sourceRows.filter((r) => (r?.title || '').trim() === sku);
        if (product.country) {
          const inCountry = matches.filter(
            (r) => !r.country || r.country === product.country
          );
          if (inCountry.length) matches = inCountry;
        }
        if (matches.length) {
          skuRow = matches.reduce((best, r) => (rowTime(r) > rowTime(best) ? r : best), matches[0]);
        }
      }
      const image = getProductImageUrl(skuRow) || getProductImageUrl(product);
      let name = (product._originalTitle || product.title || 'Product').trim();
      if (
        productFlagsFromStorage[sku]?.portfolioType === 'Limited edition' &&
        !/\(Limited edition\)/i.test(name)
      ) {
        name = `${name} (Limited edition)`;
      }
      const variantTitles = product._groupedVariantTitles?.length
        ? product._groupedVariantTitles.filter((t) => t && String(t).trim())
        : sku
          ? [sku]
          : [];
      return { image, name, groupProductNames: variantTitles };
    },
    [data, filteredDataRaw, productFlagsFromStorage]
  );

  // Shared product card renderer used by both brand and country column modes
  const renderProductCard = (product) => {
    const paletteInfo = getProductPaletteInfo(product.brand, product.title);
    const swatchColor = paletteInfo?.color || getBrandPaletteColor(product.brand) || '#c4c4c4';
    const displayTitle = getDisplayTitleForProduct(product);
    const packUnitsBracket = boardPackUnitsBracketLabel(product);
    const dualListedPromo =
      activePriceTypes.has('promo') &&
      activePriceTypes.has('regular') &&
      product.listedBesidePromoLayout &&
      product.listedDisplayPrice != null &&
      product.listedDisplayCurrency != null &&
      product.promoDisplayPrice != null &&
      product.promoDisplayCurrency != null;
    const starterKitPriceFormatted =
      product.starterKitDisplayPrice != null && product.starterKitDisplayCurrency != null
        ? formatLocalPriceDisplay(product.starterKitDisplayPrice, product.starterKitDisplayCurrency)
        : null;
    const titleForTableKit = product._originalTitle || product.title || '';
    const isKitProductRow =
      getIsKit(titleForTableKit) || product._groupPrimaryType === 'starter_kit';
    const priceNumsDifferTable = (a, b, eps = 0.02) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (!Number.isFinite(na) || !Number.isFinite(nb)) return true;
      return Math.abs(na - nb) > eps;
    };
    const showTableStarterKitBandDual =
      !!starterKitPriceFormatted &&
      dualListedPromo &&
      product.listedDisplayPrice != null &&
      product.promoDisplayPrice != null &&
      priceNumsDifferTable(product.starterKitDisplayPrice, product.listedDisplayPrice) &&
      priceNumsDifferTable(product.starterKitDisplayPrice, product.promoDisplayPrice);
    const showTableStarterKitBandSolo =
      !dualListedPromo &&
      !!starterKitPriceFormatted &&
      isKitProductRow;
    const tableKitPillSx = {
      py: 0.25,
      px: 0.55,
      borderRadius: '6px',
      backgroundColor: '#1565c0',
      display: 'inline-flex',
      alignItems: 'center',
      maxWidth: '100%',
      boxSizing: 'border-box',
    };
    const tableKitPillTextSx = {
      color: '#fff',
      fontWeight: 700,
      fontSize: '0.72rem',
      lineHeight: 1.25,
      whiteSpace: 'nowrap',
    };
    const promoDup =
      product.promoDisplayPrice != null &&
      product.displayPrice != null &&
      product.promoDisplayPrice === product.displayPrice &&
      product.promoDisplayCurrency === product.displayCurrency;
    const priceBesideNameSx = {
      ml: 0.35,
      color: 'text.secondary',
      fontWeight: 500,
      fontSize: '0.6rem',
      whiteSpace: 'nowrap',
    };
    const showPromoBesideName =
      !dualListedPromo &&
      product.promoDisplayPrice != null &&
      product.promoDisplayCurrency != null &&
      !promoDup;
    const promoBesideName = showPromoBesideName ? (
      <Box component="span" sx={priceBesideNameSx}>
        · Promo Price: {formatLocalPriceDisplay(product.promoDisplayPrice, product.promoDisplayCurrency)}
      </Box>
    ) : null;
    const variantTitles = product._groupedVariantTitles?.length
      ? product._groupedVariantTitles
      : (product._originalTitle || product.title ? [product._originalTitle || product.title] : []);
    const firstLineTitle = dualListedPromo ? (
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {displayTitle}
          {packUnitsBracket ? (
            <Box
              component="span"
              sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.6rem', ml: 0.35, whiteSpace: 'nowrap' }}
            >
              {packUnitsBracket}
            </Box>
          ) : null}
        </Box>
      </Typography>
    ) : (
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        {displayTitle}
        {packUnitsBracket ? (
          <Box
            component="span"
            sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.6rem', ml: 0.35, whiteSpace: 'nowrap' }}
          >
            {packUnitsBracket}
          </Box>
        ) : null}
        {promoBesideName}
      </Typography>
    );
    const hoverPreview = getBoardProductHoverPreview(product);
    return (
      <BrandBoardProductHoverWrap
        key={product.id || `${product.title}-${product._originalTitle || ''}`}
        productImage={hoverPreview.image}
        productName={hoverPreview.name}
        groupProductNames={hoverPreview.groupProductNames}
      >
      <Box
        onClick={() => handleProductClick(product)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: swatchColor, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0
        }} />
        <Box sx={{
          width: 40, height: 40, borderRadius: 0.5, backgroundColor: '#fff',
          border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center',
          justifyContent: 'center', overflow: 'hidden'
        }}>
          {hoverPreview.image ? (
            <img src={hoverPreview.image} alt={product.title}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <ImageIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          {firstLineTitle}
          {dualListedPromo ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.25,
                rowGap: 0.35,
              }}
            >
              {(() => {
                const { promoLabel, listedLabel } = getListedPromoInlineParts(product);
                return (
                  <>
                    <Box
                      sx={{
                        py: 0.25,
                        px: 0.55,
                        borderRadius: '6px',
                        backgroundColor: '#d32f2f',
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          lineHeight: 1.25,
                          whiteSpace: 'normal',
                        }}
                      >
                        {promoLabel}
                      </Typography>
                    </Box>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        lineHeight: 1.25,
                        color: 'text.primary',
                      }}
                    >
                      {' | '}
                      {listedLabel}
                    </Typography>
                  </>
                );
              })()}
            </Box>
          ) : null}
          {dualListedPromo && product.promoCanvasListedFallbackPct != null && product.priceIndexPromo == null ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1.15, fontSize: '0.65rem', fontWeight: 500, mt: 0.2 }}
            >
              {`${product.promoCanvasListedFallbackPct}% of regular price`}
            </Typography>
          ) : null}
          {showTableStarterKitBandDual ? (
            <Box sx={{ mt: 0.3, width: '100%' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontSize: '0.6rem', mb: 0.2, fontWeight: 500, lineHeight: 1.2 }}
              >
                Starter Kit Price
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.5,
                  rowGap: 0.35,
                }}
              >
                <Box sx={tableKitPillSx}>
                  <Typography component="span" variant="caption" sx={tableKitPillTextSx}>
                    {starterKitPriceFormatted}
                    {' / '}
                    {product.priceIndexKit != null ? `${product.priceIndexKit}%` : '?%'}
                  </Typography>
                </Box>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.25, color: 'text.primary' }}
                >
                  - Starter kit
                </Typography>
              </Box>
              {product.starterKitCanvasListedFallbackPct != null ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 500, mt: 0.2, lineHeight: 1.15 }}
                >
                  {`${product.starterKitCanvasListedFallbackPct}% of regular price`}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {!dualListedPromo && !showTableStarterKitBandSolo ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
            {formatLocalPriceDisplay(product.displayPrice, product.displayCurrency)}
            {product.priceIndexListed != null &&
            (product.priceIndexPromo != null || product.promoCanvasListedFallbackPct != null) ? (
              <>
                {' / '}
                Listed {product.priceIndexListed}% · Promo{' '}
                {product.priceIndexPromo != null ? `${product.priceIndexPromo}%` : '?%'}
                {product.indexPromoBaselineFallback ? ' · promo vs listed baseline' : ''}
              </>
            ) : product.priceIndex != null ? (
              <>
                {' / '}
                {product.priceIndex}%
                {product.indexPromoBaselineFallback ? ' (vs listed baseline)' : ''}
              </>
            ) : null}
          </Typography>
          ) : null}
          {showTableStarterKitBandSolo ? (
            <Box sx={{ mt: 0.25, width: '100%' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, rowGap: 0.35 }}>
                <Box sx={tableKitPillSx}>
                  <Typography component="span" variant="caption" sx={tableKitPillTextSx}>
                    {starterKitPriceFormatted}
                    {' / '}
                    {product.priceIndexKit != null ? `${product.priceIndexKit}%` : '?%'}
                  </Typography>
                </Box>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.25, color: 'text.primary' }}
                >
                  - Starter kit
                </Typography>
              </Box>
              {product.starterKitCanvasListedFallbackPct != null ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.65rem', fontWeight: 500, mt: 0.2, lineHeight: 1.15 }}
                >
                  {`${product.starterKitCanvasListedFallbackPct}% of regular price`}
                </Typography>
              ) : null}
            </Box>
          ) : null}
          {(groupBy === 'countries' || groupBy === 'manufacturers') && (
            <Typography variant="caption" sx={{ display: 'block', color: getBrandPaletteColor(product.brand) || '#888', fontWeight: 600 }}>
              {(product.brand || '').toUpperCase()}
            </Typography>
          )}
        </Box>
      </Box>
      </BrandBoardProductHoverWrap>
    );
  };

  const renderCanvasProductCard = (product) => {
    const paletteInfo = getProductPaletteInfo(product.brand, product.title);
    const swatchColor = paletteInfo?.color || getBrandPaletteColor(product.brand) || '#c4c4c4';
    const displayTitle = getDisplayTitleForProduct(product);
    const packUnitsBracket = boardPackUnitsBracketLabel(product);
    const dualListedPromo =
      activePriceTypes.has('promo') &&
      activePriceTypes.has('regular') &&
      product.listedBesidePromoLayout &&
      product.listedDisplayPrice != null &&
      product.listedDisplayCurrency != null &&
      product.promoDisplayPrice != null &&
      product.promoDisplayCurrency != null;
    const variantTitles = product._groupedVariantTitles?.length
      ? product._groupedVariantTitles
      : (product._originalTitle || product.title ? [product._originalTitle || product.title] : []);
    const hoverPreview = getBoardProductHoverPreview(product);
    const img = hoverPreview.image || getProductImageUrl(product);
    const pricePrimary = dualListedPromo
      ? formatLocalPriceDisplay(product.promoDisplayPrice, product.promoDisplayCurrency)
      : formatLocalPriceDisplay(product.displayPrice, product.displayCurrency);
    const starterKitPriceFormatted =
      product.starterKitDisplayPrice != null && product.starterKitDisplayCurrency != null
        ? formatLocalPriceDisplay(product.starterKitDisplayPrice, product.starterKitDisplayCurrency)
        : null;
    const titleForCanvasKit = product._originalTitle || product.title || '';
    const isKitProductRow =
      getIsKit(titleForCanvasKit) || product._groupPrimaryType === 'starter_kit';
    const priceNumsDiffer = (a, b, eps = 0.02) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (!Number.isFinite(na) || !Number.isFinite(nb)) return true;
      return Math.abs(na - nb) > eps;
    };
    const showCanvasStarterKitBandDual =
      !!starterKitPriceFormatted &&
      dualListedPromo &&
      product.listedDisplayPrice != null &&
      product.promoDisplayPrice != null &&
      priceNumsDiffer(product.starterKitDisplayPrice, product.listedDisplayPrice) &&
      priceNumsDiffer(product.starterKitDisplayPrice, product.promoDisplayPrice);

    /** Kit-only canvas card: show price in the blue starter-kit band (not a duplicate black line). */
    const showCanvasStarterKitBandSolo =
      !dualListedPromo &&
      !!starterKitPriceFormatted &&
      isKitProductRow;

    const priceLabelCaptionSx = {
      textAlign: 'center',
      lineHeight: 1.1,
      fontSize: '0.55rem',
      fontWeight: 500,
      letterSpacing: 0.02,
      mt: 0.1,
    };
    /** Match product title weight; 1px smaller than title for long “CZK / %” lines; single line in narrow card. */
    const canvasCardMainPriceSx = {
      fontWeight: 700,
      fontSize: 'calc(0.68rem - 1px)',
      textAlign: 'center',
      lineHeight: 1.2,
      width: '100%',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
    /** Footnote under ?% kit/promo lines on canvas cards. */
    const canvasListedCompareFootnoteSx = {
      color: 'text.secondary',
      textAlign: 'center',
      fontSize: '0.58rem',
      lineHeight: 1.2,
      px: 0.25,
      fontWeight: 500,
      mt: 0.15,
      mb: 0.05,
      maxWidth: '100%',
    };
    const canvasPriceKindFooterLabel = (() => {
      if (groupBy === 'countries' || groupBy === 'manufacturers') return (product.brand || '').toUpperCase() || '';
      if (dualListedPromo) return '';
      const t = product._originalTitle || product.title || '';
      if (product._groupPrimaryType === 'starter_kit') return 'Starter kit';
      if (product._groupPrimaryType === 'promo') return 'Promo Price';
      if (product._groupPrimaryType === 'actual') return 'Regular Price';
      if (getIsKit(t) || productFlagsFromStorage[t]?.isStarterKitOrRegistration) return 'Starter kit';
      if (productFlagsFromStorage[t]?.isPromoOffer) return 'Promo Price';
      return 'Regular Price';
    })();

    return (
      <BrandBoardProductHoverWrap
        key={product.id || `canvas-${product.title}-${product._originalTitle || ''}`}
        productImage={hoverPreview.image}
        productName={hoverPreview.name}
        groupProductNames={hoverPreview.groupProductNames}
      >
        <Box
          onClick={() => handleProductClick(product)}
          sx={{
            width: 118,
            flexShrink: 0,
            p: 0.85,
            borderRadius: 1.25,
            backgroundColor: '#fff',
            border: '1px solid #e6e6e6',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.35,
            transition: 'box-shadow 0.15s ease',
            '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.09)' },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 76,
              borderRadius: 0.75,
              backgroundColor: '#fafafa',
              border: '1px solid #eee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {img ? (
              <img
                src={img}
                alt=""
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // Some CDNs (e.g. IQOS RO/PL) don't send CORS headers, so the anonymous
                  // request is blocked and the image never paints. Retry once without CORS
                  // so it still displays (export inlines images separately via a proxy).
                  const el = e.currentTarget;
                  if (el.hasAttribute('crossorigin')) {
                    const original = el.src;
                    el.removeAttribute('crossorigin');
                    el.src = '';
                    el.src = original;
                  }
                }}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <ImageIcon sx={{ fontSize: 28, color: '#bdbdbd' }} />
            )}
          </Box>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: swatchColor,
              border: '1px solid rgba(0,0,0,0.12)',
            }}
          />
          <Box sx={{ textAlign: 'center', width: '100%', minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: '0.68rem',
                textAlign: 'center',
                lineHeight: 1.25,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                width: '100%',
              }}
              title={[displayTitle, packUnitsBracket].filter(Boolean).join(' ')}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                {displayTitle}
              </Box>
              {packUnitsBracket ? (
                <Box
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: '0.58rem',
                    ml: 0.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {packUnitsBracket}
                </Box>
              ) : null}
            </Typography>
          </Box>
          {!showCanvasStarterKitBandSolo ? (
          <>
          {dualListedPromo ? (
            <>
              {(() => {
                const { promoLabel, listedLabel } = getListedPromoInlineParts(product);
                return (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.35,
                      rowGap: 0.35,
                    }}
                  >
                    <Box
                      sx={{
                        py: 0.45,
                        px: 0.35,
                        borderRadius: 0.75,
                        backgroundColor: '#d32f2f',
                        boxSizing: 'border-box',
                        display: 'inline-flex',
                        maxWidth: '100%',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          ...canvasCardMainPriceSx,
                          color: '#fff',
                          display: 'block',
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          textOverflow: 'clip',
                        }}
                      >
                        {promoLabel}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        ...canvasCardMainPriceSx,
                        color: 'text.primary',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'clip',
                      }}
                    >
                      {' | '}
                      {listedLabel}
                    </Typography>
                  </Box>
                );
              })()}
              {product.priceIndexPromo == null && product.promoCanvasListedFallbackPct != null ? (
                <Typography variant="caption" component="div" sx={canvasListedCompareFootnoteSx}>
                  {`${product.promoCanvasListedFallbackPct}% of regular price`}
                </Typography>
              ) : null}
              {showCanvasStarterKitBandDual ? (
                <>
                  <KeyboardArrowDown sx={{ fontSize: 16, color: 'action.disabled', my: -0.15 }} />
                  <Typography variant="caption" color="text.secondary" sx={priceLabelCaptionSx}>
                    Starter Kit Price
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      py: 0.45,
                      px: 0.35,
                      borderRadius: 0.75,
                      backgroundColor: '#1565c0',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        ...canvasCardMainPriceSx,
                        color: '#fff',
                        display: 'block',
                      }}
                    >
                      {starterKitPriceFormatted}
                      {' / '}
                      {product.priceIndexKit != null ? `${product.priceIndexKit}%` : '?%'}
                    </Typography>
                  </Box>
                  {product.starterKitCanvasListedFallbackPct != null ? (
                    <Typography variant="caption" component="div" sx={canvasListedCompareFootnoteSx}>
                      {`${product.starterKitCanvasListedFallbackPct}% of regular price`}
                    </Typography>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <>
          <Typography
            variant="caption"
            sx={canvasCardMainPriceSx}
          >
              <>
                {pricePrimary}
                {product.priceIndexListed != null &&
                (product.priceIndexPromo != null || product.promoCanvasListedFallbackPct != null) ? (
                  <>
                    {' / '}
                    Listed {product.priceIndexListed}% · Promo{' '}
                    {product.priceIndexPromo != null ? `${product.priceIndexPromo}%` : '?%'}
                    {product.indexPromoBaselineFallback ? (
                      <Box component="span" sx={{ fontWeight: 500, fontSize: '0.62rem', opacity: 0.88 }}>
                        {' '}
                        (promo vs listed baseline)
                      </Box>
                    ) : null}
                  </>
                ) : product.priceIndex != null ? (
                  <>
                    {' / '}
                    {product.priceIndex}%
                    {product.indexPromoBaselineFallback ? (
                      <Box component="span" sx={{ fontWeight: 500, fontSize: '0.62rem', opacity: 0.88 }}>
                        {' '}
                        (vs listed baseline)
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </>
          </Typography>
          {product.promoCanvasListedFallbackPct != null &&
          product.priceIndexPromo == null ? (
            <Typography variant="caption" component="div" sx={canvasListedCompareFootnoteSx}>
              {`${product.promoCanvasListedFallbackPct}% of regular price`}
            </Typography>
          ) : null}
            </>
          )}
          </>
          ) : null}
          {showCanvasStarterKitBandSolo ? (
            <>
              <Box
                sx={{
                  width: '100%',
                  py: 0.45,
                  px: 0.35,
                  borderRadius: 0.75,
                  backgroundColor: '#1565c0',
                  boxSizing: 'border-box',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    ...canvasCardMainPriceSx,
                    color: '#fff',
                    display: 'block',
                  }}
                >
                  {starterKitPriceFormatted}
                  {' / '}
                  {product.priceIndexKit != null ? `${product.priceIndexKit}%` : '?%'}
                </Typography>
              </Box>
              {product.starterKitCanvasListedFallbackPct != null ? (
                <Typography variant="caption" component="div" sx={canvasListedCompareFootnoteSx}>
                  {`${product.starterKitCanvasListedFallbackPct}% of regular price`}
                </Typography>
              ) : null}
            </>
          ) : null}
          {canvasPriceKindFooterLabel ? (
            <Typography
              variant="caption"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: '0.55rem',
                fontWeight: 500,
                lineHeight: 1.2,
                mt: 0.1,
              }}
            >
              {canvasPriceKindFooterLabel}
            </Typography>
          ) : null}
        </Box>
      </BrandBoardProductHoverWrap>
    );
  };

  const canvasExportLockRef = useRef(false);
  const exportCanvasBoardFilenameBase = useCallback(() => {
    const safeProject = (projectKey || 'board').toString().replace(/[^\w-]+/g, '_');
    return `brand-board-canvas_${safeProject}_${dayjs().format('YYYYMMDD-HHmmss')}`;
  }, [projectKey]);

  const captureCanvasBoardElement = useCallback(async () => {
    const el = canvasBoardExportRef.current;
    if (!el) return null;
    return withInlinedImagesForExport(el, () =>
      html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: -window.scrollY,
        imageTimeout: 25000,
      })
    );
  }, []);

  const handleExportCanvasPng = useCallback(async () => {
    if (canvasExportLockRef.current) return;
    canvasExportLockRef.current = true;
    setCanvasExportBusy(true);
    try {
      const canvas = await captureCanvasBoardElement();
      if (!canvas) return;
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${exportCanvasBoardFilenameBase()}.png`;
      a.click();
    } catch (err) {
      console.error('Canvas PNG export failed', err);
    } finally {
      canvasExportLockRef.current = false;
      setCanvasExportBusy(false);
    }
  }, [captureCanvasBoardElement, exportCanvasBoardFilenameBase]);

  const handleExportCanvasPdf = useCallback(async () => {
    if (canvasExportLockRef.current) return;
    canvasExportLockRef.current = true;
    setCanvasExportBusy(true);
    try {
      const canvas = await captureCanvasBoardElement();
      if (!canvas) return;
      const imgData = canvas.toDataURL('image/png');
      const isLandscape = canvas.width >= canvas.height;
      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4',
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 28;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      const imgW = canvas.width;
      const imgH = canvas.height;
      const ratio = Math.min(maxW / imgW, maxH / imgH);
      const w = imgW * ratio;
      const h = imgH * ratio;
      const x = margin + (maxW - w) / 2;
      const y = margin + (maxH - h) / 2;
      pdf.addImage(imgData, 'PNG', x, y, w, h);
      pdf.save(`${exportCanvasBoardFilenameBase()}.pdf`);
    } catch (err) {
      console.error('Canvas PDF export failed', err);
    } finally {
      canvasExportLockRef.current = false;
      setCanvasExportBusy(false);
    }
  }, [captureCanvasBoardElement, exportCanvasBoardFilenameBase]);

  const renderBrandBoardLayout = () => {
    const layout = createBrandBoardLayout(filteredDataRaw);
    const { priceGroups, allBrands, allCountries } = layout;

    if (!priceGroups.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            No products found for the current filters.
          </Typography>
          {showMarketAnnouncementsNotice && !showItalySticksNoticeAboveBoard && (
            <Box sx={{ mt: 1.5, mx: 'auto', width: '100%' }}>
              <MarketAnnouncementBannerList messages={marketAnnouncementMessages} />
            </Box>
          )}
        </Box>
      );
    }

    // --- Build column definitions based on groupBy ---
    let columns; // Array of { key, label, headerBg, headerText }

    if (groupBy === 'countries') {
      const countries = allCountries?.length ? allCountries : [...new Set(filteredData.map((p) => p.country).filter(Boolean))].sort();
      columns = countries.map((country) => ({
        key: country,
        label: country.toUpperCase(),
        headerBg: '#546e7a',
        headerText: '#ffffff',
      }));
    } else if (groupBy === 'manufacturers') {
      const manufacturersInData = [
        ...new Set(
          priceGroups
            .flatMap((g) => g.products.map((p) => getManufacturerForBrand(p.brand)))
            .filter(Boolean)
        ),
      ];
      const orderedManufacturers = MANUFACTURER_OPTIONS.filter((m) => manufacturersInData.includes(m));
      const extraManufacturers = manufacturersInData.filter((m) => !orderedManufacturers.includes(m));
      columns = [...orderedManufacturers, ...extraManufacturers].map((manufacturer) => {
        const headerBg = getManufacturerPaletteColor(manufacturer);
        return {
          key: manufacturer,
          label: manufacturer,
          headerBg,
          headerText: getContrastTextColor(headerBg),
        };
      });
    } else {
      const orderedBrands = ['IQOS', 'GLO', 'PLOOM', 'PULZE', 'LIL'];
      const displayBrands = [
        ...orderedBrands.filter((b) => allBrands.some((v) => getBrandKey(v) === b)),
        ...allBrands.filter((b) => !orderedBrands.includes(getBrandKey(b))),
      ];
      columns = displayBrands.map((brand) => {
        const brandKey = getBrandKey(brand);
        const header = BRAND_HEADER_CONFIG[brandKey] || {
          label: (brand || '').toLowerCase(),
          color: getBrandPaletteColor(brand) || '#7d7d7d',
        };
        return {
          key: brand,
          label: header.label,
          headerBg: header.color,
          headerText: getContrastTextColor(header.color),
        };
      });
    }

    const sortProductsForCanvasBucket = (products) =>
      [...products].sort((a, b) => {
        const packDiff =
          boardPackSortValue(a, getItemPackSize) - boardPackSortValue(b, getItemPackSize);
        if (packDiff !== 0) return packDiff;
        return getDisplayTitleForProduct(a).localeCompare(getDisplayTitleForProduct(b), undefined, {
          sensitivity: 'base',
        });
      });
    const partitionCanvasBandProducts = (products) => {
      const buckets = { IQOS: [], GLO: [], PLOOM: [], other: [] };
      products.forEach((p) => {
        const k = getBrandKey(p.brand);
        if (k === 'IQOS') buckets.IQOS.push(p);
        else if (k === 'GLO') buckets.GLO.push(p);
        else if (k === 'PLOOM') buckets.PLOOM.push(p);
        else buckets.other.push(p);
      });
      return buckets;
    };

    const countryCode = (selectedCountry || 'ALL').toUpperCase();
    const dateStr = selectedDate?.isValid?.() ? selectedDate.format('YYYY-MM-DD') : 'N/A';
    const boardTitle =
      (isStickRoute ? 'Sticks Brand Board' : 'Heated Tobacco Devices Brand Board') +
      ` - ${countryCode} - ${dateStr}`;

    // Size each canvas brand column to its busiest cell so product cards never wrap: a column
    // whose fullest level holds 4 products gets enough width for one row of 4, while a column
    // with no items at all (e.g. Glo) stays thin.
    const CANVAS_CARD_WIDTH = 118; // matches renderCanvasProductCard card width
    const CANVAS_CARD_GAP = 10; // matches band cell `gap: 1.25`
    const CANVAS_CELL_PAD_X = 20; // matches band cell `px: 1.25` (both sides)
    const CANVAS_EMPTY_COL_WIDTH = 60;
    const canvasColumnMaxCounts = CANVAS_BRAND_COLUMN_KEYS.reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});
    priceGroups.forEach((group) => {
      const buckets = partitionCanvasBandProducts(group.products);
      CANVAS_BRAND_COLUMN_KEYS.forEach((key) => {
        const n = buckets[key]?.length || 0;
        if (n > canvasColumnMaxCounts[key]) canvasColumnMaxCounts[key] = n;
      });
    });
    const canvasColumnTrack = (n) => {
      if (n <= 0) return `${CANVAS_EMPTY_COL_WIDTH}px`;
      const minPx = n * CANVAS_CARD_WIDTH + (n - 1) * CANVAS_CARD_GAP + CANVAS_CELL_PAD_X;
      return `minmax(${minPx}px, ${n}fr)`;
    };
    const canvasMainGridTemplate = `72px ${CANVAS_BRAND_COLUMN_KEYS.map((key) =>
      canvasColumnTrack(canvasColumnMaxCounts[key])
    ).join(' ')}`;

    if (brandBoardVisualStyle === BRAND_BOARD_VISUAL_CANVAS) {
      return (
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', border: '1px solid #d9d9d9', overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mb: 1.25,
            }}
          >
            {canvasExportBusy ? <CircularProgress size={22} sx={{ color: BRAND_BOARD_ACCENT }} /> : null}
            <Button
              size="small"
              variant="outlined"
              disabled={canvasExportBusy}
              onClick={handleExportCanvasPng}
            >
              Export PNG
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={canvasExportBusy}
              onClick={handleExportCanvasPdf}
            >
              Export PDF
            </Button>
          </Box>
          <Box ref={canvasBoardExportRef} sx={{ backgroundColor: '#f5f5f5', width: 'fit-content', minWidth: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25, letterSpacing: 0.5 }}>
              {boardTitle}
            </Typography>
            <Box
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
                width: 'fit-content',
                minWidth: '100%',
              }}
            >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: canvasMainGridTemplate,
                alignItems: 'stretch',
                borderBottom: CANVAS_GRID_LINE,
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  borderRight: CANVAS_GRID_LINE,
                  backgroundColor: '#fafafa',
                  boxSizing: 'border-box',
                }}
              />
              {CANVAS_BRAND_COLUMN_KEYS.map((brandKey, hi) => {
                const header = BRAND_HEADER_CONFIG[brandKey];
                const bg = header?.color || '#7d7d7d';
                return (
                  <Box
                    key={brandKey}
                    sx={{
                      minWidth: 0,
                      textAlign: 'center',
                      py: 0.65,
                      px: 1.25,
                      boxSizing: 'border-box',
                      backgroundColor: bg,
                      color: getContrastTextColor(bg),
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                      letterSpacing: 0.45,
                      borderLeft: hi > 0 ? CANVAS_GRID_LINE : 'none',
                    }}
                  >
                    {header?.label ?? brandKey}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ pt: 2.5, pb: 2.75, backgroundColor: '#fff' }}>
              {priceGroups.map((group, index) => {
                const bandBuckets = partitionCanvasBandProducts(group.products);
                const hasOther = bandBuckets.other.length > 0;
                return (
                  <Box
                    key={`canvas-${group.priceIndex}-${index}`}
                    sx={{
                      borderBottom: CANVAS_GRID_LINE,
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: canvasMainGridTemplate,
                        alignItems: 'stretch',
                        minHeight: hasOther ? 120 : 132,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.65,
                          borderRight: CANVAS_GRID_LINE,
                          backgroundColor: '#fafafa',
                          boxSizing: 'border-box',
                          minWidth: 0,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.2, fontSize: '0.65rem' }}>
                          Index
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, textAlign: 'center', fontSize: '0.9rem' }}>
                          {group.priceIndex}%
                        </Typography>
                      </Box>
                      {CANVAS_BRAND_COLUMN_KEYS.map((brandKey, ci) => (
                        <Box
                          key={`${group.priceIndex}-${brandKey}-${index}`}
                          sx={{
                            minWidth: 0,
                            borderLeft: ci > 0 ? CANVAS_GRID_LINE : 'none',
                            px: 1.25,
                            py: 1.5,
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.25,
                            alignItems: 'flex-start',
                            alignContent: 'center',
                            backgroundColor: ci % 2 === 0 ? CANVAS_COL_BG_SHADE : CANVAS_COL_BG_LIGHT,
                            backgroundImage: [
                              CANVAS_BAND_GRID_BG,
                              'linear-gradient(180deg, rgba(0,0,0,0.02) 0, transparent 22px)',
                            ].join(', '),
                          }}
                        >
                          {sortProductsForCanvasBucket(bandBuckets[brandKey]).map((product) =>
                            renderCanvasProductCard(product)
                          )}
                        </Box>
                      ))}
                    </Box>
                    {hasOther ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: canvasMainGridTemplate,
                          borderTop: '1px dashed #c5c5c5',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <Box
                          sx={{
                            borderRight: CANVAS_GRID_LINE,
                            backgroundColor: '#f5f5f5',
                            boxSizing: 'border-box',
                            minWidth: 0,
                          }}
                        />
                        <Box sx={{ gridColumn: '2 / -1', px: 1.25, py: 1.15, boxSizing: 'border-box', minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.68rem' }}>
                            Other brands
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                            {sortProductsForCanvasBucket(bandBuckets.other).map((product) =>
                              renderCanvasProductCard(product)
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper sx={{ p: 2, backgroundColor: '#f6f6f6', border: '1px solid #d9d9d9' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 1 }}>
          {boardTitle}
        </Typography>

        {/* Column headers */}
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box sx={{ width: 80 }} />
          {columns.map((col) => (
            <Box
              key={col.key}
              sx={{
                flex: 1, textAlign: 'center',
                backgroundColor: col.headerBg, color: col.headerText,
                fontWeight: 700, textTransform: 'uppercase',
                fontSize: '0.75rem', py: 0.5, borderRadius: 0.5, mx: 0.5
              }}
            >
              {col.label}
            </Box>
          ))}
        </Box>

        {/* Price group rows */}
        <Box sx={{ mt: 1 }}>
          {priceGroups.map((group, index) => (
            <Box
              key={`${group.priceIndex}-${index}`}
              sx={{ display: 'flex', borderBottom: '1px solid #d9d9d9', minHeight: 70, alignItems: 'stretch' }}
            >
              <Box sx={{
                width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 600, color: '#444'
              }}>
                {group.priceIndex}%
              </Box>

              {columns.map((col) => {
                const colProducts = (groupBy === 'countries'
                  ? group.products.filter((p) => countryMatches(p.country, col.key))
                  : groupBy === 'manufacturers'
                    ? group.products.filter((p) => getManufacturerForBrand(p.brand) === col.key)
                    : group.products.filter((p) => getBrandKey(p.brand) === col.key)
                ).sort(
                  (a, b) =>
                    boardPackSortValue(a, getItemPackSize) - boardPackSortValue(b, getItemPackSize)
                );

                return (
                  <Box
                    key={`${group.priceIndex}-${col.key}`}
                    sx={{
                      flex: 1, px: 1, py: 1,
                      display: 'flex', flexDirection: 'column', gap: 0.5,
                      backgroundColor: '#f9f9f9', borderLeft: '1px solid #ededed'
                    }}
                  >
                    {colProducts.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    ) : (
                      colProducts.map((product) => renderProductCard(product))
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const deduplicatedProducts = useMemo(
    () => buildBrandBoardProducts(filteredDataRaw),
    [
      filteredDataRaw,
      matchingGroups,
      getDisplayPriceForItem,
      productFlagsFromStorage,
      priceDisplayMode,
      getItemPackSize,
      selectedPackSizes,
    ]
  );

  const isPowerUser = useMemo(() => {
    const role = user?.role;
    return role === 'Power User' || role === 'Admin';
  }, [user]);
  const internalReportingComparisonEnabled = isPowerUser && SHOW_INTERNAL_REPORTING_COMPARISON_TESTING;

  const reportingStorageScope = useMemo(
    () => buildReportingStorageScopeBase(projectKey, productType),
    [projectKey, productType]
  );

  const reportingContextDateKey = useMemo(
    () => selectedDate?.format?.('YYYY-MM-DD') || '',
    [selectedDate]
  );

  const reportingContextCountry = useMemo(
    () => selectedCountry || '',
    [selectedCountry]
  );

  const reportingContextCategory = useMemo(() => {
    const effectiveCategories = selectedCategoryGroups.length > 0
      ? selectedCategoryGroups
      : (routeCategoryGroup ? [routeCategoryGroup] : []);
    return effectiveCategories.length > 0 ? effectiveCategories.join(', ') : 'All';
  }, [selectedCategoryGroups, routeCategoryGroup]);

  const reportingRecordSuffix = useMemo(() => {
    const datePart = reportingContextDateKey || 'none';
    const countryPart = reportingContextCountry || 'all';
    const categoryPart = reportingContextCategory || 'all';
    const safeCategoryPart = categoryPart.replace(/\s+/g, '_');
    return `${datePart}__${countryPart}__${safeCategoryPart}`;
  }, [reportingContextDateKey, reportingContextCountry, reportingContextCategory]);

  const reportingRecordKey = useMemo(
    () => `${reportingStorageScope}__${reportingRecordSuffix}`,
    [reportingStorageScope, reportingRecordSuffix]
  );

  const legacyReportingDraftKeys = useMemo(
    () => [
      `${buildReportingStorageScopeBase(projectKey, productType)}__hq__${reportingRecordSuffix}`,
      `${buildReportingStorageScopeBase(projectKey, productType)}__market__${reportingRecordSuffix}`,
    ],
    [projectKey, productType, reportingRecordSuffix]
  );

  const nonEmptyReportingEntries = useMemo(
    () =>
      Object.entries(reportingEntries || {}).filter(([, v]) => {
        const hasPrice = (v?.reportedPrice || '').toString().trim() !== '';
        const hasNote = (v?.note || '').toString().trim() !== '';
        const accepted = !!v?.acceptedDiscrepancy;
        const legacyMissing = !!v?.missing;
        return hasPrice || hasNote || accepted || legacyMissing;
      }),
    [reportingEntries]
  );

  const reportingBoardProducts = useMemo(() => {
    const layout = createBrandBoardLayout(filteredDataRaw);
    return (layout?.priceGroups || []).flatMap((g) => g.products || []);
  }, [filteredDataRaw, createBrandBoardLayout]);

  const reportingRows = useMemo(() => {
    return reportingBoardProducts
      .filter((product) => !reportingContextCountry || countryMatches(product.country, reportingContextCountry))
      .map((product) => ({
        key: getReportingProductKey(product),
        product,
      }))
      .sort((a, b) => (a.product.title || '').localeCompare(b.product.title || ''));
  }, [reportingBoardProducts, reportingContextCountry]);

  const handleReportingScreenshotUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setReportingScreenshotFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setReportingScreenshotPreview(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
    setReportingUploadDialogOpen(false);
  }, []);

  useEffect(() => {
    if (!internalReportingComparisonEnabled) return undefined;
    const onPaste = (event) => {
      if (!reportingUploadDialogOpen) return;
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) return;
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (!item?.type?.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        setReportingSectionExpanded(true);
        setReportingScreenshotFileName(`clipboard-${dayjs().format('YYYYMMDD-HHmmss')}.png`);
        const reader = new FileReader();
        reader.onload = () => setReportingScreenshotPreview(typeof reader.result === 'string' ? reader.result : '');
        reader.readAsDataURL(file);
        setReportingUploadDialogOpen(false);
        break;
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [internalReportingComparisonEnabled, reportingUploadDialogOpen]);

  const updateReportingEntry = useCallback((key, patch) => {
    setReportingEntries((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { ...DEFAULT_REPORTING_ENTRY }),
        ...patch,
      },
    }));
  }, []);

  useEffect(() => {
    if (!internalReportingComparisonEnabled) return;
    try {
      const raw = localStorage.getItem(INTERNAL_REPORTING_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      let saved = parsed?.[reportingRecordKey];
      if (!saved) {
        for (let i = 0; i < legacyReportingDraftKeys.length; i += 1) {
          const k = legacyReportingDraftKeys[i];
          if (parsed?.[k]) {
            saved = parsed[k];
            break;
          }
        }
      }
      if (!saved) {
        setReportingScreenshotFileName('');
        setReportingScreenshotPreview('');
        setReportingEntries({});
        setReportingManualMissingProducts([]);
        return;
      }
      setReportingScreenshotFileName(saved.fileName || '');
      setReportingScreenshotPreview(saved.preview || '');
      setReportingEntries(saved.entries || {});
      setReportingManualMissingProducts(saved.manualMissingProducts || []);
    } catch (_) {
      // ignore invalid persisted data
    }
  }, [internalReportingComparisonEnabled, reportingRecordKey, legacyReportingDraftKeys]);

  useEffect(() => {
    if (!internalReportingComparisonEnabled) return;
    try {
      const raw = localStorage.getItem(INTERNAL_REPORTING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[reportingRecordKey] = {
        fileName: reportingScreenshotFileName,
        preview: reportingScreenshotPreview,
        entries: reportingEntries,
        manualMissingProducts: reportingManualMissingProducts,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(INTERNAL_REPORTING_STORAGE_KEY, JSON.stringify(parsed));
    } catch (_) {
      // ignore persistence errors
    }
  }, [internalReportingComparisonEnabled, reportingRecordKey, reportingScreenshotFileName, reportingScreenshotPreview, reportingEntries, reportingManualMissingProducts]);

  useEffect(() => {
    if (!internalReportingComparisonEnabled) return;
    try {
      const raw = localStorage.getItem(INTERNAL_REPORTING_COMPARISONS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      setSavedComparisons(mergeSavedComparisonsFromStorage(parsed, reportingStorageScope));
    } catch (_) {
      setSavedComparisons([]);
    }
  }, [internalReportingComparisonEnabled, reportingStorageScope]);

  const persistSavedComparisons = useCallback((nextList) => {
    setSavedComparisons(nextList);
    try {
      const raw = localStorage.getItem(INTERNAL_REPORTING_COMPARISONS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[reportingStorageScope] = nextList;
      localStorage.setItem(INTERNAL_REPORTING_COMPARISONS_KEY, JSON.stringify(parsed));
    } catch (_) {
      // ignore persistence errors
    }
  }, [reportingStorageScope]);

  const handleSaveComparison = useCallback(() => {
    const uniqueContextKey = `${reportingContextDateKey || 'N/A'}__${(reportingContextCountry || 'All').toUpperCase()}__${reportingContextCategory || 'All'}`;
    const comparisonName = `${(reportingContextCountry || 'All').toUpperCase()} - ${reportingContextCategory || 'All'} - ${reportingContextDateKey || 'N/A'}`;
    // Build rows from current board layout here (avoids stale empty reportingRows in callback).
    const layout = createBrandBoardLayout(filteredDataRaw);
    const boardProducts = (layout?.priceGroups || []).flatMap((g) => g.products || []);
    const rowsForSnapshot = boardProducts
      .filter((product) => !reportingContextCountry || countryMatches(product.country, reportingContextCountry))
      .map((product) => {
        const key = getReportingProductKey(product);
        const entry = reportingEntries[key] || {};
        const d = getReportingEntryDefaults(entry);
        const legacy = !Object.prototype.hasOwnProperty.call(entry, 'acceptedDiscrepancy');
        const base = {
          key,
          boardPrice: product?.displayPrice ?? '',
          reportedPrice: d.reportedPrice,
          isManualRow: false,
        };
        if (legacy) {
          base.missing = d.missing;
        } else {
          base.acceptedDiscrepancy = d.acceptedDiscrepancy;
          base.note = d.note;
        }
        return base;
      });
    const manualRowsForSnapshot = (reportingManualMissingProducts || []).map((row) => {
      const legacy = !Object.prototype.hasOwnProperty.call(row, 'acceptedDiscrepancy');
      const base = {
        key: row.id,
        boardPrice: '',
        reportedPrice: (row.reportedPrice ?? '').toString(),
        isManualRow: true,
      };
      if (legacy) {
        base.missing = !!row.missing;
      } else {
        base.acceptedDiscrepancy = !!row.acceptedDiscrepancy;
        base.note = (row.note ?? '').toString();
      }
      return base;
    });
    const rowSnapshot = [...rowsForSnapshot, ...manualRowsForSnapshot];
    const { totalCount, tickCount, alertCount } = statusCountsFromRowSnapshot(rowSnapshot);

    const item = {
      id: uniqueContextKey,
      uniqueContextKey,
      name: comparisonName,
      createdAt: new Date().toISOString(),
      context: {
        date: reportingContextDateKey || 'N/A',
        country: reportingContextCountry || 'All',
        category: reportingContextCategory || 'All',
      },
      screenshotFileName: reportingScreenshotFileName,
      screenshotPreview: reportingScreenshotPreview,
      entries: reportingEntries,
      manualMissingProducts: reportingManualMissingProducts,
      rowSnapshot,
      statusSummary: {
        totalCount,
        tickCount,
        alertCount,
      },
    };
    const next = [item, ...savedComparisons.filter((c) => (c.uniqueContextKey || c.id) !== uniqueContextKey)];
    persistSavedComparisons(next);
  }, [
    reportingContextDateKey,
    reportingContextCountry,
    reportingContextCategory,
    reportingScreenshotFileName,
    reportingScreenshotPreview,
    reportingEntries,
    reportingManualMissingProducts,
    filteredDataRaw,
    createBrandBoardLayout,
    savedComparisons,
    persistSavedComparisons,
  ]);

  const handleLoadComparison = useCallback((comparison) => {
    const savedCountry = comparison?.context?.country;
    const savedDate = comparison?.context?.date;
    const savedCategory = comparison?.context?.category;

    if (savedCountry && savedCountry !== 'All') {
      setCountryTouched(true);
      setSelectedCountry(savedCountry.toLowerCase() === 'all' ? '' : savedCountry);
    }
    if (savedDate && savedDate !== 'N/A') {
      setDateSelectionMode(DATE_MODE_DAILY);
      setDateAggregationMode(DATE_AGGREGATION_DAY);
      setSelectedDailyDateKey(savedDate);
    }
    if (savedCategory && savedCategory !== 'All') {
      const parsedCategories = savedCategory
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (parsedCategories.length > 0) setSelectedCategoryGroups(parsedCategories);
    }

    setReportingScreenshotFileName(comparison?.screenshotFileName || '');
    setReportingScreenshotPreview(comparison?.screenshotPreview || '');
    setReportingEntries(comparison?.entries || {});
    setReportingManualMissingProducts(comparison?.manualMissingProducts || []);
    setReportingSectionExpanded(true);
  }, []);

  const addManualMissingProduct = useCallback(() => {
    setReportingManualMissingProducts((prev) => [
      ...prev,
      {
        id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        productName: '',
        reportedPrice: '',
        acceptedDiscrepancy: false,
        note: '',
      },
    ]);
  }, []);

  const updateManualMissingProduct = useCallback((id, patch) => {
    setReportingManualMissingProducts((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }, []);

  const removeManualMissingProduct = useCallback((id) => {
    setReportingManualMissingProducts((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const handleDeleteComparison = useCallback((comparisonId) => {
    const next = savedComparisons.filter((c) => c.id !== comparisonId);
    persistSavedComparisons(next);
  }, [savedComparisons, persistSavedComparisons]);

  useEffect(() => {
    if (availableDates.length === 0) return;
    const latestDate = availableDates[availableDates.length - 1];
    if (!selectedDailyDateKey && latestDate?.isValid?.()) {
      setSelectedDailyDateKey(latestDate.format('YYYY-MM-DD'));
    }
    if (!selectedWeekKey && weekOptions.length > 0) {
      setSelectedWeekKey(weekOptions[weekOptions.length - 1].weekKey);
    }
    if (!selectedMonthKey && monthOptions.length > 0) {
      setSelectedMonthKey(monthOptions[monthOptions.length - 1].key);
    }
  }, [availableDates, weekOptions, monthOptions, selectedDailyDateKey, selectedWeekKey, selectedMonthKey]);

  useEffect(() => {
    if (availableDates.length === 0) return;
    let nextDate = null;
    if (dateSelectionMode === DATE_MODE_DAILY) {
      if (selectedDailyDateKey) {
        const dailyDate = dayjs(selectedDailyDateKey).startOf('day');
        if (dailyDate.isValid()) nextDate = dailyDate;
      }
    } else if (dateSelectionMode === DATE_MODE_WEEKLY) {
      const selectedWeek = weekOptions.find((w) => w.weekKey === selectedWeekKey);
      if (selectedWeek) {
        nextDate = selectedWeek.weekStart.add(Math.max(0, (selectedWeekday || 1) - 1), 'day');
      }
    } else {
      if (selectedMonthKey) {
        const monthStart = dayjs(`${selectedMonthKey}-01`).startOf('day');
        if (monthStart.isValid()) {
          const maxDay = monthStart.daysInMonth();
          const safeDay = Math.max(1, Math.min(selectedMonthDay || 1, maxDay));
          if (safeDay !== selectedMonthDay) setSelectedMonthDay(safeDay);
          nextDate = monthStart.date(safeDay);
        }
      }
    }
    if (nextDate?.isValid?.() && (!selectedDate || !selectedDate.isSame(nextDate, 'day'))) {
      setSelectedDate(nextDate);
    }
  }, [dateSelectionMode, selectedDailyDateKey, weekOptions, selectedWeekKey, selectedWeekday, selectedMonthKey, selectedMonthDay, availableDates, selectedDate]);

  const dateFilterLabel = useMemo(() => {
    if (!selectedDate?.isValid?.()) return 'Date filter';
    if (dateSelectionMode === DATE_MODE_DAILY) {
      return `Daily · ${selectedDate.format('YYYY-MM-DD')}`;
    }
    if (dateSelectionMode === DATE_MODE_WEEKLY) {
      const weekNum = selectedWeekKey?.split('-W')?.[1] || '';
      if (dateAggregationMode === DATE_AGGREGATION_AVERAGE) {
        return `Weekly · Week ${weekNum} · Average`;
      }
      return `Weekly · Week ${weekNum} · ${selectedDate.format('dddd')}`;
    }
    if (dateAggregationMode === DATE_AGGREGATION_AVERAGE) {
      return `Monthly · ${selectedDate.format('MMMM')} · Average`;
    }
    return `Monthly · ${selectedDate.format('MMMM')} · Day ${selectedDate.date()}`;
  }, [dateSelectionMode, dateAggregationMode, selectedDate, selectedWeekKey]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: BRAND_BOARD_ACCENT }} />
        <Typography sx={{ ml: 2 }}>Loading brand board summary...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadData} sx={{ mt: 2 }}>
          <Refresh sx={{ mr: 1 }} />
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Store sx={{ fontSize: 22 }} />
          Brand Board Summary
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={brandBoardVisualStyle}
            onChange={(_, next) => {
              if (next != null) setBrandBoardVisualStyle(next);
            }}
            aria-label="Brand board layout"
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.35,
                px: 1.25,
                fontSize: '0.75rem',
                textTransform: 'none',
              },
            }}
          >
            <ToggleButton value={BRAND_BOARD_VISUAL_GRID}>Table</ToggleButton>
            <ToggleButton value={BRAND_BOARD_VISUAL_CANVAS}>Canvas</ToggleButton>
          </ToggleButtonGroup>
          <Chip label={`${selectedCountry.toUpperCase()} · ${deduplicatedProducts.length} products`} size="small" />
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 1.5, mb: 0, backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}
      >
        {(() => {
          const compactSx = {
            '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
            '& .MuiInputBase-input': { padding: '4px 8px' },
            '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
            '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
            '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BOARD_ACCENT },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BOARD_ACCENT },
            '& .MuiCheckbox-root.Mui-checked': { color: BRAND_BOARD_ACCENT },
          };
          const menuProps = {
            PaperProps: {
              style: { maxHeight: 400, width: 280 },
              sx: {
                '& .MuiMenuItem-root.Mui-selected': { backgroundColor: BRAND_BOARD_ACCENT_SOFT },
                '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: BRAND_BOARD_ACCENT_SOFT },
              },
            },
            autoFocus: false,
          };
          const colSx = { display: 'flex', flexDirection: 'column', gap: 1 };
          return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 2, maxWidth: '100%' }}>
              {/* Column 1: Country, Retailer */}
              <Box sx={colSx}>
                {!isMarketScope && groupBy !== 'countries' && (
                  <FormControl fullWidth size="small" sx={compactSx}>
                    <InputLabel>Country</InputLabel>
                    <Select value={selectedCountry} label="Country"
                      onChange={(e) => { setCountryTouched(true); setSelectedCountry(e.target.value); }}>
                      {availableCountries.map((c) => (
                        <MenuItem key={c} value={c} dense sx={{ fontSize: '0.7rem' }}>{c.toUpperCase()}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Retailer</InputLabel>
                  <Select multiple label="Retailer" value={selectedRetailers}
                    onChange={(e) => setSelectedRetailers(e.target.value)}
                    input={<OutlinedInput label="Retailer" />}
                    renderValue={(sel) => sel.length ? sel.slice(0, 2).join(', ') + (sel.length > 2 ? ` +${sel.length - 2}` : '') : 'All'}
                    MenuProps={menuProps}
                    onClose={() => setRetailerSearchTerm('')}
                  >
                    <ListSubheader sx={{ lineHeight: '48px' }}>
                      <TextField size="small" autoFocus placeholder="Search retailers..." fullWidth
                        value={retailerSearchTerm} onChange={(e) => setRetailerSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
                    </ListSubheader>
                    <Box sx={{ px: 1, py: 0.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (selectedRetailers.length === retailerOptions.length) {
                            setSelectedRetailers([]);
                          } else {
                            setSelectedRetailers(retailerOptions);
                          }
                        }}
                        sx={{ color: BRAND_BOARD_ACCENT }}
                      >
                        Select all
                      </Button>
                    </Box>
                    {manufacturerRetailers.length > 0 && (
                      <ListSubheader
                        disableSticky
                        sx={{
                          fontSize: '0.72rem',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.25,
                          lineHeight: '24px',
                        }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, color: BRAND_BOARD_ACCENT }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleRetailerGroupSelection(manufacturerRetailers);
                          }}
                        >
                          Manufacturer
                        </Button>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRetailerGroupsExpanded((prev) => ({ ...prev, manufacturer: !prev.manufacturer }));
                          }}
                        >
                          {retailerGroupsExpanded.manufacturer ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </ListSubheader>
                    )}
                    {retailerGroupsExpanded.manufacturer && manufacturerRetailers.map((r) => (
                      <MenuItem
                        key={r}
                        value={r}
                        dense
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const isSelected = selectedRetailersSet.has(r);
                          if (isSelected) setSelectedRetailers((selectedRetailers || []).filter((x) => x !== r));
                          else setSelectedRetailers([...(selectedRetailers || []), r]);
                        }}
                        sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      >
                        <Checkbox
                          checked={selectedRetailersSet.has(r)}
                          size="small"
                          sx={{ py: 0, color: BRAND_BOARD_ACCENT, '&.Mui-checked': { color: BRAND_BOARD_ACCENT } }}
                        />
                        <ListItemText primary={r} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                    {thirdPartyRetailers.length > 0 && (
                      <ListSubheader
                        disableSticky
                        sx={{
                          fontSize: '0.72rem',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 0.25,
                          lineHeight: '24px',
                        }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, color: BRAND_BOARD_ACCENT }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleRetailerGroupSelection(thirdPartyRetailers);
                          }}
                        >
                          3rd party
                        </Button>
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setRetailerGroupsExpanded((prev) => ({ ...prev, thirdParty: !prev.thirdParty }));
                          }}
                        >
                          {retailerGroupsExpanded.thirdParty ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </ListSubheader>
                    )}
                    {retailerGroupsExpanded.thirdParty && thirdPartyRetailers.map((r) => (
                      <MenuItem
                        key={r}
                        value={r}
                        dense
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const isSelected = selectedRetailersSet.has(r);
                          if (isSelected) setSelectedRetailers((selectedRetailers || []).filter((x) => x !== r));
                          else setSelectedRetailers([...(selectedRetailers || []), r]);
                        }}
                        sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      >
                        <Checkbox
                          checked={selectedRetailersSet.has(r)}
                          size="small"
                          sx={{ py: 0, color: BRAND_BOARD_ACCENT, '&.Mui-checked': { color: BRAND_BOARD_ACCENT } }}
                        />
                        <ListItemText primary={r} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {/* Column 2: Brand, Search products */}
              <Box sx={colSx}>
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Brand</InputLabel>
                  <Select multiple label="Brand" value={selectedBrands}
                    onChange={(e) => setSelectedBrands(e.target.value)}
                    input={<OutlinedInput label="Brand" />}
                    renderValue={(sel) => sel.length ? sel.slice(0, 2).join(', ') + (sel.length > 2 ? ` +${sel.length - 2}` : '') : 'All'}
                    MenuProps={menuProps}
                    onClose={() => setBrandSearchTerm('')}
                  >
                    <ListSubheader sx={{ lineHeight: '48px' }}>
                      <TextField size="small" autoFocus placeholder="Search brands..." fullWidth
                        value={brandSearchTerm} onChange={(e) => setBrandSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
                    </ListSubheader>
                    {filterOptions.brands
                      .filter((b) => b.toLowerCase().includes(brandSearchTerm.toLowerCase()))
                      .map((b) => (
                        <MenuItem key={b} value={b} dense sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}>
                          <Checkbox
                            checked={selectedBrands.includes(b)}
                            size="small"
                            sx={{
                              py: 0,
                              color: BRAND_BOARD_ACCENT,
                              '&.Mui-checked': { color: BRAND_BOARD_ACCENT },
                            }}
                          />
                          <ListItemText primary={b} sx={{ my: 0 }} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <TextField fullWidth size="small" label="Search products" value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={compactSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }}
                />
              </Box>
              {/* Column 3: Bundle Selection, Starter Kit */}
              <Box sx={colSx}>
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Bundle Selection</InputLabel>
                  <Select
                    multiple
                    label="Bundle Selection"
                    value={bundleSelectionValue}
                    onChange={(e) => {
                      const v = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                      setSelectedCategoryGroups(v.filter(Boolean));
                    }}
                    input={<OutlinedInput label="Bundle Selection" />}
                    renderValue={(selected) =>
                      (selected || [])
                        .map((value) => bundleSelectionFilterOptions.find((o) => o.value === value)?.label || value)
                        .join(', ') || 'All'
                    }
                    MenuProps={menuProps}
                    endAdornment={
                      bundleSelectionFilterOptions.length > 0 ? (
                        <InputAdornment position="end" sx={{ mr: 2.25 }}>
                          <Tooltip
                            title={
                              isDevicesFromRoute
                                ? 'Device bundle is defined as device + sticks/accessories'
                                : 'Sticks bundle is defined as multiple brands offer, for multiple packs use number of units filter'
                            }
                            arrow
                            placement="right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InfoOutlined sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                          </Tooltip>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    {bundleSelectionFilterOptions.map(({ label, value }) => (
                      <MenuItem key={`bundle-sel-${value}`} value={value} dense sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}>
                        <Checkbox
                          checked={bundleSelectionValue.includes(value)}
                          size="small"
                          sx={{
                            py: 0,
                            color: BRAND_BOARD_ACCENT,
                            '&.Mui-checked': { color: BRAND_BOARD_ACCENT },
                          }}
                        />
                        <ListItemText primary={label} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {isDevicesFromRoute && !isStickRoute ? (
                  <FormControl fullWidth size="small" sx={compactSx}>
                    <InputLabel>Starter Kit</InputLabel>
                    <Select
                      value={normalizeStarterKitFilter(starterKitProductFilter)}
                      label="Starter Kit"
                      onChange={(e) => setStarterKitProductFilter(e.target.value)}
                    >
                      {STARTER_KIT_FILTER_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} dense sx={{ fontSize: '0.7rem' }}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}
              </Box>
              {/* Column 4: Price Type, Limited edition */}
              <Box sx={colSx}>
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Price Type</InputLabel>
                  <Select
                    multiple
                    label="Price Type"
                    value={PRICE_TYPE_OPTIONS.filter(({ key }) => activePriceTypes?.has(key)).map((o) => o.key)}
                    onChange={(e) => setActivePriceTypes(sanitizeActivePriceTypes(new Set(e.target.value)))}
                    input={<OutlinedInput label="Price Type" />}
                    renderValue={(selected) =>
                      selected.map((k) => PRICE_TYPE_OPTIONS.find((o) => o.key === k)?.label || k).join(', ')
                    }
                    MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 260 } } }}
                  >
                    {PRICE_TYPE_OPTIONS.flatMap(({ key, label, italic, info, dividerBefore }) => {
                      const items = [];
                      if (dividerBefore) items.push(<Divider key={`div-${key}`} sx={{ my: 0.5 }} />);
                      items.push(
                        <MenuItem key={key} value={key} dense sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem', ...(italic ? { fontStyle: 'italic', color: 'text.secondary' } : {}) } }}>
                          <Checkbox
                            checked={!!(activePriceTypes?.has(key))}
                            size="small"
                            sx={{
                              py: 0,
                              color: BRAND_BOARD_ACCENT,
                              '&.Mui-checked': { color: BRAND_BOARD_ACCENT },
                            }}
                          />
                          <ListItemText primary={label} sx={{ my: 0 }} />
                          {info && (
                            <Tooltip title={info} arrow placement="right" onClick={(e) => e.stopPropagation()}>
                              <InfoOutlined sx={{ fontSize: '0.9rem', color: 'text.disabled', ml: 0.5, flexShrink: 0, '&:hover': { color: 'text.secondary' } }} />
                            </Tooltip>
                          )}
                        </MenuItem>
                      );
                      return items;
                    })}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Limited edition</InputLabel>
                  <Select
                    value={normalizeLimitedEditionFilter(limitedEditionFilter)}
                    label="Limited edition"
                    onChange={(e) => setLimitedEditionFilter(e.target.value)}
                  >
                    {LIMITED_EDITION_FILTER_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt} dense sx={{ fontSize: '0.7rem' }}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {/* Column 5: Date, Base product (100%) */}
              <Box sx={colSx}>
                {availableDates.length > 0 && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<CalendarToday sx={{ fontSize: 14 }} />}
                      onClick={(e) => setDateFilterAnchorEl(e.currentTarget)}
                      sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontSize: '0.72rem',
                        height: 30,
                        px: 1,
                        color: BRAND_BOARD_ACCENT,
                        borderColor: BRAND_BOARD_ACCENT,
                        '& .MuiButton-startIcon': { color: BRAND_BOARD_ACCENT },
                        '&:hover': {
                          borderColor: BRAND_BOARD_ACCENT,
                          backgroundColor: BRAND_BOARD_ACCENT_SOFT,
                        },
                      }}
                    >
                      {dateFilterLabel}
                    </Button>
                    <Menu
                      anchorEl={dateFilterAnchorEl}
                      open={Boolean(dateFilterAnchorEl)}
                      onClose={() => setDateFilterAnchorEl(null)}
                    >
                      <Box sx={{ p: 1.5, width: 320, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <FormControl fullWidth size="small" sx={compactSx}>
                          <InputLabel>Date mode</InputLabel>
                          <Select
                            value={dateSelectionMode}
                            label="Date mode"
                            onChange={(e) => {
                              const mode = e.target.value;
                              setDateSelectionMode(mode);
                              setDateAggregationMode(DATE_AGGREGATION_DAY);
                              if (mode === DATE_MODE_DAILY) {
                                const latestDate = availableDates[availableDates.length - 1];
                                if (latestDate?.isValid?.()) setSelectedDailyDateKey(latestDate.format('YYYY-MM-DD'));
                              }
                              if (mode === DATE_MODE_WEEKLY) setSelectedWeekday(1);
                              if (mode === DATE_MODE_MONTHLY) setSelectedMonthDay(1);
                            }}
                          >
                            <MenuItem value={DATE_MODE_DAILY} dense sx={{ fontSize: '0.7rem' }}>Daily</MenuItem>
                            <MenuItem value={DATE_MODE_WEEKLY} dense sx={{ fontSize: '0.7rem' }}>Weekly</MenuItem>
                            <MenuItem value={DATE_MODE_MONTHLY} dense sx={{ fontSize: '0.7rem' }}>Monthly</MenuItem>
                          </Select>
                        </FormControl>
                        {dateSelectionMode !== DATE_MODE_DAILY ? (
                          <FormControl fullWidth size="small" sx={compactSx}>
                            <InputLabel>Show</InputLabel>
                            <Select
                              value={dateAggregationMode}
                              label="Show"
                              onChange={(e) => setDateAggregationMode(e.target.value)}
                            >
                              <MenuItem value={DATE_AGGREGATION_DAY} dense sx={{ fontSize: '0.7rem' }}>Specific day</MenuItem>
                              <MenuItem value={DATE_AGGREGATION_AVERAGE} dense sx={{ fontSize: '0.7rem' }}>Average</MenuItem>
                            </Select>
                          </FormControl>
                        ) : null}
                        {dateSelectionMode === DATE_MODE_DAILY ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="Date"
                              value={selectedDailyDateKey ? dayjs(selectedDailyDateKey) : null}
                              onChange={(newValue) => {
                                if (newValue?.isValid?.()) {
                                  setSelectedDailyDateKey(newValue.format('YYYY-MM-DD'));
                                }
                              }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  fullWidth: true,
                                  sx: compactSx,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        ) : null}
                        {dateSelectionMode === DATE_MODE_WEEKLY ? (
                          <>
                            <FormControl fullWidth size="small" sx={compactSx}>
                              <InputLabel>Week number</InputLabel>
                              <Select
                                value={selectedWeekKey}
                                label="Week number"
                                onChange={(e) => setSelectedWeekKey(e.target.value)}
                              >
                                {weekOptions.map((w) => (
                                  <MenuItem key={w.weekKey} value={w.weekKey} dense sx={{ fontSize: '0.7rem' }}>
                                    {w.weekLabel}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {dateAggregationMode === DATE_AGGREGATION_DAY ? (
                              <FormControl fullWidth size="small" sx={compactSx}>
                                <InputLabel>Day of week</InputLabel>
                                <Select
                                  value={selectedWeekday}
                                  label="Day of week"
                                  onChange={(e) => setSelectedWeekday(Number(e.target.value))}
                                >
                                  {WEEKDAY_OPTIONS.map((d) => (
                                    <MenuItem key={d.value} value={d.value} dense sx={{ fontSize: '0.7rem' }}>
                                      {d.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : null}
                          </>
                        ) : null}
                        {dateSelectionMode === DATE_MODE_MONTHLY ? (
                          <>
                            <FormControl fullWidth size="small" sx={compactSx}>
                              <InputLabel>Month</InputLabel>
                              <Select
                                value={selectedMonthKey}
                                label="Month"
                                onChange={(e) => {
                                  setSelectedMonthKey(e.target.value);
                                  setSelectedMonthDay(1);
                                }}
                              >
                                {monthOptions.map((m) => (
                                  <MenuItem key={m.key} value={m.key} dense sx={{ fontSize: '0.7rem' }}>
                                    {m.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {dateAggregationMode === DATE_AGGREGATION_DAY ? (
                              <FormControl fullWidth size="small" sx={compactSx}>
                                <InputLabel>Day of month</InputLabel>
                                <Select
                                  value={selectedMonthDay}
                                  label="Day of month"
                                  onChange={(e) => setSelectedMonthDay(Number(e.target.value))}
                                >
                                  {(() => {
                                    const monthStart = selectedMonthKey ? dayjs(`${selectedMonthKey}-01`) : null;
                                    const daysInMonth = monthStart?.isValid?.() ? monthStart.daysInMonth() : 31;
                                    return Array.from({ length: daysInMonth }, (_, idx) => idx + 1).map((dayValue) => (
                                      <MenuItem key={dayValue} value={dayValue} dense sx={{ fontSize: '0.7rem' }}>
                                        {dayValue}
                                      </MenuItem>
                                    ));
                                  })()}
                                </Select>
                              </FormControl>
                            ) : null}
                          </>
                        ) : null}
                      </Box>
                    </Menu>
                  </>
                )}
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel id="brand-board-base-product-label">Base product (100%)</InputLabel>
                  <Select
                    labelId="brand-board-base-product-label"
                    id="brand-board-base-product-select"
                    value={
                      baseProduct === BASE_PRODUCT_AUTO
                        ? BASE_PRODUCT_AUTO
                        : baseProduct ?? BASE_PRODUCT_SELECT_FIRST
                    }
                    label="Base product (100%)"
                    renderValue={(selected) => {
                      if (selected === BASE_PRODUCT_SELECT_FIRST) return 'First product (100%)';
                      if (selected === BASE_PRODUCT_AUTO) {
                        const resolvedLabel = effectiveBaseProduct
                          ? formatBaseProductSelectLabel(effectiveBaseProduct, isStickRoute)
                          : 'no match';
                        if (baseProductSkuOverride) {
                          return `Auto (${resolvedLabel}) — ${baseProductSkuOverride}`;
                        }
                        return `Auto (${resolvedLabel})`;
                      }
                      if (baseProductSkuOverride) {
                        const baseLabel = formatBaseProductSelectLabel(selected, isStickRoute);
                        return `${baseLabel} — ${baseProductSkuOverride}`;
                      }
                      return formatBaseProductSelectLabel(selected, isStickRoute);
                    }}
                    onChange={(e) => {
                      const v = e.target.value;
                      initialBaseProductDefaultAppliedRef.current = true;
                      if (v === BASE_PRODUCT_SELECT_FIRST) setBaseProduct(null);
                      else setBaseProduct(v);
                    }}
                  >
                    <MenuItem value={BASE_PRODUCT_SELECT_FIRST} dense sx={{ fontSize: '0.7rem' }}>
                      <em>First product (100%)</em>
                    </MenuItem>
                    <MenuItem
                      value={BASE_PRODUCT_AUTO}
                      dense
                      sx={{
                        fontSize: '0.7rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <span>Auto (by availability)</span>
                      <Tooltip
                        title="Set priority order (e.g. Terea, then Evo). First product available in the selected country becomes 100%."
                        arrow
                        placement="right"
                      >
                        <IconButton
                          size="small"
                          aria-label="Auto base product priority settings"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openBaseProductAutoQueueDialog();
                          }}
                        >
                          <SettingsOutlined sx={{ fontSize: '0.95rem' }} />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                    {baseProductOptionsForSelect.map((opt) => (
                      <MenuItem key={opt} value={opt} dense sx={{ fontSize: '0.7rem' }}>
                        {isStickRoute && typeof opt === 'string' && opt.includes(STICK_BASE_PRODUCT_PACK_SEP)
                          ? formatStickBaseProductOptionLabel(opt)
                          : opt}
                      </MenuItem>
                    ))}
                    <Divider />
                    <ListSubheader disableSticky sx={{ lineHeight: '20px', fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary' }}>
                      Base product settings
                    </ListSubheader>
                
                    <MenuItem
                      dense
                      disableRipple
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 0.5, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
                    >
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          mt: 0.25,
                          '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                          '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BOARD_ACCENT },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BOARD_ACCENT },
                        }}
                      >
                        <InputLabel id="brand-board-compare-to-price-type-label">Compare to price type</InputLabel>
                        <Select
                          labelId="brand-board-compare-to-price-type-label"
                          value={compareToPriceType}
                          label="Compare to price type"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setCompareToPriceType(e.target.value)}
                          sx={{
                            '& .MuiSelect-select': { py: 0.55, fontSize: '0.7rem' },
                            '& .MuiSvgIcon-root': { fontSize: '1rem' },
                          }}
                        >
                          <MenuItem value={BASE_COMPARE_TYPE_AUTO} dense sx={{ fontSize: '0.7rem', display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <span>Automatically</span>
                            <Tooltip title="Compares regular price to regular price, promo price to promo price, and starter kit price to starter kit price." arrow placement="right">
                              <InfoOutlined
                                sx={{ fontSize: '0.8rem', color: 'text.disabled', flexShrink: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Tooltip>
                          </MenuItem>
                          <MenuItem value={BASE_COMPARE_TYPE_LISTED} dense sx={{ fontSize: '0.7rem' }}>Regular price</MenuItem>
                          <MenuItem value={BASE_COMPARE_TYPE_PROMO} dense sx={{ fontSize: '0.7rem' }}>Promo price</MenuItem>
                          <MenuItem value={BASE_COMPARE_TYPE_STARTER_KIT} dense sx={{ fontSize: '0.7rem' }}>Starter Kit</MenuItem>
                        </Select>
                      </FormControl>
                    </MenuItem>
                    <MenuItem
                      dense
                      disableRipple
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 0.5, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
                    >
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{
                          mt: 0.25,
                          '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                          '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                          '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BOARD_ACCENT },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_BOARD_ACCENT },
                        }}
                      >
                        <InputLabel id="brand-board-base-product-sku-label">Specific SKU (optional)</InputLabel>
                        <Select
                          labelId="brand-board-base-product-sku-label"
                          value={baseProductSkuOverride}
                          label="Specific SKU (optional)"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setBaseProductSkuOverride(e.target.value)}
                          disabled={!effectiveBaseProduct || specificBaseSkuOptions.length === 0}
                          sx={{
                            '& .MuiSelect-select': { py: 0.55, fontSize: '0.7rem' },
                            '& .MuiSvgIcon-root': { fontSize: '1rem' },
                          }}
                        >
                          <MenuItem value="" dense sx={{ fontSize: '0.7rem' }}>
                            Auto (use selected base product)
                          </MenuItem>
                          {specificBaseSkuOptions.map((sku) => (
                            <MenuItem key={sku} value={sku} dense sx={{ fontSize: '0.7rem' }}>
                              {sku}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {/* Column 6: Group by, Number of units (sticks only), Price display (sticks only) */}
              <Box sx={colSx}>
                {!isMarketScope && (
                  <FormControl fullWidth size="small" sx={compactSx}>
                    <InputLabel>Group by</InputLabel>
                    <Select value={groupBy} label="Group by" onChange={(e) => setGroupBy(e.target.value)}>
                      <MenuItem value="brands" dense sx={{ fontSize: '0.7rem' }}>Brands</MenuItem>
                      <MenuItem value="manufacturers" dense sx={{ fontSize: '0.7rem' }}>Manufacturers</MenuItem>
                      <MenuItem value="countries" dense sx={{ fontSize: '0.7rem' }}>Countries</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {isStickRoute && (
                  <FormControl fullWidth size="small" sx={compactSx}>
                    <InputLabel>Number of units</InputLabel>
                    <Select
                      multiple
                      label="Number of units"
                      value={selectedPackSizes}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = typeof raw === 'string' ? raw.split(',') : [...raw];
                        const toggledMinimal = next.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL);
                        const numericOnly = next.filter((x) => x !== BRAND_BOARD_PACK_SELECTION_MINIMAL);
                        if (toggledMinimal && numericOnly.length === 0) {
                          setSelectedPackSizes([BRAND_BOARD_PACK_SELECTION_MINIMAL]);
                          return;
                        }
                        if (toggledMinimal && numericOnly.length > 0) {
                          const prevHadMinimal = selectedPackSizes.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL);
                          if (!prevHadMinimal) {
                            setSelectedPackSizes([BRAND_BOARD_PACK_SELECTION_MINIMAL]);
                            return;
                          }
                        }
                        setSelectedPackSizes(numericOnly);
                      }}
                      input={<OutlinedInput label="Number of units" />}
                      renderValue={(sel) => {
                        if (!sel.length) return 'All';
                        if (sel.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL)) return 'Minimal';
                        const nums = [...sel].sort((a, b) => Number(a) - Number(b));
                        return nums.slice(0, 2).map((n) => `${n}-pack`).join(', ') + (nums.length > 2 ? ` +${nums.length - 2}` : '');
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 400, width: 260 } }, autoFocus: false }}
                      onClose={() => setPackSizeSearchTerm('')}
                    >
                      <MenuItem
                        key={BRAND_BOARD_PACK_SELECTION_MINIMAL}
                        value={BRAND_BOARD_PACK_SELECTION_MINIMAL}
                        dense
                        sx={{ py: 0.35, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                      >
                        <Checkbox
                          checked={selectedPackSizes.includes(BRAND_BOARD_PACK_SELECTION_MINIMAL)}
                          size="small"
                          sx={{
                            py: 0,
                            color: BRAND_BOARD_ACCENT,
                            '&.Mui-checked': { color: BRAND_BOARD_ACCENT },
                          }}
                        />
                        <ListItemText
                          primary="Minimal"
                          secondary="Smallest pack for each product"
                          sx={{ my: 0 }}
                          secondaryTypographyProps={{ variant: 'caption', sx: { lineHeight: 1.25 } }}
                        />
                      </MenuItem>
                      <Divider sx={{ my: 0.5 }} />
                      <ListSubheader sx={{ lineHeight: '48px' }}>
                        <TextField size="small" autoFocus placeholder="Search pack sizes..." fullWidth
                          value={packSizeSearchTerm} onChange={(e) => setPackSizeSearchTerm(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment> }} />
                      </ListSubheader>
                      {packSizeOptions
                        .filter((s) => String(s).includes(packSizeSearchTerm))
                        .map((s) => (
                          <MenuItem key={s} value={s} dense sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}>
                            <Checkbox
                              checked={selectedPackSizes.includes(s)}
                              size="small"
                              sx={{
                                py: 0,
                                color: BRAND_BOARD_ACCENT,
                                '&.Mui-checked': { color: BRAND_BOARD_ACCENT },
                              }}
                            />
                            <ListItemText primary={`${s}-pack`} sx={{ my: 0 }} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
                {isStickRoute && (
                  <FormControl fullWidth size="small" sx={compactSx}>
                    <InputLabel>Price display</InputLabel>
                    <Select value={priceDisplayMode} label="Price display" onChange={(e) => setPriceDisplayMode(e.target.value)}>
                      <MenuItem value="absolute" dense sx={{ fontSize: '0.7rem' }}>Absolute price</MenuItem>
                      <MenuItem value="per_unit" dense sx={{ fontSize: '0.7rem' }}>Per unit price</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
              {/* Column 7: Currency (display); index row still uses EUR internally */}
              <Box sx={colSx}>
                <FormControl fullWidth size="small" sx={compactSx}>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={effectiveDisplayCurrency}
                    label="Currency"
                    onChange={(e) => setDisplayCurrencySetting(e.target.value)}
                  >
                    {currencyFilterOptions.map((c) => (
                      <MenuItem key={c} value={c} dense sx={{ fontSize: '0.7rem' }}>
                        {c === 'local' ? 'Local' : c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

            </Box>
          );
        })()}
      </Paper>

      {isSelectedDateUnavailable && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {`Data are not available for this date ${selectedDate?.format?.('YYYY-MM-DD') || ''}`.trim()}
        </Alert>
      )}

      <MarketRegulationsPanel
        variant={isMarketScope ? 'market' : 'hq'}
        selectedMarket={selectedMarket}
        selectedCountries={
          isHqScope && groupBy !== 'countries' && selectedCountry ? [selectedCountry] : []
        }
        accentColor={BRAND_BOARD_ACCENT}
      />

      {showItalySticksNoticeAboveBoard && (
        <Box sx={{ mb: 1.5 }}>
          <MarketAnnouncementBannerList messages={marketAnnouncementMessages} />
        </Box>
      )}

      {renderBrandBoardLayout()}

      {internalReportingComparisonEnabled && (
        <Paper sx={{ p: 2, border: '1px solid #d9d9d9', backgroundColor: '#fffdf7' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: reportingSectionExpanded ? 1 : 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Internal reporting comparison testing
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() => setReportingSectionExpanded((v) => !v)}
              endIcon={reportingSectionExpanded ? <ExpandLess /> : <ExpandMore />}
            >
              {reportingSectionExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </Box>

          {reportingSectionExpanded && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Upload a reporting screenshot, or press Ctrl+V to paste one from clipboard. Then assign reported prices manually. If a value differs from the board, you can accept the discrepancy and add a short note.
              </Typography>

              <Box sx={{ mb: 1.5, p: 1.25, border: '1px dashed #d8d8d8', borderRadius: 1, backgroundColor: '#fff' }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  The reporting date is set to: {reportingContextDateKey || 'N/A'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  The country is set to: {(reportingContextCountry || 'All').toUpperCase()}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  The product category is set to: {reportingContextCategory}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setReportingUploadDialogOpen(true)}
                >
                  Upload screenshot
                </Button>
                {reportingScreenshotFileName ? (
                  <Chip size="small" label={reportingScreenshotFileName} />
                ) : null}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveComparison}
                  disabled={nonEmptyReportingEntries.length === 0 && reportingManualMissingProducts.length === 0 && !reportingScreenshotPreview}
                >
                  Save comparison
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addManualMissingProduct}
                >
                  Add product (not on board)
                </Button>
              </Box>

              <Dialog
                open={reportingUploadDialogOpen}
                onClose={() => setReportingUploadDialogOpen(false)}
                maxWidth="xs"
                fullWidth
              >
                <DialogTitle>Upload screenshot</DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
                    <Button variant="outlined" component="label" size="small">
                      Upload screenshot from file
                      <input type="file" hidden accept="image/*" onChange={handleReportingScreenshotUpload} />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      Or paste screenshot from clipboard (using CTRL+V)
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setReportingUploadDialogOpen(false)} size="small">Close</Button>
                </DialogActions>
              </Dialog>

              {reportingScreenshotPreview ? (
                <Box sx={{ mb: 2 }}>
                  <Box
                    component="img"
                    src={reportingScreenshotPreview}
                    alt="Reporting screenshot"
                    sx={{ maxWidth: '100%', maxHeight: 320, border: '1px solid #ddd', borderRadius: 1 }}
                  />
                </Box>
              ) : null}

              <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid #ececec', borderRadius: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 2.4fr) minmax(52px, 0.48fr) minmax(56px, 0.48fr) minmax(160px, 1.35fr)',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid #ececec',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Product</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Board price</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Reported price</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Accept discrepancy / note</Typography>
                </Box>
                {reportingRows.map(({ key, product }) => {
                  const rawEntry = reportingEntries[key];
                  const entry = getReportingEntryDefaults(rawEntry);
                  const isTick = isBoardRowTick(rawEntry, product.displayPrice);
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 2.4fr) minmax(52px, 0.48fr) minmax(56px, 0.48fr) minmax(160px, 1.35fr)',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderBottom: '1px solid #f1f1f1',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {isTick ? (
                          <CheckCircle sx={{ color: 'success.main', fontSize: 16, flexShrink: 0 }} />
                        ) : (
                          <WarningAmber sx={{ color: 'warning.main', fontSize: 16, flexShrink: 0 }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 0 }}>
                          {getDisplayTitleForProduct(product)}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatLocalPriceDisplay(product.displayPrice, product.displayCurrency)}
                      </Typography>
                      <TextField
                        size="small"
                        value={entry.reportedPrice}
                        onChange={(e) => updateReportingEntry(key, { reportedPrice: e.target.value })}
                        placeholder="e.g. 5.49"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '6px 8px' } }}
                        sx={{ width: '100%', maxWidth: '100%' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={entry.acceptedDiscrepancy}
                              onChange={(e) =>
                                updateReportingEntry(key, {
                                  acceptedDiscrepancy: e.target.checked,
                                  ...(e.target.checked ? { missing: false } : {}),
                                })}
                            />
                          )}
                          label={<Typography variant="caption">Accept</Typography>}
                          sx={{ m: 0, flexShrink: 0, alignItems: 'center' }}
                        />
                        <TextField
                          size="small"
                          value={entry.note}
                          onChange={(e) => updateReportingEntry(key, { note: e.target.value })}
                          placeholder="Note"
                          inputProps={{ style: { fontSize: '0.75rem', padding: '6px 8px' } }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                      </Box>
                    </Box>
                  );
                })}
                {reportingManualMissingProducts.map((row) => {
                  const isTick = isManualReportingRowTick(row);
                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 2.4fr) minmax(52px, 0.48fr) minmax(56px, 0.48fr) minmax(160px, 1.35fr)',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderBottom: '1px solid #f1f1f1',
                        alignItems: 'center',
                        backgroundColor: '#fff8f8',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {isTick ? (
                          <CheckCircle sx={{ color: 'success.main', fontSize: 16, flexShrink: 0 }} />
                        ) : (
                          <WarningAmber sx={{ color: 'warning.main', fontSize: 16, flexShrink: 0 }} />
                        )}
                        <TextField
                          size="small"
                          value={row.productName || ''}
                          onChange={(e) => updateManualMissingProduct(row.id, { productName: e.target.value })}
                          placeholder="Product name (screenshot)"
                          inputProps={{ style: { fontSize: '0.75rem', padding: '6px 8px' } }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">—</Typography>
                      <TextField
                        size="small"
                        value={row.reportedPrice || ''}
                        onChange={(e) => updateManualMissingProduct(row.id, { reportedPrice: e.target.value })}
                        placeholder="e.g. 5.49"
                        inputProps={{ style: { fontSize: '0.75rem', padding: '6px 8px' } }}
                        sx={{ width: '100%', maxWidth: '100%' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              size="small"
                              checked={!!row.acceptedDiscrepancy}
                              onChange={(e) =>
                                updateManualMissingProduct(row.id, {
                                  acceptedDiscrepancy: e.target.checked,
                                  ...(e.target.checked ? { missing: false } : {}),
                                })}
                            />
                          )}
                          label={<Typography variant="caption">Accept</Typography>}
                          sx={{ m: 0, flexShrink: 0, alignItems: 'center' }}
                        />
                        <TextField
                          size="small"
                          value={(row.note ?? '').toString()}
                          onChange={(e) => updateManualMissingProduct(row.id, { note: e.target.value })}
                          placeholder="Note"
                          inputProps={{ style: { fontSize: '0.75rem', padding: '6px 8px' } }}
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeManualMissingProduct(row.id)}
                          sx={{ minWidth: 'auto', px: 0.5, flexShrink: 0 }}
                        >
                          x
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Existing comparisons
                </Typography>
                {savedComparisons.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No saved comparisons yet.
                  </Typography>
                ) : (
                  savedComparisons.map((comparison) => {
                    const comparisonEntries = Object.entries(comparison.entries || {});
                    const nonEmpty = comparisonEntries.filter(([, v]) => {
                      const hasPrice = (v?.reportedPrice || '').toString().trim() !== '';
                      const hasNote = (v?.note || '').toString().trim() !== '';
                      return hasPrice || hasNote || !!v?.acceptedDiscrepancy || !!v?.missing;
                    });
                    const manualCount = (comparison.manualMissingProducts || []).length;
                    let totalCount = 0;
                    let tickCount = 0;
                    let alertCount = 0;
                    if (Array.isArray(comparison.rowSnapshot) && comparison.rowSnapshot.length > 0) {
                      const s = statusCountsFromRowSnapshot(comparison.rowSnapshot);
                      totalCount = s.totalCount;
                      tickCount = s.tickCount;
                      alertCount = s.alertCount;
                    } else if (comparison.statusSummary && comparison.statusSummary.totalCount > 0) {
                      totalCount = comparison.statusSummary.totalCount;
                      tickCount = comparison.statusSummary.tickCount;
                      alertCount = comparison.statusSummary.alertCount;
                    } else {
                      totalCount = nonEmpty.length + manualCount;
                      tickCount = 0;
                      alertCount = totalCount;
                    }
                    return (
                      <Accordion key={comparison.id} disableGutters sx={{ mb: 0.75 }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {comparison.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {comparison.context?.date} · {(comparison.context?.country || '').toUpperCase()} · {comparison.context?.category}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <WarningAmber sx={{ color: 'warning.main', fontSize: 14 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {alertCount}/{totalCount}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <CheckCircle sx={{ color: 'success.main', fontSize: 14 }} />
                                <Typography variant="caption" color="text.secondary">
                                  {tickCount}/{totalCount}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                            <Button size="small" variant="outlined" onClick={() => handleLoadComparison(comparison)}>
                              Load
                            </Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteComparison(comparison.id)}>
                              Delete
                            </Button>
                          </Box>
                          {comparison.screenshotPreview ? (
                            <Box
                              component="img"
                              src={comparison.screenshotPreview}
                              alt="Saved comparison screenshot"
                              sx={{ maxWidth: '100%', maxHeight: 220, border: '1px solid #ddd', borderRadius: 1, mb: 1 }}
                            />
                          ) : null}
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            Entries saved: {nonEmpty.length + manualCount}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Box>
            </>
          )}
        </Paper>
      )}

      <Dialog
        open={baseProductAutoQueueDialogOpen}
        onClose={() => setBaseProductAutoQueueDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          Auto base product priority
          <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.75, fontWeight: 400 }}>
            {baseProductAutoQueueRouteKey === 'sticks'
              ? 'Sticks: walk the list top to bottom; the first product or SKU available in the selected country becomes 100%.'
              : 'Devices: same logic for device baselines.'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
            {baseProductAutoQueueDraft.map((entry, idx) => {
              const selectedChoice =
                autoBaseProductQueueChoices.find((c) => c.value === entry) ||
                (entry
                  ? {
                      value: entry,
                      label: entry.startsWith(AUTO_BASE_QUEUE_SKU_PREFIX)
                        ? entry.slice(AUTO_BASE_QUEUE_SKU_PREFIX.length)
                        : formatBaseProductSelectLabel(entry, isStickRoute),
                      kind: 'legacy',
                    }
                  : null);
              return (
              <Box key={`auto-base-q-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 18, flexShrink: 0, textAlign: 'right' }}>
                  {idx + 1}.
                </Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={autoBaseProductQueueChoices}
                  groupBy={(option) =>
                    option.kind === 'sku' ? 'Specific SKUs' : 'Base products (group / pack)'
                  }
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  value={selectedChoice}
                  onChange={(_, option) => {
                    const next = [...baseProductAutoQueueDraft];
                    next[idx] = option?.value || '';
                    setBaseProductAutoQueueDraft(next);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select base product or SKU" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {option.label}
                        </Typography>
                        {option.kind === 'sku' ? (
                          <Typography variant="caption" color="text.secondary">
                            Specific SKU
                          </Typography>
                        ) : null}
                      </Box>
                    </li>
                  )}
                />
                <IconButton
                  size="small"
                  aria-label="Move up"
                  disabled={idx === 0}
                  onClick={() => {
                    if (idx === 0) return;
                    const next = [...baseProductAutoQueueDraft];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setBaseProductAutoQueueDraft(next);
                  }}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Move down"
                  disabled={idx >= baseProductAutoQueueDraft.length - 1}
                  onClick={() => {
                    if (idx >= baseProductAutoQueueDraft.length - 1) return;
                    const next = [...baseProductAutoQueueDraft];
                    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                    setBaseProductAutoQueueDraft(next);
                  }}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Remove"
                  onClick={() => {
                    setBaseProductAutoQueueDraft(baseProductAutoQueueDraft.filter((_, i) => i !== idx));
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
              );
            })}
            <Button
              size="small"
              variant="text"
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              onClick={() => setBaseProductAutoQueueDraft([...baseProductAutoQueueDraft, ''])}
            >
              Add priority row
            </Button>
            <Button
              size="small"
              variant="text"
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              onClick={() =>
                setBaseProductAutoQueueDraft(
                  (DEFAULT_BASE_PRODUCT_AUTO_QUEUES[baseProductAutoQueueRouteKey] || []).map((entry) =>
                    migrateLegacyQueueEntry(
                      entry,
                      autoBaseProductQueueChoices,
                      baseProductOptionsForSelect,
                      isStickRoute
                    )
                  )
                )
              }
            >
              Reset to defaults
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setBaseProductAutoQueueDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: BRAND_BOARD_ACCENT, '&:hover': { bgcolor: '#1b5e20' } }}
            onClick={() => {
              const cleaned = baseProductAutoQueueDraft.map((s) => String(s || '').trim()).filter(Boolean);
              const fallback = DEFAULT_BASE_PRODUCT_AUTO_QUEUES[baseProductAutoQueueRouteKey] || [];
              const nextQueues = {
                ...baseProductAutoQueues,
                [baseProductAutoQueueRouteKey]: cleaned.length ? cleaned : [...fallback],
              };
              setBaseProductAutoQueues(nextQueues);
              persistBaseProductAutoQueues(nextQueues);
              setBaseProductAutoQueueDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ProductDetailPopup
        open={productPopupOpen}
        onClose={() => setProductPopupOpen(false)}
        productName={productPopupData.productName}
        productImage={productPopupData.productImage}
        productData={productPopupData.productData}
        productDataRows={productPopupData.productDataRows}
        groupProductNames={productPopupData.groupProductNames}
        packSizeOverrides={productPopupData.packSizeOverrides}
        productFlags={productPopupData.productFlags}
        pricingAsOfDate={productPopupData.pricingAsOfDate}
        pinnedChartPrices={productPopupData.pinnedChartPrices}
        activePriceTypes={activePriceTypes}
        projectKey={projectKey}
        country={productPopupData.productData?.country || selectedCountry}
      />
    </Box>
  );
}

export default BrandBoardSummaryPaletteView;
